import type { CaregiverEmployer, ContractorSector, Finding, HousingRegion, IncomeSource, LeavingReason, IsoDate, PayBasisAnswer, PayslipFields, RecuperationPayment, Sector, Sex, SpecialStatus, UserAnswers, WorkerType, YearMonth } from '../model';
import { hasUnsavedData } from './unsavedData';
import { answersSummaryParts, fileRecognised, planAsk, type AskedOnPage, goalSummaryKey, payslipsOfFile as filePayslips, uploadLabelKey, type Loaded } from './docTypes';
import { type FileRow, fileRows, noTextPagesOf, type OcrState, sameFile, turnedAwayPages } from './fileList';
import { largestAmountIndex } from './largestAmount';
import { CAREGIVER_EMPLOYERS, CONTRACTOR_SECTORS, claimableTotal, HOUSING_REGIONS, INCOME_SOURCES, LEAVING_REASONS, incomeSourcesKey, MISSING_INPUTS, WORKER_TYPES, foreignWorkerType, isIsoDate } from '../model';
import { parsePayslipFields } from '../model';
import { COMBINATIONS, GOALS, QUESTION_FEEDS, WHO, translationOnly, altKey, alternative, atStep, feedsSomething, form106Years, guardedStep, noticeKey, possibleChecks, stepAfter, stillKey, stopHeadingKey, type Combination, type Goal, type Who, type FlowStepId as FlowStep, type Situation } from '../model/possibleChecks';
import { themeMenu } from './theme';
import { scrollMotion } from './motion';
import { HEADING_SELECTOR, markFocus, restoreFocus, tabLeaving, takesTyping, watchTab } from './focusKeeper';
import type { Form106Draft, Form106Fields, ManualProblem as Form106Problem, ReviewItem as Form106ReviewItem } from '../form106';
import { LANGS, LANGUAGES, MESSAGES, addMessages, firstKey, formatMoney, hasMessage, isLang, loadLanguage, preferredLanguage, t, type Lang } from '../i18n';
import type { MoreInfo } from './moreInfo';
import { formatDate, formatDateWords, formatMonth } from './stepsView';
import { createWelcome, shouldShowWelcome } from './welcome';
import type { DataLossAction, LossKind } from './leaveGuard';
import { losesSomething, type Loss } from './loss';
import { choiceQuestion, type ChoiceSpec } from './question';
import { runRulesStep } from './rulesStep';
import { isDevMode } from './devMode';
import { CONTACT_EMAIL, DRAFT_PAGES, LICENCES_FILE, PAGES, PAGE_NAMES, pageFromHash, type PageName } from './pages';
import { COUNTER_URL, checkCount, countCheck, countingOff, markedParts, readTotal, setCountingOff } from './counter';
import { brandHero, brandLogo } from './brand/hero';
import { startScreen } from './flowD/start';
import { canDragDrop } from './flowD/dragModel';
import { deadlineYears, israelIsoDate, israelYear, rememberOpening, shouldShowOpening } from './flowD/openingModel';
import type * as FlowSteps from './flowD/steps';
import type * as FlowErrors from './flowD/errors';
import type * as FlowPeople from './flowD/people';
import type { ConfirmDeps } from './confirmRows';
import { barStepOf, flowStepOf, minutesToSay, timeLeftMs, whoNeeded, type BarStep, type Needed as FlowNeeded } from './flowD/model';
import DATA_VALIDITY from '../params/dataValidity.json';
import logoUrl from '../assets/logo.svg';
import { buildAnimal, buildHash } from '../build/indicator';
import { checkForUpdates, type UpdateCheck } from '../pwa/register';
import { clearLater } from './updateStatus';
import { APP_VERSION, BUILD, BUILD_ID, REPORT_EMAIL } from '../diagnostics/buildInfo';
import type { DiagnosticReport, ErrorStep, Feedback } from '../diagnostics';
import type { PageText, Reconciliation } from '../parsers';
import type { UploadedFile } from '../upload';
import { isImageType, MAX_FILE_BYTES, sniffType } from '../upload/sniff';
import type { ConfirmKey, HeldKey, OcrDraft } from '../ocr/extract';
import type { PageResult } from '../ocr/pipeline';
import type { OcrEngine, OcrProgress } from '../ocr/browser';
import type * as ReadQueueModule from '../ocr/readQueue';
import type { ReadQueue, SharedEngine } from '../ocr/readQueue';
import { hasEncryptEntry, type PdfWorkerMode } from '../upload/pdfReader';
import { currentReaderConfig, loadPdfjs, readWithLadder } from './pdfLoader';
import { startReading, type BytesResult } from '../upload/fileBytes';
import { describeError, isDebug, type DebugEvent } from './debugPanel';
import { photoTipKeys, scannerDevice } from './photoTips';
import type { UaData } from './photoTooLarge';
import type { ManualEntry, ManualField, ManualProblem, ManualVouch } from '../manual';
import type * as Engine from './engine';
// Beta builds only (MAGIA_BETA=1): every use is behind __BETA__, so production builds drop them (vite.config.ts).
import { markScreen } from '../beta/aids';
// Opt-in error reports: every build (MATT GO 2026-10-26 04:58), behind __ERROR_REPORTS__ (MAGIA_ERROR_REPORTS=0 drops them).
import { errorReportOffer } from '../beta/errorReportOffer';
import { noteRecentError } from '../beta/recentErrors';
import { isChecklistHash, renderChecklist } from '../beta/checklist';
import { screenKey } from '../beta/screenCodes';
import { markStripeCode } from '../beta/stripeCode';
import { glueSeparators, splitQuestion } from './glue';
import { startShare } from '../share/payload';
import { paragraphChildren } from './linkText';

/** One analysed payslip, from pasted data or an uploaded PDF. */
interface Analysed {
  fields: PayslipFields;
  findings: Finding[];
  reconciliation?: Reconciliation;
  /** For PDFs: where it came from. */
  /** `fileIndex`: the upload it was read from (two files can share a name). */
  source?: { file: string; page: number; vendor: string; fileIndex?: number };
  /** Read from a scan or photo and confirmed by the user (findings capped, labelled experimental). */
  ocr?: true;
  /** From a payroll system whose support is beta (findings capped at medium; see VendorParser.support). */
  betaVendor?: true;
}

/** A scanned PDF or photo being read by OCR (docs/phase2-plan.md 4.2–4.4). */
interface OcrJob {
  /** labels: a digital PDF in an unknown layout read by its labels (src/fallback), straight to the confirmation screen. */
  kind: 'scan' | 'photo' | 'labels';
  /** queued: waiting its turn (files are read one at a time, src/ocr/readQueue.ts). */
  status: 'offer' | 'queued' | 'running' | 'review' | 'failed' | 'cancelled';
  progress?: OcrProgress;
  /** When its pages started being read (the engine ready): design D's reading screen times files to say an honest time left. */
  readStart?: number;
  /** timeout: the engine didn't start in time, or the reading stopped making progress. */
  failure?: 'decode' | 'download' | 'error' | 'nothingRead' | 'notForm106' | 'timeout' | 'tooLarge';
  pages: OcrPage[];
  /** Couldn't download the engine, took too long or the engine failed: read once more, automatically, after the other files (retried: done). */
  retry?: 'pending' | 'done';
}
interface OcrPage {
  /** The page of the file (1-based): with per-page OCR, not every page is read. */
  page: number;
  result: PageResult;
  /** As read, for "which fields were edited" (names only in the report). */
  original: OcrDraft;
  draft: OcrDraft;
  confirmed: boolean;
  /** Matt GO G105: taken as read for the translation only (no number confirmation); "Also check this payslip" asks for the confirmation. */
  forTranslation?: boolean;
}
/**
 * A scan's or photo's pages as the OCR read them (the processed, upright, deskewed page image and its items), per file and
 * page number (G11 slice 3): the translated view draws that image with the labels translated. In memory only, for as
 * long as the file is (a WeakMap on the File); never stored or sent. A Form 106's OCR job ends once read, so its pages
 * live here, not in the job.
 */
const scanPictures = new WeakMap<File, Map<number, PageResult>>();
/** Vendor name for an OCR payslip from an unknown layout (generic label search, lower confidence). */
const OCR_GENERIC = 'generic (OCR)';
/** Vendor name for a digital payslip in an unknown layout, its numbers found by their labels (src/fallback) and confirmed. */
const LABELS_GENERIC = 'generic (labels)';
const capLow = (findings: Finding[]): Finding[] => findings.map((f) => (f.confidence === 'low' ? f : { ...f, confidence: 'low' }));

const LANG_KEY = 'magia-li.lang';
/** The take-home pay calculator's page (`__TAKE_HOME__` builds; src/calculator). */
const TAKE_HOME_HASH = /^#\/take-home\/?$/;

/** The chosen language; on a first visit, the browser's language when the app offers it, else Hebrew. */
export function loadLang(): Lang {
  try {
    const stored = localStorage.getItem(LANG_KEY);
    if (isLang(stored)) return stored;
  } catch {
    // Storage blocked (private mode etc.): fall back to the browser's language.
  }
  return preferredLanguage(navigator.languages ?? [navigator.language]);
}

function saveLang(lang: Lang): void {
  try {
    localStorage.setItem(LANG_KEY, lang);
  } catch {
    // Not essential.
  }
}

/** Small DOM helper; text only, never HTML, so pasted content can't inject markup. */
function el<K extends keyof HTMLElementTagNameMap>(
  tag: K,
  attrs: Record<string, string> = {},
  ...children: (Node | string)[]
): HTMLElementTagNameMap[K] {
  const node = document.createElement(tag);
  for (const [k, v] of Object.entries(attrs)) node.setAttribute(k, v);
  node.append(...children);
  return node;
}

/**
 * The approved privacy line (Matt, 2026-10-22, mailbox 13:36): "No sign-up. No names. Your browser reads your documents
 * right here, on your device. None of the data you provide ever leaves your device. We only count anonymous usage
 * statistics, and even that you can turn off." "turn off" links to the counter's switch. A language whose text isn't
 * re-translated yet has no `{off}`: the link follows the text. Where space is tight (phones) the short form
 * "No sign-up. No names. No personal data collected." (`brand.privacyStrip`) shows instead: two spans, switched by CSS
 * (styles.css `.privacy-long` / `.privacy-short`), so the primary action stays in the first phone viewport.
 */
function privacyLine(tr: (key: string) => string): (Node | string)[] {
  const text = tr(COUNTER_URL ? 'app.privacyLine.counter' : 'app.privacyLine.noCounter');
  let long: (Node | string)[] = [text];
  if (COUNTER_URL) {
    const link = el('a', { href: '#/privacy/counter' }, tr('app.privacyLink'));
    const at = text.indexOf('{off}');
    long = at < 0 ? [text, ' ', link] : [text.slice(0, at), link, text.slice(at + 5)];
  }
  return [el('span', { class: 'privacy-long' }, ...long), el('span', { class: 'privacy-short' }, tr('brand.privacyStrip'))];
}

/**
 * The guided flow (docs/ux-rules.md "Guided flow"): what to check, who you are, upload, a few questions, results.
 * One step is open at a time; finished steps are one line with Edit.
 */
/** Goals not open yet: shown, marked "coming soon", not selectable. Form 106 until its Hebrew texts are reviewed (vite.config.ts). */
const COMING_SOON: readonly Goal[] = __FORM106_READY__ ? [] : ['form106'];


export interface State {
  lang: Lang;
  flow: { step: FlowStep; goal?: Goal; who?: Who; /** The sector was answered on "Who are you?": the questions step does not ask it again. */ sectorOnWho?: true;
    /** Design D: every box of "Does any of this apply to you?" unticked (not answered, never "None of these"). */ statusesUnanswered?: true;
    /** Design D: something in "Does any of this apply to you?" was ticked ("None of these" too); until then its answer line says "Not answered yet". */ statusesAnswered?: true;
    /** Design D: Continue was pressed with something missing: the missing items stay marked until answered. */ neededShown?: true };
  json: string;
  answers: UserAnswers;
  exampleLoaded: boolean;
  /** Set after Check is pressed or PDFs are read. */
  result: { input: 'manual' | 'digitalPdf'; payslips: Analysed[]; enteredByHand?: true; crashed?: true } | { errors: string[] } | undefined;
  /**
   * Uploaded PDFs (docs/phase2-plan.md 4.1a), in memory only: never stored or sent. While set, results
   * come from them; pressing Check on pasted data replaces them.
   */
  uploads: UploadedFile[] | undefined;
  /** The chosen files, in the same order as `uploads` (OCR reads them again). */
  files: File[];
  /** OCR jobs by file index; one engine reads them one at a time (ocrReader). */
  ocr: Map<number, OcrJob>;
  /** Files being read right now, not yet in `files` (shown at the end of the list). */
  pending: File[];
  /** Passwords of protected PDFs, by file index: memory only, never stored, never reported. */
  passwords: Map<number, string>;
  /** Names of chosen files skipped because they were already in the list (a short note). */
  skipped: string[];
  /** Names of chosen files skipped because their bytes are a file's already added, under any name (SHA-256 on the device). */
  twice: string[];
  /**
   * Which copy to use of a document read from more than one file (src/upload/duplicates.ts), by the set's key
   * (`dupKey`): the kept copy's `refKey`. Memory only.
   */
  dupKeep: Map<string, string>;
  /**
   * Whose documents are checked (src/upload/people.ts; the confirm step's question, src/ui/flowD/people.ts): the person
   * picked and the answer about documents whose ID wasn't read, by document keys (`refKey`), never an ID. Memory only.
   */
  people: Engine.PeopleAnswers;
  /** The header's language menu is open. */
  langMenuOpen: boolean;
  /** Manual entry is shown (hidden by default: "Can't upload? Enter the numbers yourself"). */
  manualOpen: boolean;
  /** Day/month/year chosen in date selects outside manual entry, by field id (a half-filled date survives re-renders). */
  dateParts: Record<string, { day: string; month: string; year: string }>;
  dragging: boolean;
  /** Manual entry (docs/phase2-plan.md 4.5): what the user typed, kept across re-renders. */
  manual: ManualEntry;
  /** The parts chosen in the date selects (a half-filled date must survive re-renders). */
  manualDates: Partial<Record<'payMonth' | 'startDate', { day: string; month: string; year: string }>>;
  /** Problems found when the form was last checked (checks don't run while there are any). */
  manualProblems: { kind: 'invalid' | 'check'; problems: ManualProblem[] } | undefined;
  /** Typo checks on the manual form the user said are wrong for their payslip ("This number is right"). */
  manualVouched: ManualVouch[];
  /** Which "More info" panels are open (kept across re-renders). */
  moreOpen: Set<string>;
  /** Where PDF.js runs in this browser (module worker, classic worker, main thread), for the diagnostic report. */
  pdfReader?: PdfWorkerMode;
  /** Which PDF.js build read the last PDF (debug panel). */
  pdfBuild?: 'modern' | 'legacy';
  /** Start dates the user gave (or confirmed from a printed seniority date) for PDFs that don't print one, by file name. */
  startDates: Map<string, IsoDate>;
  /** A beta vendor's PDF: pension values held back as implausible that the user said are right, by `file#page`. */
  vouched: Map<string, HeldKey[]>;
  /** Values typed under a "can't check" result (never a dead end, src/ui/missingInfo.ts), by `file#page` (`manual#1`). */
  fieldFixes: Map<string, Partial<PayslipFields>>;
  /** The same for a Form 106, by its place in form106Docs() before fixes. */
  form106Fixes: Map<number, Partial<Form106Fields>>;
  /** Typed values held back once as implausible (pension), by the same key: used when sent again unchanged. */
  fixWarned: Map<string, string>;
  /** Which hand-made input the Check button runs: the form or pasted JSON. */
  source: 'form' | 'json';
  updateAvailable: boolean;
  /** "Check for updates" in the footer: what it's doing or found. */
  updateCheck?: 'checking' | UpdateCheck;
  /** The payslip translation's table (src/translate, loaded on first use), and which payslips show it. */
  tv?: typeof import('../translate');
  translated: Set<number>;
  /** The public counter's totals (documents checked; worth-checking figures, rounded down, added up in whole shekels). */
  checksTotal?: { documents: number; estimatedFound: number };
  /** Design D's flow after the start screen (src/ui/flowD/steps.ts: its parts, CSS and texts; loaded with the check engine, __FLOW_D__ builds only). */
  fd?: typeof FlowSteps;
  /** Design D screen 8, the cards for a file that wasn't read (src/ui/flowD/errors.ts), loaded with the flow steps. */
  fdErrors?: typeof FlowErrors;
  /** The UX-1 results screens (src/results, loaded with the check engine). */
  rv?: typeof import('../results');
  /** The "Please check" note (src/results/note.ts): its own chunk, loaded in preview builds only. */
  note?: typeof import('../results/note');
  /** Form 106 (src/form106, loaded on first use): the module, and numbers typed in by the user. */
  f106?: typeof import('../form106');
  form106Manual?: Form106Fields;
  form106Open: boolean;
  form106Draft: Form106Draft;
  form106Problems?: Form106Problem[] | undefined;
  /** "Check your answers": the row being changed in place, and what was confirmed there (docsSig) before the results. */
  confirmEdit?: string | undefined;
  /** The held value whose Change box is open on the questions step ("Please check these numbers"). */
  heldEdit?: string | undefined;
  /** Brand C's "Translate a payslip": the results open with every payslip's translation. */
  translateFirst?: boolean;
  /** A translation was asked for ("Translate a payslip", kept after the results open): a Form 106 then says it has none. */
  translateAsked?: boolean;
  confirmedSig?: string;
  /** Beta diagnostics (docs/phase2-plan.md 6.1): opt-in, built only when asked, never sent. */
  diag: {
    open: boolean; feedback: Record<string, Feedback>; comment: string; report: DiagnosticReport | undefined; copied: boolean; includeLayout: boolean;
    /** Errors caught this session; the report keeps only step and type. */
    errors: { step: ErrorStep; error: unknown }[];
  };
}

export interface App {
  showUpdateNotice(): void;
  /** True when a reload would lose something entered or loaded (src/pwa/register.ts). */
  hasUnsavedData(): boolean;
}

export function mountApp(root: HTMLElement): App {
  const fresh = (): State => ({
    lang: loadLang(), flow: { step: 'goal' }, json: '', answers: {}, exampleLoaded: false, result: undefined, updateAvailable: false, uploads: undefined, files: [], ocr: new Map(), pending: [], skipped: [], twice: [], dupKeep: new Map(), people: {}, passwords: new Map(), langMenuOpen: false, manualOpen: false, dateParts: {}, dragging: false, manual: {}, manualDates: {}, manualProblems: undefined, manualVouched: [], moreOpen: new Set(), startDates: new Map(), vouched: new Map(), fieldFixes: new Map(), form106Fixes: new Map(), fixWarned: new Map(), source: 'form', form106Open: false, form106Draft: {}, translated: new Set(),
    diag: { open: false, feedback: {}, comment: '', report: undefined, copied: false, includeLayout: true, errors: [] },
  });
  const state: State = fresh();

  /** Counts an error once (a rules error rethrown reaches the window handler too). */
  const noted = new WeakSet<object>();
  const debug = isDebug(location.search);
  const debugEvents: DebugEvent[] = [];
  /** The panel's text, its own chunk: loaded only with ?debug=1 (the start screen stays small); the panel shows once it is in. */
  let debugTextMod: typeof import('./debugText') | undefined;
  if (debug) void import('./debugText').then((m) => { debugTextMod = m; render(); }).catch(() => undefined);
  /** Which file is being read, for the debug panel: numbered, never named. */
  let debugWhere = 'page';
  const noteError = (step: ErrorStep, err: unknown) => {
    if (typeof err === 'object' && err !== null) {
      if (noted.has(err)) return;
      noted.add(err);
    }
    state.diag.errors.push({ step, error: err });
    // "Report this error" names the latest error, by step and listed type only (src/beta/recentErrors.ts).
    if (__ERROR_REPORTS__) noteRecentError(step, err);
    // The debug panel (?debug=1) keeps the message too, in memory only, never in the report.
    if (debug) debugEvents.push({ time: new Date().toTimeString().slice(0, 8), where: debugWhere, stage: step, ...describeError(err) });
  };
  /**
   * src/ui/leaveGuard.ts guardLoss: with nothing to lose the action runs at once, inside the tap; otherwise the guard's
   * dialog (its own chunk, off the start screen; precached) asks first. Resolves whether it ran. If the chunk can't be
   * loaded even on a second try, nothing runs (nothing is lost) and the error goes where every lazy chunk's does (the
   * window handler: the diagnostic report and ?debug=1), as with the check engine.
   */
  const guardLoss = async (id: DataLossAction, lang: Lang, dir: 'rtl' | 'ltr', loss: Loss, action: () => void, kind?: LossKind): Promise<boolean> => {
    if (!losesSomething(loss)) {
      action();
      return true;
    }
    const m = await import('./leaveGuard').catch(() => import('./leaveGuard'));
    return m.guardLoss(id, lang, dir, loss, action, kind);
  };
  const rulesStep = <T>(run: () => T) => runRulesStep(run, noteError);
  /** The Form 106 module, loaded the first time it's needed (its own chunk: the start screen stays small). */
  const loadForm106 = async () => {
    if (!state.f106) {
      const m = await import('../form106');
      m.registerMessages(addMessages);
      await m.loadMessages(state.lang, addMessages);
      state.f106 = m;
    }
    return state.f106;
  };
  /**
   * The generic fallback reader (src/fallback; stream G), in builds with `__FALLBACK__` only (preview): its own chunk,
   * loaded the first time a file arrives that no known reader reads. `fallbackLoaded`: once loaded, for the synchronous
   * steps (a Form 106 figure settled by the year's payslips).
   */
  let fallbackLoading: Promise<typeof import('../fallback')> | undefined;
  let fallbackLoaded: typeof import('../fallback') | undefined;
  const loadFallback = (): Promise<typeof import('../fallback')> =>
    (fallbackLoading ??= __FALLBACK__ ? import('../fallback').then((m) => (fallbackLoaded = m)) : Promise.reject(new Error('no fallback reader in this build')));
  /** A Form 106 no known layout reads, by its field codes; only what passes the read gate (src/form106/readGate.ts). */
  const fallback106 = async (pages: readonly PageText[], scan = false): Promise<NonNullable<UploadedFile['form106']> | undefined> => {
    const m = await loadFallback();
    const read = m.readForm106Fallback(pages, eng().defaultParams, today(), { scan });
    return read && !(await loadForm106()).form106Gate(read, eng().defaultParams, today()) ? read : undefined;
  };
  /** A payslip PDF no known layout reads, by its labels: the pages where something was read, to be confirmed. */
  const fallbackPayslips = async (pages: readonly PageText[]): Promise<NonNullable<UploadedFile['labelsRead']>> =>
    (await loadFallback()).readPayslipPages(pages, eng().defaultParams, today());
  /**
   * The check engine (rules, params, readers, results; src/ui/engine.ts): its own chunk, loaded when a goal is picked
   * or a file arrives, so the start screen loads only itself (CLAUDE.md "Size budget"). Every step after the goal and
   * every file read runs with it loaded; `eng()` failing means a path skipped `loadEngine`.
   */
  let engine: typeof Engine | undefined;
  let engineLoading: Promise<typeof Engine> | undefined;
  const loadEngine = (): Promise<typeof Engine> => (engineLoading ??= Promise.all([import('./engine'), loadResultsScreens(), loadFlowSteps()]).then(([m]) => {
    // The "More info" texts come with the engine in a build (src/build/lazyExplain.ts), before any result is built.
    m.registerExplainTexts(addMessages);
    // A returning reader's OCR engine starts now, from the files kept on this device (src/ocr/warm.ts): never a download.
    void import('../ocr/warm').then(async (w) => (await w.shouldWarm()) && (await reader()).engine.get()).catch(() => undefined);
    return (engine = m);
  }));
  /** Design D's flow steps (src/ui/flowD/steps.ts): their own chunk, CSS and texts, loaded with the check engine; nothing in a build without the flag. */
  async function loadFlowSteps(): Promise<void> {
    if (!__FLOW_D__ || state.fd) return;
    const [m, errors] = await Promise.all([import('./flowD/steps'), import('./flowD/errors')]);
    m.registerMessages(addMessages);
    errors.registerErrorMessages(addMessages);
    await Promise.all([m.loadMessages(state.lang, addMessages), errors.loadErrorMessages(state.lang, addMessages)]);
    state.fdErrors = errors;
    state.fd = m;
  }
  const fd = (): typeof FlowSteps => {
    if (!state.fd) throw new Error('flow steps not loaded');
    return state.fd;
  };
  /** The UX-1 results screens (src/results: its CSS, the Rubik font and its texts), loaded together with the check engine. */
  async function loadResultsScreens(): Promise<void> {
    if (state.rv) return;
    const m = await import('../results');
    m.registerMessages(addMessages);
    await m.loadMessages(state.lang, addMessages);
    if (__BETA__) state.note = await import('../results/note');
    state.rv = m;
  }
  const eng = (): typeof Engine => {
    if (!engine) throw new Error('check engine not loaded');
    return engine;
  };
  /**
   * Design D's opening page (src/ui/flowD/opening.ts; MATT GO 2026-10-23 00:56): on a first visit before the start screen
   * (in place of the old welcome), and again from "Why check?". Its chunk and the flow's texts load first (a lazy chunk;
   * the start screen's size budget). "Start" remembers it was seen (src/ui/flowD/openingModel.ts).
   */
  let openingD: typeof import('./flowD/opening') | undefined;
  let showOpening = __FLOW_D__ && !pageFromHash(location.hash) && !(__BETA__ && isChecklistHash(location.hash)) && !(__TAKE_HOME__ && TAKE_HOME_HASH.test(location.hash)) && shouldShowOpening();
  const loadOpening = async (): Promise<void> => {
    if (!__FLOW_D__ || openingD) return;
    const [m] = await Promise.all([import('./flowD/opening'), loadFlowSteps()]);
    openingD = m;
  };
  /** "Why check?" on the start screen: the opening page again, its heading focused. */
  const reopenOpening = (): void => {
    showOpening = true;
    void loadOpening().then(() => {
      render();
      window.scrollTo(0, 0);
      document.getElementById('opening-heading')?.focus();
    }, () => {
      // Its chunk didn't load: stay on the start screen.
      showOpening = false;
    });
  };
  /** The opening page's "Start": the start screen, and later visits open there. */
  const leaveOpening = (): void => {
    rememberOpening();
    showOpening = false;
    render();
    window.scrollTo(0, 0);
    document.getElementById('goal-heading')?.focus();
  };
  /** Design D's translation view and privacy page (src/ui/flowD/translate.ts, privacy.ts; __FLOW_D__ builds only). */
  let tvD: typeof import('./flowD/translate') | undefined;
  let pvD: typeof import('./flowD/privacy') | undefined;
  /** The take-home pay calculator's chunk (src/calculator; `__TAKE_HOME__` builds), loaded when #/take-home is opened. */
  let calcMod: typeof import('../calculator') | undefined;
  /** The payslip translation's chunk (src/translate), loaded the first time a payslip is translated. */
  const loadTranslation = async () => {
    // Design D (redesign part 2, screen 9): the translation's own layout, with the flow's texts.
    if (__FLOW_D__ && !tvD) [tvD] = await Promise.all([import('./flowD/translate'), loadFlowSteps()]);
    if (!state.tv) {
      const m = await import('../translate');
      m.registerMessages(addMessages);
      await m.loadMessages(state.lang, addMessages);
      state.tv = m;
    }
    return state.tv;
  };
  /**
   * "Cover your details and send the page" (src/cover; docs/plans/cover-and-send.md): its own chunk, loaded when the person
   * presses one of the two buttons (the unknown-format offer, the diagnostic report's second button). Only in a build with
   * `__COVER__` (preview builds); the production build has neither the buttons nor the chunk. The tool runs on its own
   * screen over the app (the app is hidden, not changed) and gives the focus back to the button when it closes.
   */
  const openCoverTool = async (indices: readonly number[], from: HTMLElement | null): Promise<void> => {
    const m = await import('../cover');
    m.registerMessages(addMessages);
    await m.loadMessages(state.lang, addMessages);
    const files = indices.flatMap((i) => {
      const file = state.files[i];
      const upload = state.uploads?.[i];
      if (!file || upload?.fileType === undefined) return [];
      const kind = upload.fileType === 'pdf' ? ('pdf' as const) : upload.fileType === 'unknown' ? undefined : ('image' as const);
      if (!kind) return [];
      const password = state.passwords.get(i);
      return [{
        kind, vendor: upload.vendor,
        ...(password !== undefined ? { password } : {}),
        ...(kind === 'pdf' && upload.unrecognised?.pages ? { pages: upload.unrecognised.pages } : {}),
        bytes: async () => (await bytesOf(file)).bytes,
      }];
    });
    m.openCover({
      t: (key, params) => t(state.lang, key, params),
      root,
      files,
      loadPdfjs: async () => (await loadPdfjs(noteError)).pdfjs,
      choiceQuestion,
      email: REPORT_EMAIL,
      buildId: BUILD_ID,
      unknownLayout: unknownFormatIndices().length > 0,
      // The app's one shared OCR engine (src/ocr/readQueue.ts; on the device, its files come from this site): the tool waits for
      // the files being read to finish (the app is hidden meanwhile, so no new file is put in line), then reads on the same
      // engine. Cancelling stops the engine only while the tool's own read is in progress (the next reading starts a new one).
      ocr: async () => {
        const r = await reader();
        let reading = false;
        return {
          read: async (page) => {
            await r.queue.idle();
            const engine = await r.engine.get();
            r.engine.listen(undefined);
            reading = true;
            try {
              // Without the other binarisation's reading (only the Form 106 cross-check uses it): its image isn't kept.
              const result = await engine.read(page, () => true);
              delete result.other;
              return result;
            } finally {
              reading = false;
            }
          },
          cancel: () => {
            if (reading) r.engine.stop();
          },
        };
      },
      onClose: () => {
        render();
        (from && document.contains(from) ? from : document.getElementById('results-report-problem'))?.focus();
      },
      onReport: () => openReport(false),
    });
  };
  /** The button that opens the tool for these files; nothing at all in a build without it. */
  const coverButton = (label: string, indices: readonly number[]): HTMLElement | '' => {
    if (indices.length === 0) return '';
    const b = button(label, () => void openCoverTool(indices, b), 'secondary');
    return b;
  };

  /**
   * A Form 106 read by the generic reader with a figure several numbers fit: settled by the year's payslips when they
   * add up to exactly one of them (src/fallback/form106.ts settleWithPayslips); still only a suggestion to confirm.
   */
  const settled = (f: NonNullable<UploadedFile['form106']>): NonNullable<UploadedFile['form106']> =>
    fallbackLoaded && 'candidates' in f && f.candidates && state.uploads ? fallbackLoaded.settleWithPayslips(f as Parameters<typeof fallbackLoaded.settleWithPayslips>[0], entriesOf(state.uploads).map((e) => e.fields)) : f;
  /** A form the person said is the checked person's (its ID not read; src/upload/people.ts): checked as theirs. */
  const withWorker = <T extends { fields: Form106Fields }>(d: T, file: number): T =>
    (state.uploads && engine && confirmedIn(withOcr(state.uploads), { file }) ? { ...d, fields: { ...d.fields, worker: eng().confirmedWorker() } } : d);
  /** Every Form 106 in play: read from files (digital or scan) and typed in. */
  const form106Docs = ():{ fields: Form106Fields; warnings: string[]; scan?: true; scanOf?: 'scannedPdf' | 'photo'; typed?: true; file?: string; fixed?: true; monthsRowImage?: Engine.RowImage }[] => [
    // A form read from two files (a PDF and a photo of it): only the copy the user kept (src/upload/duplicates.ts).
    ...(state.uploads ?? []).flatMap((u, i) => (u.form106 && !formNotCounted(i) ? [withWorker({ ...settled(u.form106), file: u.name, ...(u.form106.scan ? { scanOf: eng().scanSourceOf(u) } : {}) }, i)] : [])),
    // Typed in by the person: theirs (src/upload/people.ts confirmedWorker).
    ...(state.form106Manual ? [{ fields: { ...state.form106Manual, worker: { confirmed: true as const } }, warnings: [], typed: true as const }] : []),
  ].map((d, i) => {
    // Values typed under a "can't check" result (the months worked from the picker…) over what was read.
    const fix = state.form106Fixes.get(i);
    // Months confirmed or typed: a printed yearly total of credit points becomes the monthly figure (src/form106/parse.ts).
    const fields = fix ? { ...d.fields, ...fix } : d.fields;
    const withPoints = state.f106?.creditPointsFromTotal(fields) ?? fields;
    // A value read that doesn't fit the rest of the form is held back until confirmed (src/form106/plausibility.ts);
    // what the person typed, or confirmed by sending it, never is.
    const held = !('typed' in d) && state.f106 && engine ? state.f106.holdUnusual(withPoints, engine.defaultParams, Object.keys(fix ?? {})) : withPoints;
    return fix ? { ...d, fields: held, fixed: true as const } : { ...d, fields: held };
  });
  const hasForm106 = () => form106Docs().length > 0;
  /**
   * The months picker's view of form `i` (src/ui/monthsPicker.ts): its months row cut from the scan (in memory only) and
   * why nothing is ticked (no marks in the row, or the row not read).
   */
  const monthsRowOf = (i: number): NonNullable<Engine.MissingInfoSpec['monthsRow']> => {
    const d = form106Docs()[i];
    const status = d?.warnings.find((w) => w.startsWith('months: box row no'));
    return {
      ...(d?.monthsRowImage ? { image: d.monthsRowImage } : {}),
      ...(status ? { status: status.endsWith('noMarks') ? 'noMarks' as const : 'noRow' as const } : {}),
    };
  };
  // Tab leaving a field whose `change` redraws the page: the focus goes on to the next control (focusKeeper K9).
  watchTab(document);
  // CSP violations on the page (a blocked worker, script or request): recorded by directive only.
  document.addEventListener('securitypolicyviolation', (e) => {
    noteError('csp', e.effectiveDirective);
    if (debug) debugEvents.push({ time: new Date().toTimeString().slice(0, 8), where: 'page', stage: 'csp', name: e.effectiveDirective, message: `blocked ${e.blockedURI || '(inline)'}` });
  });
  /** The rules crashed: say so and open the report section so the tester can share the error. */
  const crashed = (input: 'manual' | 'digitalPdf', payslips: Analysed[], enteredByHand?: true): State['result'] => {
    state.diag.open = true;
    return { input, payslips, crashed: true, ...(enteredByHand ? { enteredByHand } : {}) };
  };
  window.addEventListener('error', (e) => noteError('ui', e.error));
  window.addEventListener('unhandledrejection', (e) => noteError('ui', e.reason));

  /** The parsed payslip, if the JSON is currently valid; used to decide which questions to show. */
  const currentFields = (): PayslipFields | undefined => {
    try {
      const parsed = parsePayslipFields(JSON.parse(state.json));
      return parsed.ok ? parsed.fields : undefined;
    } catch {
      return undefined;
    }
  };

  /** Where values typed under a "can't check" result go for a payslip typed in (or pasted) rather than read from a file. */
  const MANUAL_FIX = 'manual#1';

  /** The Check button: the form, or pasted JSON, whichever the user worked with last. */
  function check(): void {
    if (state.source === 'form') checkManual();
    else checkJson();
  }

  /** Runs the checks on the form. Invalid or implausible numbers stop them and ask the user to look again. */
  function checkManual(): void {
    state.source = 'form';
    state.uploads = undefined;
    state.diag.report = undefined;
    const r = eng().manualToFields(state.manual, state.manualVouched);
    if (r.status === 'ok') {
      state.manualProblems = undefined;
      // The user may mistype: findings from manual entry are at most medium confidence. Values typed under a
      // "can't check" result (never a dead end) count as typed too.
      const fields = { ...r.fields, ...state.fieldFixes.get(MANUAL_FIX) };
      const out = rulesStep(() => eng().capForManual(eng().runAllRules(fields, eng().defaultParams, state.answers, { asOf: today() })));
      state.result = out.ok ? { input: 'manual', enteredByHand: true, payslips: [{ fields, findings: out.value }] } : crashed('manual', [{ fields, findings: [] }], true);
    } else {
      state.manualProblems = { kind: r.status, problems: r.problems };
      state.result = undefined;
    }
    render();
    if (r.status !== 'ok') document.getElementById('manual-problems')?.focus();
  }

  function checkJson(): void {
    let value: unknown;
    try {
      value = JSON.parse(state.json);
    } catch (err) {
      state.result = { errors: [t(state.lang, 'input.invalidJson', { error: err instanceof Error ? err.message : String(err) })] };
      return render();
    }
    const parsed = parsePayslipFields(value);
    state.uploads = undefined;
    if (parsed.ok) {
      const fields = { ...parsed.fields, ...state.fieldFixes.get(MANUAL_FIX) };
      const out = rulesStep(() => eng().runAllRules(fields, eng().defaultParams, state.answers, { asOf: today() }));
      state.result = out.ok ? { input: 'manual', payslips: [{ fields, findings: out.value }] } : crashed('manual', [{ fields, findings: [] }]);
    } else state.result = { errors: parsed.errors };
    // A report describes one set of results: build it again after they change.
    state.diag.report = undefined;
    render();
  }

  /** Today's date in Israel (the opening page's helper) drives the claim time limits; the rules themselves stay pure. */
  const today = () => israelIsoDate(new Date()) as IsoDate;

  /** Results from the uploaded PDFs: every payslip by pay month, analysed together. */
  function analyseUploads(uploads: UploadedFile[]): void {
    // A digital payslip: numbers that don't fit the rest of it are held back (a beta vendor's parser may read the
    // wrong column; any file may be the wrong one), as for a scan; the check then says what's missing.
    // A start date given in the questions and values typed under a "can't check" result are the user's: never held.
    const entries = heldEntries(uploads);
    const out = rulesStep(() => eng().analyse(entries.map((e) => e.fields), eng().defaultParams, state.answers, today()));
    const findings = out.ok ? out.value : [];
    if (!out.ok) state.diag.open = true;
    state.result = {
      ...(out.ok ? {} : { crashed: true as const }),
      input: 'digitalPdf',
      payslips: entries.map((e, i) => ({
        fields: e.fields,
        // OCR: at most medium confidence even after confirmation; an unknown layout or a beta vendor, low.
        // A beta vendor's digital payslip: at most medium. Per page: a mixed file's digital pages are digital.
        findings: e.ocr
          ? (e.vendor === OCR_GENERIC || e.vendor === LABELS_GENERIC || eng().isBetaVendor(e.vendor) ? capLow : eng().capForManual)(findings[i] ?? [])
          : eng().isBetaVendor(e.vendor) || state.fieldFixes.has(`${e.fileName}#${e.page}`) ? eng().capForManual(findings[i] ?? []) : (findings[i] ?? []),
        ...(e.ocr ? { ocr: true as const } : {}),
        ...(eng().isBetaVendor(e.vendor) ? { betaVendor: true as const } : {}),
        ...(e.reconciliation ? { reconciliation: e.reconciliation } : {}),
        source: { file: e.fileName, page: e.page, vendor: e.vendor, ...(e.fileIndex !== undefined ? { fileIndex: e.fileIndex } : {}) },
      })),
    };
    state.diag.report = undefined;
  }

  /** Every payslip read, with what was held back from it (src/upload/hold.ts): the checks and "Please check these numbers" alike. */
  const heldEntries = (uploads: UploadedFile[]) =>
    eng().holdEntries(entriesOf(uploads), eng().defaultParams, { vouched: state.vouched, fixes: state.fieldFixes, startDates: state.startDates });

  /**
   * Matt GO G105 (2026-10-30): the translate flow does not ask for number confirmation (numbers have nothing to do with
   * translating words). Every OCR page read and waiting is taken as read for the translation (`forTranslation`); the
   * confirmation is asked when "Also check this payslip" is chosen (checkInstead). Unsure coded values say "unclear" in the key.
   */
  function acceptForTranslation(): void {
    if (!state.translateAsked) return;
    let any = false;
    for (const job of state.ocr.values()) {
      if (job.status !== 'review') continue;
      for (const pg of job.pages) if (!pg.confirmed) { pg.confirmed = true; pg.forTranslation = true; any = true; }
    }
    if (any && state.uploads) analyseUploads(state.uploads);
  }
  /** Leaving the translation for the check: pages taken as read only for it are asked again (the confirmation step). */
  function checkInstead(): void {
    for (const job of state.ocr.values()) for (const pg of job.pages) if (pg.forTranslation) { pg.confirmed = false; delete pg.forTranslation; }
    if (state.uploads) analyseUploads(state.uploads);
  }

  /**
   * The uploads plus every confirmed OCR page. Unconfirmed OCR values never reach the rules
   * (docs/phase2-plan.md 4.4): a file waiting for OCR or confirmation gives no payslips.
   */
  function withOcr(uploads: UploadedFile[]): UploadedFile[] {
    return uploads.map((u, i) => {
      const job = state.ocr.get(i);
      if (!job) return u;
      // Whole-file OCR, or a mixed / doubtful file's OCR pages merged with its digital ones (src/upload/ocrMerge.ts).
      return eng().mergeOcr(u, job.pages.filter((p) => p.confirmed && (!p.forTranslation || state.translateAsked === true)).map((p) => {
        const g = eng().gate(p.draft, eng().defaultParams);
        return { page: p.page, fields: eng().confirmedFields(p.draft, eng().defaultParams), reconciliation: { linesToGross: g.linesToGross, grossToNet: g.grossToNet }, passes: g.ok, vendor: p.draft.vendor ?? (u.labelsRead ? LABELS_GENERIC : OCR_GENERIC) };
      }), (p) => eng().payslipGate(p, eng().defaultParams, today()));
    });
  }

  /**
   * The same document read from more than one file (Matt, 2026-10-23 19:29, item E; src/upload/duplicates.ts): each
   * file has an id for this visit (indices shift when a file is removed), each set a key from its members, and the
   * user's choice is kept by that key; a set that changes (a file added or removed) is asked again.
   */
  const fileIds = new WeakMap<File, number>();
  let nextFileId = 0;
  const idOfFile = (f: File | undefined): number => {
    if (!f) return -1;
    let id = fileIds.get(f);
    if (id === undefined) fileIds.set(f, (id = nextFileId++));
    return id;
  };
  const refKey = (r: Engine.DocRef): string => `${idOfFile(state.files[r.file])}${r.page === undefined ? '' : `#${r.page}`}`;
  const dupKey = (g: Engine.DuplicateGroup): string => `${g.kind}:${g.period}:${g.members.map(refKey).join(',')}`;
  const keptOf = (g: Engine.DuplicateGroup): Engine.DocRef | undefined => g.members.find((m) => refKey(m) === state.dupKeep.get(dupKey(g)));
  /** Every set of the same document in these uploads (with their confirmed scan pages). */
  const dupGroups = (merged: UploadedFile[] = withOcr(state.uploads ?? [])): Engine.DuplicateGroup[] => eng().duplicateGroups(merged);
  /** The sets the user hasn't chosen a copy of yet: the upload step asks, and can't continue until answered. */
  const dupsUnanswered = (): Engine.DuplicateGroup[] => (state.uploads && engine ? dupGroups().filter((g) => !keptOf(g)) : []);
  /** The copies not counted: the ones the user didn't keep, or, not answered, all but the first (never two of one). */
  const copiesNotCounted = (merged: UploadedFile[]): Engine.DocRef[] => eng().notCounted(dupGroups(merged), keptOf);
  /**
   * Whose documents are checked (Matt, 2026-10-26: never two people's together; src/upload/people.ts): every document
   * counted once, in file and page order, grouped into people by the worker read on it; the person picked on the confirm
   * step, the others set aside (not deleted).
   */
  const peopleIn = (merged: UploadedFile[]): Engine.PeopleView => {
    const copies = copiesNotCounted(merged);
    const counted = (r: Engine.DocRef) => !copies.some((c) => eng().sameRef(c, r));
    const workerAt = (r: Engine.DocRef) => (r.page === undefined ? merged[r.file]?.form106?.fields.worker : merged[r.file]?.payslips.find((p) => p.page === r.page)?.fields.worker);
    // A document whose copies were read with other ID numbers (a photo misread): whose it is isn't known, so it is asked.
    const groups = dupGroups(merged);
    const workerOf = (r: Engine.DocRef) => {
      const ids = new Set(groups.filter((g) => g.members.some((m) => eng().sameRef(m, r))).flatMap((g) => g.members.flatMap((m) => workerAt(m)?.id ?? [])));
      return eng().unsureWorker(workerAt(r), [...ids]);
    };
    const doc = (ref: Engine.DocRef, kind: 'form106' | 'payslip'): Engine.PersonDoc => {
      const worker = workerOf(ref);
      return { ref, kind, ...(worker ? { worker } : {}) };
    };
    const docs: Engine.PersonDoc[] = merged.flatMap((u, file) => [
      ...(u.form106 && counted({ file }) ? [doc({ file }, 'form106')] : []),
      ...(u.vendor ? u.payslips.filter((p) => counted({ file, page: p.page })).map((p) => doc({ file, page: p.page }, 'payslip')) : []),
    ]);
    return eng().peopleView(docs, state.people, refKey);
  };
  /** Not counted: the copies, and the documents set aside as another person's. */
  const notCountedIn = (merged: UploadedFile[]): Engine.DocRef[] => [...copiesNotCounted(merged), ...peopleIn(merged).setAside];
  /** Counted with the person checked because the user said so (their ID wasn't read): checked as theirs. */
  const confirmedIn = (merged: UploadedFile[], r: Engine.DocRef): boolean => peopleIn(merged).confirmed.some((c) => eng().sameRef(c, r));
  /** Every payslip read, by month, each document once, only the person checked's (and, unanswered, unknown ones on their own). */
  const entriesOf = (uploads: UploadedFile[]) => {
    const merged = withOcr(uploads);
    const confirmed = peopleIn(merged).confirmed;
    return eng().entriesByMonth(merged, notCountedIn(merged)).map((e) => (confirmed.some((c) => c.file === e.fileIndex && c.page === e.page) ? { ...e, fields: { ...e.fields, worker: eng().confirmedWorker() } } : e));
  };
  /** Which upload each counted Form 106 comes from (form106Docs' order; -1: typed in). */
  const formsCounted = (): number[] => [...(state.uploads ?? []).flatMap((u, i) => (u.form106 && !formNotCounted(i) ? [i] : [])), ...(state.form106Manual ? [-1] : [])];
  /** Values typed for a Form 106 (by its place in form106Docs) stay with that form when another copy is chosen. */
  const keepForm106Fixes = (before: number[]): void => {
    const after = formsCounted();
    state.form106Fixes = new Map([...state.form106Fixes].flatMap(([i, fix]) => {
      const at = after.indexOf(before[i] ?? -2);
      return at >= 0 ? [[at, fix] as const] : [];
    }));
  };
  /** File `i`'s Form 106 is a copy not counted. */
  const formNotCounted = (i: number): boolean => Boolean(state.uploads && engine) && notCountedIn(withOcr(state.uploads ?? [])).some((r) => r.file === i && r.page === undefined);
  /** The file's SHA-256 (src/upload/duplicates.ts fileDigest), from its bytes read at pick time: on the device, memory only. */
  const digests = new WeakMap<File, Promise<string | undefined>>();
  const digestOf = (f: File): Promise<string | undefined> => {
    let d = digests.get(f);
    if (!d) {
      d = (async () => {
        try {
          // The bytes read at pick time, without reporting a failure here: the file's own reading (bytesOf, in
          // readOne) reports it, named by the file's place in the list (the debug report).
          startBytes(f);
          const got = await fileBytes.get(f);
          return got?.bytes ? await eng().fileDigest(got.bytes) : undefined;
        } catch {
          // No Web Crypto (an old browser, a page not served over HTTPS): the file is read as any other.
          return undefined;
        }
      })();
      digests.set(f, d);
    }
    return d;
  };

  /**
   * Reads the chosen PDFs and images on this device (nothing is uploaded anywhere) and adds them to
   * the list. A file already in the list is skipped with a short note.
   */
  /** Bumped when the files are cleared (Clear all, a new check): a reading still going on then is dropped, not added. */
  let filesGeneration = 0;
  /** Files dropped on a start-screen goal card, held through "Who are you?" (see goTo); never read before it is answered. */
  let heldFiles: File[] = [];
  /** Set when a stop on "Who are you?" let held files go (unread): the stop notice says so (flow.early.dropLetGo) until the user leaves the step. */
  let heldLetGo = false;
  /** Continue was pressed on the upload step with a "which one should we use?" unanswered: the missing line shows. */
  let dupNeededShown = false;
  async function readFiles(given: File[]): Promise<void> {
    const generation = filesGeneration;
    await loadEngine();
    if (generation !== filesGeneration) return;
    // A name handed over as its UTF-8 bytes ("d798d795…png") shown as its letters, on every screen (src/upload/fileName.ts).
    const chosen = given.map(eng().withReadableName);
    const named: File[] = [];
    state.skipped = [];
    state.twice = [];
    for (const f of chosen) {
      if ([...state.files, ...state.pending, ...named].some((g) => sameFile(f, g))) state.skipped.push(f.name);
      else named.push(f);
    }
    // The identical file under another name (the same bytes): kept once, said at once (Matt, 2026-10-23 19:29).
    const known = new Set((await Promise.all([...state.files, ...state.pending].map(digestOf))).filter((d): d is string => d !== undefined));
    const fresh: File[] = [];
    for (const [i, d] of (await Promise.all(named.map(digestOf))).entries()) {
      const f = named[i];
      if (!f) continue;
      if (d !== undefined && known.has(d)) state.twice.push(f.name);
      else {
        if (d !== undefined) known.add(d);
        fresh.push(f);
      }
    }
    if (generation !== filesGeneration) return;
    if (fresh.length === 0) return render();
    state.pending = [...state.pending, ...fresh];
    // Files chosen (from the upload step, or straight away, e.g. shared from another app): the upload step shows
    // how each was read; its Continue goes on to the questions once nothing there needs the user.
    state.flow = { ...state.flow, goal: state.flow.goal ?? (state.files.length + state.pending.length > 1 ? 'several' : 'month'), step: 'upload' };
    render();
    const read: UploadedFile[] = [];
    for (const [n, file] of fresh.entries()) {
      debugWhere = `file ${state.files.length + n + 1} (${file.size < 1024 ? `${file.size} B` : `${Math.round(file.size / 1024)} KB`})`;
      try {
        read.push(await readOne(file));
      } catch (err) {
        noteError('upload', err);
        // An unexpected error in Magia Li's own code (not the file, not the PDF reader): said as such.
        read.push({ name: file.name, vendor: null, problem: 'appError', pages: [], payslips: [] });
      }
    }
    if (generation !== filesGeneration) return;
    // Appended when read, files and uploads together, so indices (and OCR jobs) stay aligned.
    const offset = state.files.length;
    state.files = [...state.files, ...fresh];
    state.uploads = [...(state.uploads ?? []), ...read];
    read.forEach((u, i) => {
      if (u.ocr) state.ocr.set(offset + i, { kind: u.ocr, status: 'offer', pages: [] });
      if (u.labelsRead) void showLabelsRead(u);
    });
    state.pending = state.pending.filter((f) => !fresh.includes(f));
    analyseUploads(state.uploads);
    // Reading finishes in the background: keep the keyboard focus where the user is (a menu, a field).
    renderKeepingFocus();
  }

  /**
   * One file, by what its bytes are (never its name or MIME type: phone pickers often give neither):
   * a PDF is read (with `password` for a protected one, in memory only); a JPEG, PNG or WebP goes to
   * OCR; a HEIC photo too where this browser can decode it, else a specific message; anything else is
   * "not a PDF or image".
   */
  async function readOne(file: File, password?: string): Promise<UploadedFile> {
    const got = file.size <= MAX_FILE_BYTES ? await bytesOf(file) : undefined;
    const method = got?.method ? { readMethod: got.method } : {};
    const none = (problem: UploadedFile['problem'], fileType?: UploadedFile['fileType']): UploadedFile => ({ name: file.name, vendor: null, ...(problem ? { problem } : {}), pages: [], payslips: [], ...(fileType ? { fileType } : {}), ...method });
    if (file.size > MAX_FILE_BYTES || !got) return none('tooLarge');
    if (!got.bytes) {
      // The phone wouldn't hand over the file's bytes (e.g. a cloud file not yet downloaded): not the reader.
      return none('fileUnreadable');
    }
    const bytes = got.bytes;
    // An empty file: a cloud file the phone hasn't downloaded (iCloud Drive, Google Drive), not a real document.
    if (bytes.length === 0) {
      noteError('fileRead', Object.assign(new Error('the file arrived empty'), { name: 'NotReadableError' }));
      return none('fileUnreadable');
    }
    const type = sniffType(bytes);
    if (type === 'pdf') {
      // Encrypted (seen in the bytes): ask for the password first, before the reader starts, so the prompt
      // appears even where the reader then fails. An empty password opens files locked only against editing.
      if (password === undefined && hasEncryptEntry(bytes)) return { ...none('needsPassword', 'pdf'), encrypted: true };
      // The reader, moving to the next configuration while it fails here (src/ui/pdfLoader.ts).
      const done = await readWithLadder((pdfjs) => eng().readPdfFile(pdfjs, file.name, bytes, noteError, password), noteError);
      if (!done) {
        // The reader itself can't load in this browser (e.g. an iPhone older than iOS 16.4): not the file's fault.
        return { ...none('readerFailed', 'pdf'), ...(hasEncryptEntry(bytes) ? { encrypted: true as const } : {}) };
      }
      state.pdfReader = done.reader.mode;
      state.pdfBuild = done.reader.legacy ? 'legacy' : 'modern';
      // Not a payslip layout: maybe a Form 106 (its reader loads only now, docs/form106.md).
      if (__FORM106_READY__ && done.result.problem === 'unknownLayout' && done.result.pages.length) {
        const f106 = await loadForm106();
        const form106 = f106.parseForm106(done.result.pages);
        // The read gate (src/form106/readGate.ts): a 106 layout whose key figures weren't read isn't recognised yet.
        const unrecognised = form106 && f106.form106Gate(form106, eng().defaultParams, today());
        // No known layout reads it: the generic reader (preview builds), every figure to be confirmed.
        const generic = (!form106 || unrecognised) && __FALLBACK__ ? await fallback106(done.result.pages) : undefined;
        const chosen = generic ?? form106;
        if (chosen) {
          if (!generic && unrecognised) return { ...done.result, ...method, unrecognised };
          const read: UploadedFile = { ...done.result, form106: chosen, ...method };
          delete read.problem;
          return read;
        }
      }
      if (__FALLBACK__ && done.result.problem === 'unknownLayout' && !done.result.unrecognised && done.result.pages.length) {
        const generic = await fallbackPayslips(done.result.pages);
        if (generic.length) return { ...done.result, ...method, labelsRead: generic };
      }
      return { ...done.result, ...method };
    }
    if (type === 'heic' && !(await canDecodeImage(new Blob([bytes as BlobPart])))) return none('heicUnsupported', 'heic');
    if (isImageType(type)) return { ...none('noText', type), ocr: 'photo' };
    return none('notPdf', 'unknown');
  }

  /**
   * A digital PDF read by the generic reader (src/fallback): its pages drawn as images, and each page's figures put on
   * the confirmation screen beside the crop of where they were printed ("Compare the image with the numbers"), as a scan
   * of an unknown layout is. No OCR: the numbers come from the PDF's own text. Until confirmed, nothing reaches a check.
   * Drawing fails (or the file was removed meanwhile): the file stays "couldn't read it", as without the reader.
   */
  async function showLabelsRead(u: UploadedFile): Promise<void> {
    const at = () => state.uploads?.indexOf(u) ?? -1;
    const file = state.files[at()];
    const labelsRead = u.labelsRead;
    if (!file || !labelsRead) return;
    let pages: OcrPage[] = [];
    try {
      const { bytes } = await bytesOf(file);
      if (!bytes) throw Object.assign(new Error('the file can no longer be read'), { name: 'NotReadableError' });
      // Every page read, beyond the OCR page limit: none is dropped.
      const images = await eng().pdfPages((await loadPdfjs(noteError)).pdfjs, bytes, state.passwords.get(at()), labelsRead.map((l) => l.page));
      const m = await loadFallback();
      pages = labelsRead.flatMap(({ page, read }) => {
        const img = images[page - 1];
        if (!img) return [];
        const ppp = img.pointsPerPixel ?? 1;
        const draft = m.fallbackDraft(read, m.pointsToPixels(ppp, img.gray.height));
        const result: PageResult = { draft, items: [], gray: img.gray, quality: { lowResolution: false, blurred: false }, confidence: 100, quarters: 0, skew: 0, resampled: false, threshold: 'otsu', pointsPerPixel: ppp };
        return [{ page, result, original: structuredClone(draft), draft: structuredClone(draft), confirmed: false }];
      });
    } catch (err) {
      noteError('parse', err);
    }
    const index = at();
    if (index < 0 || !state.uploads) return;
    if (pages.length) {
      state.ocr.set(index, { kind: 'labels', status: 'review', pages });
      acceptForTranslation();
    }
    else {
      const notRead = { ...u };
      delete notRead.labelsRead;
      state.uploads[index] = notRead;
    }
    analyseUploads(state.uploads);
    renderKeepingFocus();
  }

  /** Whether this browser can decode the image (Safari decodes iPhone HEIC photos; most others can't). */
  async function canDecodeImage(file: Blob): Promise<boolean> {
    try {
      const bitmap = await createImageBitmap(file);
      bitmap.close();
      return true;
    } catch {
      return false;
    }
  }

  /** Opens a password-protected PDF with the password typed for it; the password lives in memory only. */
  async function unlockFile(index: number, password: string): Promise<void> {
    const file = state.files[index];
    const uploads = state.uploads;
    if (!file || !uploads) return;
    const u = await readOne(file, password);
    const opened = u.problem !== 'wrongPassword' && u.problem !== 'needsPassword';
    if (opened) state.passwords.set(index, password);
    state.uploads = uploads.map((x, i) => (i === index ? u : x));
    if (u.ocr) state.ocr.set(index, { kind: u.ocr, status: 'offer', pages: [] });
    if (u.labelsRead) void showLabelsRead(u);
    analyseUploads(state.uploads);
    render();
    document.getElementById(opened ? 'pdf-choose' : `password-${index}`)?.focus();
  }

  /** Removes one file; the checks run again on the rest. */
  function removeFile(index: number): void {
    const uploads = state.uploads;
    if (!uploads) return;
    // Open translations are by payslip position, which shifts.
    state.translated = new Set();
    // A reading in progress belongs to a file index that is about to shift: stop it (it can be restarted).
    if ([...state.ocr.values()].some((j) => j.status === 'running' || j.status === 'queued')) stopOcr();
    state.files = state.files.filter((_, i) => i !== index);
    state.uploads = uploads.filter((_, i) => i !== index);
    state.ocr = new Map([...state.ocr].filter(([i]) => i !== index).map(([i, j]) => [i > index ? i - 1 : i, j]));
    state.passwords = new Map([...state.passwords].filter(([i]) => i !== index).map(([i, p]) => [i > index ? i - 1 : i, p]));
    state.skipped = [];
    state.twice = [];
    if (state.uploads.length === 0) return clearFiles();
    analyseUploads(state.uploads);
    render();
  }

  /** "Clear all": every file, its OCR and the results from them. */
  function clearFiles(): void {
    stopOcr();
    filesGeneration++;
    state.pending = [];
    state.ocr = new Map();
    state.passwords = new Map();
    state.startDates = new Map();
    state.vouched = new Map();
    state.files = [];
    state.uploads = undefined;
    state.skipped = [];
    state.twice = [];
    state.dupKeep = new Map();
    state.people = {};
    state.result = undefined;
    state.diag.report = undefined;
    state.translated = new Set();
    state.flow.step = 'upload';
    render();
  }

  /**
   * The OCR reader, loaded with the first reading (src/ocr/readQueue.ts): one shared engine, and the files read through
   * it one at a time, in the order asked.
   */
  let ocrReader: { mod: typeof ReadQueueModule; queue: ReadQueue<OcrJob>; engine: SharedEngine<OcrEngine, OcrProgress> } | undefined;
  async function reader(): Promise<NonNullable<typeof ocrReader>> {
    const mod = await import('../ocr/readQueue');
    ocrReader ??= {
      mod,
      queue: mod.createReadQueue(readJob, (job) => job.retry === 'pending' && Boolean((job.retry = 'done'))),
      engine: mod.sharedEngine(async (onProgress, signal) => (await import('../ocr/browser')).createOcrEngine(onProgress, signal)),
    };
    return ocrReader;
  }
  /** How long each scan or photo took to read in this session (ms, the engine's start not included): design D's time left. */
  const readTimes: number[] = [];
  /** Ends the file being read now: cancelled by the user, or stopped because it made no progress. */
  let stopCurrent: ((why: 'cancel' | 'timeout') => void) | undefined;
  const jobIndex = (job: OcrJob): number | undefined => [...state.ocr].find(([, j]) => j === job)?.[0];

  /** Stops all OCR: the files waiting are taken out of line, the one being read is stopped (a new engine is made next time). */
  function stopOcr(): void {
    ocrReader?.queue.clear();
    // Every file waiting or being read, also those put in line while the reader was still loading (runOcr adds only
    // files still waiting once it has loaded).
    for (const job of state.ocr.values()) if (job.status === 'queued' || job.status === 'running') job.status = 'cancelled';
    stopCurrent?.('cancel');
    ocrReader?.engine.stop();
  }

  /** Cancel on one file's card: out of line if it was waiting, stopped if it is being read (the next one starts). */
  function cancelOcr(job: OcrJob): void {
    if (job.status === 'queued') ocrReader?.queue.remove(job);
    else if (job.status === 'running') stopCurrent?.('cancel');
    job.status = 'cancelled';
  }

  /** The OCR progress line, updated in place so typing elsewhere isn't interrupted. */
  function showOcrProgress(index: number): void {
    const job = state.ocr.get(index);
    // Design D: the file's row and the overall bar (the text says only "Reading…": the stages are not news to the reader).
    if (__FLOW_D__ && job && state.fd) state.fd.showReadingProgress(`fd-file-${index}`, job.progress?.stage === 'recognize' ? job.progress.progress : undefined);
    const node = document.getElementById(`ocr-progress-${index}`);
    if (!job || !node) return;
    const p = job.progress;
    const pct = p?.progress === undefined ? '' : ` ${Math.round(p.progress * 100)}%`;
    node.textContent = `${t(state.lang, `ocr.stage.${p?.stage ?? 'prepare'}`)}${pct}`;
    const bar = document.getElementById(`ocr-bar-${index}`) as HTMLProgressElement | null;
    if (bar) {
      if (p?.progress === undefined) bar.removeAttribute('value');
      else bar.value = p.progress;
    }
  }

  /**
   * Each chosen file's bytes, read the moment it was picked (src/upload/fileBytes.ts) and kept in page
   * memory for later steps (a password, OCR), so the phone's file is never asked for again.
   */
  const fileBytes = new WeakMap<File, Promise<BytesResult>>();
  function startBytes(file: File): void {
    if (file.size <= MAX_FILE_BYTES && !fileBytes.has(file)) fileBytes.set(file, startReading(file));
  }
  const reported = new WeakSet<File>();
  async function bytesOf(file: File): Promise<BytesResult> {
    startBytes(file);
    const r = await (fileBytes.get(file) ?? startReading(file));
    if (!reported.has(file)) {
      reported.add(file);
      // Each refused method, by stage and error type; the final failure as fileRead.
      for (const f of r.failures) noteError(f.method === 'arrayBuffer' ? 'fileReadArrayBuffer' : 'fileReadFileReader', f.error);
      if (!r.bytes) {
        // A new record (the last method's error is already counted under its own stage).
        const last = describeError(r.failures.at(-1)?.error ?? new Error('no bytes'));
        noteError('fileRead', Object.assign(new Error(last.message), { name: last.name }));
      }
    }
    return r;
  }

  /** Puts files' scans in line to be read ("Read this file", "Read all files", "Try again"); the first starts at once. */
  async function runOcr(indices: number[]): Promise<void> {
    const jobs = indices.flatMap((i) => state.ocr.get(i) ?? []);
    if (!jobs.length) return;
    for (const job of jobs) {
      job.status = 'queued';
      job.pages = [];
      delete job.failure;
      delete job.progress;
      delete job.retry;
    }
    render();
    document.getElementById(`ocr-cancel-${indices[0] ?? 0}`)?.focus();
    const { queue, engine } = await reader();
    // The engine starts now, before the first file's turn (a failed start is that file's to report).
    engine.get().catch(() => undefined);
    // A file cancelled while the reader loaded stays out.
    for (const job of jobs) if (job.status === 'queued') queue.add(job);
  }

  /** Reads one scanned PDF or photo with OCR, page by page, into drafts for the user to confirm (its turn in the queue). */
  async function readJob(job: OcrJob): Promise<void> {
    const r = await reader();
    const index = jobIndex(job);
    const file = index === undefined ? undefined : state.files[index];
    if (index === undefined || !file || job.status !== 'queued') return;
    debugWhere = `file ${index + 1} (OCR)`;
    job.status = 'running';
    job.progress = { stage: 'download', progress: 0 };
    const focusWasHere = (): boolean => {
      const a = document.activeElement;
      // The step's heading counts as lost too: a redraw puts the focus there when its control is gone (src/ui/focusKeeper.ts).
      return !a || a === document.body || a.matches(HEADING_SELECTOR) || Boolean(a.closest(`#ocr-card-${index}`));
    };
    renderKeepingFocus();
    // Cancel (this file or all) and a reading that stalls end the wait at once, whatever step the reading is in; the
    // engine is stopped, so nothing it still does reaches this file.
    let live = true;
    let watch: { poke(): void; stop(): void } | undefined;
    const stopped = new Promise<never>((_, reject) => {
      stopCurrent = (why) => {
        if (!live) return;
        live = false;
        reject(why === 'timeout' ? new r.mod.OcrTimeout('stall') : new r.mod.OcrCancelled());
        r.engine.stop();
      };
    });
    stopped.catch(() => undefined);
    // The shared engine's progress goes to this file while it is read (stage marks for timing: performance.getEntriesByType('mark')).
    r.engine.listen((p: OcrProgress) => {
      performance.mark(`ocr:${p.stage}`);
      job.progress = p;
      watch?.poke();
      showOcrProgress(index);
    });
    const read = async (): Promise<void> => {
      const engine = await r.engine.get();
      job.readStart = performance.now();
      // From here on, a reading that reports no progress for too long is stopped (the start has its own limit).
      if (live) watch = r.mod.stallWatch(r.mod.ocrStallTimeoutMs(), () => stopCurrent?.('timeout'));
      let pages;
      try {
        const { bytes } = await bytesOf(file);
        if (!bytes) throw Object.assign(new Error('the file can no longer be read'), { name: 'NotReadableError' });
        performance.mark('ocr:pages-start');
        pages = job.kind === 'photo' ? [await eng().photoPage(new Blob([bytes as BlobPart]))] : await eng().pdfPages((await loadPdfjs(noteError)).pdfjs, bytes, state.passwords.get(index));
      } catch (err) {
        // Stopped meanwhile (cancel, or no progress): the stop has already said why.
        if (!live) return;
        noteError('upload', err);
        // A photo over 64 megapixels (src/ocr/photo.ts) is refused with its own way on, not "this browser can't open it".
        job.failure = err instanceof Error && err.name === 'PhotoTooLarge' ? 'tooLarge' : 'decode';
        throw err;
      }
      if (!live) return;
      performance.mark('ocr:pages-end');
      // Per-page routing: only the pages the reader marked (a mixed file's scans, doubtful digital pages).
      const wanted = state.uploads?.[index]?.ocrPages;
      // A page's reading with the other binarisation, when one was made: the Form 106 cross-check below uses it.
      const others = new Map<number, PageResult>();
      for (const [n, page] of pages.entries()) {
        if (wanted && !wanted.includes(n + 1)) continue;
        const { other, ...result } = await engine.read(page, (d) => eng().gate(d, eng().defaultParams).ok);
        if (!live) return;
        // Kept only while every page is unclaimed by a payslip layout (the cross-check needs that), and only in builds that
        // read Form 106s: a page image each.
        if (result.draft.vendor !== null) others.clear();
        else if (__FORM106_READY__ && other && job.pages.every((p) => p.result.draft.vendor === null)) others.set(n + 1, other);
        job.pages.push({ page: n + 1, result, original: result.draft, draft: structuredClone(result.draft), confirmed: false });
        const pictures = scanPictures.get(file) ?? new Map<number, PageResult>();
        scanPictures.set(file, pictures.set(n + 1, result));
      }
      performance.mark('ocr:read-end');
      // No payslip on the scan: maybe a Form 106 (same text items as a PDF's, docs/form106.md).
      const upload = state.uploads?.[index];
      if (__FORM106_READY__ && upload && job.pages.length && job.pages.every((p) => p.result.draft.vendor === null)) {
        // A resampled page (straightened, enlarged, a photo) is read twice; figures the two reads don't share are asked.
        const reread = async () => {
          const out = [];
          for (const jp of job.pages) {
            const src = pages[jp.page - 1];
            const threshold = jp.result.threshold === 'otsu' ? 'adaptive' : 'otsu';
            // Already read with that binarisation (the same reading, src/ocr/pipeline.ts `other`): not read a third time.
            const done = others.get(jp.page);
            if (done?.threshold === threshold) out.push(done);
            else if (src) out.push(await engine.read({ ...src, threshold }, () => true));
          }
          return out;
        };
        const f106 = await loadForm106();
        const known = await f106.parseScanChecked(job.pages.map((p) => p.result), reread);
        if (!live) return;
        // No known layout reads it: the generic reader on the same OCR text (preview builds), every figure to be confirmed.
        const generic = (!known || f106.form106Gate(known, eng().defaultParams, today())) && __FALLBACK__ ? await fallback106(job.pages.map((p) => ({ items: p.result.items })), true) : undefined;
        if (!live) return;
        const form106 = generic ?? known;
        if (form106 && state.uploads) {
          const scanned = eng().withScanned106(upload, form106, job.pages.map((p) => p.result));
          // The read gate, as for a digital form: not recognised → the unknown-format offer, its OCR text kept for the layout.
          const unrecognised = f106.form106Gate(form106, eng().defaultParams, today());
          if (unrecognised) {
            const notRecognised: UploadedFile = { ...scanned, problem: 'unknownLayout', unrecognised };
            delete notRecognised.form106;
            delete notRecognised.ocr;
            state.uploads[index] = notRecognised;
          } else state.uploads[index] = scanned;
          state.ocr.delete(index);
          analyseUploads(state.uploads);
          return;
        }
      }
      if (!live) return;
      // The Form 106 goal: a scan that is neither a 106 nor a known vendor's payslip isn't put on the payslip review
      // screen (its months and lines aren't a 106's); it can be typed in instead.
      if (state.flow.goal === 'form106' && job.pages.length && job.pages.every((p) => p.result.draft.vendor === null)) {
        job.status = 'failed';
        job.failure = 'notForm106';
        return;
      }
      // A photo in which no text at all was found (blurry, dark, at an angle) has nothing to check: "we couldn't read the
      // numbers" with the photo tips (design D screen 8), not an empty review screen.
      const nothing = !job.pages.length || (job.kind === 'photo' && job.pages.every((p) => p.result.items.length === 0));
      job.status = nothing ? 'failed' : 'review';
      if (nothing) job.failure = 'nothingRead';
    };
    const reading = read();
    // When a stop wins the race, the reading's own rejection that follows is expected.
    reading.catch(() => undefined);
    try {
      await Promise.race([reading, stopped]);
    } catch (err) {
      if (err instanceof Error && err.name === 'Timeout') {
        // Too slow to start, or stuck: the "couldn't read" card (try again, or type the numbers in).
        job.failure = 'timeout';
        job.status = 'failed';
        noteError((err as InstanceType<typeof r.mod.OcrTimeout>).phase === 'start' ? 'ocrStart' : 'parse', err);
        r.engine.stop();
      } else if (err instanceof Error && err.message === 'cancelled') job.status = 'cancelled';
      else {
        // The engine's files not arriving ("couldn't download") is told apart from the engine failing ("something went
        // wrong": try again, or type the numbers in); src/ui/ocrFailure.ts, redesign screen 8 (Matt, 2026-10-22 18:00).
        const classify = (() => {
          try {
            return eng().engineFailureOf;
          } catch {
            return undefined; // the engine chunk itself isn't loaded: nothing to tell apart
          }
        })();
        const failure = err instanceof Error && classify ? classify({ name: err.name, stage: (err as { stage?: string }).stage, message: (err as { causeMessage?: string }).causeMessage ?? err.message, online: navigator.onLine !== false }) : 'error';
        if (err instanceof Error && err.name === 'OcrEngineUnavailable') {
          job.failure = failure;
          // Its startup stage and the cause's type (src/ocr/browser.ts), never the message.
          const e = err as Error & { stage?: ErrorStep; causeName?: string };
          noteError(e.stage ?? 'ocrStart', Object.assign(new Error(e.message), { name: e.causeName ?? 'Error' }));
          // Workers can't start in this browser at all: a second try would fail the same way.
          if (e.stage === 'workerSpawn') job.retry = 'done';
        }
        if (!job.failure) {
          noteError('parse', err);
          // The engine's own code that didn't arrive (its chunk, offline) is a download failure too.
          if (failure === 'download') job.failure = 'download';
        }
        // Local console only (never sent): engine and loading errors carry no payslip content. A
        // rejection that isn't an Error (a library's empty reason) is named rather than logged as undefined.
        console.error('OCR failed:', err instanceof Error ? err : new Error(`non-Error rejection: ${String(err)}`));
        job.failure ??= 'error';
        job.status = 'failed';
        // A new engine for the next file.
        r.engine.stop();
      }
    } finally {
      live = false;
      watch?.stop();
      stopCurrent = undefined;
      r.engine.listen(undefined);
      // A file read to the end (to check, or read as a Form 106): its time, without the engine's start, for the time left.
      if (job.readStart !== undefined && ((job.status as OcrJob['status']) === 'review' || state.ocr.get(index) !== job)) readTimes.push(performance.now() - job.readStart);
      delete job.readStart;
    }
    // Couldn't download the engine, took too long (seen once in a batch on a fresh profile, 2026-10-22) or the engine failed
    // (a Tesseract worker can crash when its memory runs out: "memory access out of bounds", seen in the private gate the
    // same day): one more try, automatically, after the other files, on a fresh engine (stopped above); the second
    // failure stays on the card.
    if (job.status === 'failed' && (job.failure === 'download' || job.failure === 'timeout' || job.failure === 'error') && !job.retry) {
      job.retry = 'pending';
      job.status = 'queued';
      job.pages = [];
      delete job.failure;
      delete job.progress;
    }
    // Focus follows the reading only if it was on this file's card (and it wasn't cancelled there): to the next file
    // waiting, else this file's result.
    const follow = focusWasHere() && job.status !== 'cancelled';
    const next = r.queue.waiting()[0];
    // A file put back for its automatic second try counts as waiting (the queue re-adds it once this returns).
    const nextIndex = next ? jobIndex(next) : job.retry === 'pending' ? index : undefined;
    acceptForTranslation();
    renderKeepingFocus();
    if (follow) document.getElementById(nextIndex !== undefined ? `ocr-cancel-${nextIndex}` : state.ocr.has(index) ? `ocr-job-${index}` : 'upload-heading')?.focus();
  }

  /**
   * A protected PDF's password, asked on its row: what it is usually (the ID number), and that it is
   * used only on this device to open the file, never stored or sent. Not a <form> submit to anywhere:
   * the password goes straight to PDF.js in memory.
   */
  function passwordForm(index: number, name: string, wrong: boolean): HTMLElement {
    const tr = (key: string, params?: Record<string, string | number>) => t(state.lang, key, params);
    const input = el('input', { type: 'password', id: `password-${index}`, autocomplete: 'off', autocapitalize: 'off', spellcheck: 'false', dir: 'ltr', 'aria-describedby': `password-help-${index} file-status-${index}`, ...(wrong ? { 'aria-invalid': 'true' } : {}) });
    // Empty is allowed: some PDFs are locked only against editing and open without a password.
    const open = () => void unlockFile(index, input.value);
    input.addEventListener('keydown', (e) => {
      if (e.key === 'Enter') open();
    });
    return el('div', { class: 'password-form' },
      el('label', { for: `password-${index}` }, tr('upload.password.label', { name })),
      el('p', { id: `password-help-${index}`, class: 'help' }, tr('upload.password.help'), ' ', tr('upload.password.empty')),
      el('div', { class: 'row' }, input, button(tr('upload.password.open'), open)));
  }

  /**
   * The uploaded files, sorted by the pay month read from their content (src/ui/fileList.ts): name,
   * month, status, and a remove button each; "Clear all" below. Removing a file reruns the checks.
   */
  interface FileListParts {
    /** The message heading (upload.md): what was read, or what couldn't be; '' in the plain layout. */
    result: HTMLElement | '';
    /** The list (its heading is only its label once the message heading says it). */
    list: (HTMLElement | '')[];
    /** The "can't read it" remedy: type the numbers in. */
    enter: HTMLElement | '';
    /** "Remove all files". */
    clear: HTMLElement | '';
  }
  function renderFileList(mode: 'plain' | 'message' | 'cannotRead' = 'plain', d?: 'reading' | 'ready'): FileListParts | undefined {
    const tr = (key: string, params?: Record<string, string | number>) => t(state.lang, key, params);
    const uploads = state.uploads ?? [];
    if (uploads.length === 0 && state.pending.length === 0) return undefined;
    const ocrState = (i: number): OcrState | undefined => {
      const j = state.ocr.get(i);
      if (!j) return undefined;
      if (j.status === 'review') return j.pages.length > 0 && j.pages.every((p) => p.confirmed) ? 'confirmed' : 'review';
      return j.status;
    };
    // A Form 106's place among the forms in play (state.form106Fixes is keyed by it).
    // Its place in form106Docs(), which leaves out a copy not used (src/upload/duplicates.ts); that copy has none (-1).
    const places = eng().formPlaces(uploads, notCountedIn(withOcr(uploads)));
    const formIndex = (i: number) => places[i] ?? -1;
    // Months and tax years typed in on a row count as read (the list and the checks agree).
    const fixed = withOcr(uploads).map((u, i): UploadedFile => ({
      ...u,
      payslips: u.payslips.map((p) => {
        const month = state.fieldFixes.get(`${u.name}#${p.page}`)?.payMonth;
        return month ? { ...p, fields: { ...p.fields, payMonth: month } } : p;
      }),
      ...(u.form106 ? { form106: { ...u.form106, fields: { ...u.form106.fields, ...state.form106Fixes.get(formIndex(i)) } } } : {}),
    }));
    const rows = fileRows(fixed, ocrState, state.pending.map((f) => f.name));
    // Name · document type · period · status, each its own part (never run together); while reading only the status.
    const period = (r: FileRow) =>
      r.periodMissing === 'taxYear' ? tr('upload.taxYearNotFound')
        : r.periodMissing && !r.months.length ? tr('upload.monthNotFound')
        : r.taxYear ? tr('upload.taxYear', { year: String(r.taxYear) })
        : r.months.length === 0 ? ''
        : r.months.length === 1 ? namedMonth(r.months[0] ?? '')
        : tr('upload.monthRange', { first: namedMonth(r.months[0] ?? ''), last: namedMonth(r.months.at(-1) ?? ''), count: r.months.length });
    /** The period asked for right on the row (the dead-ends component): the first page without a month, or the 106's tax year. */
    const periodPicker = (r: FileRow): HTMLElement | '' => {
      const index = r.index;
      if (index === undefined || !r.periodMissing) return '';
      const u = fixed[index];
      if (!u) return '';
      if (r.periodMissing === 'taxYear') {
        return eng().missingInfo({
          el, tr, id: `file-${index}-year`, level: 4, missing: ['taxYear'], answer: () => undefined, monthName,
          texts: { heading: tr('upload.period.yearHeading'), help: tr('upload.period.yearHelp'), use: tr('upload.period.yearUse') },
          onUse: (values) => useForm106Fix([formIndex(index)], values),
        });
      }
      const page = r.periodMissing.pages[0];
      const p = u.payslips.find((x) => x.page === page);
      if (!p) return '';
      return eng().missingInfo({
        el, tr, id: `file-${index}-month-${p.page}`, level: 4, missing: ['payMonth'], answer: () => undefined, monthName,
        texts: { heading: tr(u.payslips.length > 1 ? 'upload.period.monthHeadingPage' : 'upload.period.monthHeading', { page: p.page }), help: tr('upload.period.monthHelp'), use: tr('upload.period.monthUse') },
        onUse: (values) => usePayslipFix(`${u.name}#${p.page}`, p.fields, values),
      });
    };
    const merged = withOcr(uploads);
    const items = rows.map((r) => {
      const reason = r.problem === 'unknownLayout' ? unknownReasonKey(r.index) : `upload.reason.${r.problem}`;
      // A payslip PDF read in part: the payslips the read gate turned away are named on the row (Matt, 2026-10-21).
      const away = r.index === undefined ? [] : turnedAwayPages(r.status, merged[r.index]);
      const noText = r.noTextPages ?? [];
      // A Form 106's type is its own part of the row: its status is just "Read".
      // One file that couldn't be read: the reason is said once, in the message above the list.
      const status = mode === 'cannotRead' && rows.length === 1 && r.status === 'notRecognised' ? tr('upload.status.notRead')
        : tr(`upload.status.${r.status === 'form106' ? 'read' : r.status}`) + (r.problem ? ` ${tr(reason)}` : '')
          + (away.length ? ` · ${tr('upload.status.payslipsNotRead', { pages: away.join(', '), count: away.length })}` : '')
          + (noText.length ? ` · ${tr('upload.status.pagesNoText', { pages: noText.join(', '), count: noText.length })}` : '');
      const remove = r.index === undefined ? '' : (() => {
        const index = r.index;
        const b = button('✕', () => {
          // Where the rows are on the screen now (design D sorts and folds them; never their order in the files).
          const before = removeLabels();
          const at = before.indexOf(tr('upload.remove', { name: r.name }));
          // A file that gave nothing (not read, a scan not read yet) is removed without asking.
          const gave = payslipsOfFile(index) > 0 || Boolean(state.uploads?.[index]?.form106);
          void guardLoss('removeFile', state.lang, LANGUAGES[state.lang].dir, gave ? { file: r.name } : {}, () => {
            removeFile(index);
            // Focus stays in the list: the next row on the screen, else the one before, else "Add files".
            focusAfterRemoving(before, at);
          }, 'remove');
        }, 'secondary');
        b.classList.add('remove');
        b.setAttribute('aria-label', tr('upload.remove', { name: r.name }));
        return b;
      })();
      // Visible "·" between the parts; screen readers hear a pause (a hidden comma) instead.
      const sep = () => el('span', { class: 'sep' }, el('span', { 'aria-hidden': 'true' }, '·'), el('span', { class: 'visually-hidden' }, ', '));
      const when = period(r);
      const notFound = r.periodMissing === 'taxYear' || (r.periodMissing !== undefined && !r.months.length);
      const parts = [
        r.docType ? el('span', { class: 'file-type' }, tr(`upload.type.${r.docType}`)) : '',
        when ? el('span', { class: `file-month${notFound ? ' not-found' : ''}`, dir: 'auto' }, when) : '',
        el('span', { class: 'file-status', id: `file-status-${r.index ?? 'x'}` }, status),
      ].filter((x): x is HTMLSpanElement => x !== '');
      const picker = periodPicker(r);
      if (d) {
        // Design D: one row per file: what it is (its months, else its name), then the name and where its reading is.
        const reading = r.status === 'reading' || r.status === 'scanReading';
        // A scan not read yet waits for "Read all files" (clock mark, its own status words).
        const waiting = r.status === 'scanQueued' || r.status === 'scanToRead';
        const done = ['read', 'scanRead', 'labelsRead', 'form106'].includes(r.status) && !r.periodMissing && !away.length && !noText.length;
        // A file in a format we don't read: the short status on the row, the explanation under a closed "Why?" (at most
        // 40 words above the fold); its remedy, "Enter the numbers yourself", stays visible under the list. Every other
        // problem keeps its reason on the row: there the reason holds the fix (never a dead end).
        const short = r.problem === 'unknownLayout' && !(mode === 'cannotRead' && rows.length === 1)
          ? (r.status === 'notRecognised' ? tr('upload.status.notRead') : tr(`upload.status.${r.status === 'form106' ? 'read' : r.status}`))
            + (away.length ? ` · ${tr('upload.status.payslipsNotRead', { pages: away.join(', '), count: away.length })}` : '')
          : status;
        const word = reading ? tr('readingD.reading') : r.status === 'scanQueued' ? tr('readingD.next') : done ? tr(d === 'reading' ? (r.status === 'scanRead' || (r.index !== undefined && Boolean(state.uploads?.[r.index]?.ocr || state.uploads?.[r.index]?.form106?.scan)) ? 'upload.status.scanRead' : 'upload.status.digitalRead') : 'readyD.fullyRead') : short;
        // Ready: a fully read row says nothing more than its check mark (less-text mockups); screen readers still hear it.
        const quietDone = done && d !== 'reading';
        const whyKey = `fd-file-${r.index ?? 'x'}-why`;
        const spec: FlowSteps.FileRowSpec = {
          state: reading ? 'reading' : waiting ? 'waiting' : done ? 'done' : 'problem',
          title: when && !notFound ? when : r.name,
          sub: quietDone ? (when && !notFound ? r.name : '') : when && !notFound ? `${r.name} · ${word}` : word,
          ...(quietDone ? { quiet: when && !notFound ? ` · ${word}` : word } : {}),
          why: !reading && !waiting && !done && short !== status
            ? { label: tr('uploadD.why'), text: tr(reason), fold: { open: state.moreOpen.has(whyKey), onToggle: (open) => (open ? state.moreOpen.add(whyKey) : state.moreOpen.delete(whyKey)) } }
            : undefined,
          id: `fd-file-${r.index ?? 'x'}`,
          extra: [
            d === 'ready' && remove ? Object.assign(remove, { textContent: '' }) : '',
            picker,
            r.status === 'locked' && r.index !== undefined ? passwordForm(r.index, r.name, r.problem === 'wrongPassword') : '',
          ],
        };
        if (d === 'ready' && remove) remove.append(fd().flowIcon('x'));
        return fd().fileRows(el, [spec], '').firstElementChild as HTMLLIElement;
      }
      const item = el('li', { class: `file-item status-${r.status}` },
        el('span', { class: 'file-name' }, el('bdi', { dir: 'ltr' }, r.name)),
        el('span', { class: 'file-meta' }, ...parts.flatMap((x, i) => (i ? [sep(), x] : [x]))),
        remove);
      if (picker) item.append(picker);
      if (r.status === 'locked' && r.index !== undefined) item.append(passwordForm(r.index, r.name, r.problem === 'wrongPassword'));
      return item;
    });
    const unrecognised = rows.some((r) => r.status === 'notRecognised');
    // What was read, counted as before (a Form 106 is one, never "0 payslips"); said to screen readers as a status.
    const entries = entriesOf(uploads);
    const counts = { forms: uploads.filter((u, i) => u.form106 && !formNotCounted(i)).length, payslips: entries.length, files: uploads.length };
    const summary = () => {
      if (counts.forms === 0) return tr(state.flow.goal === 'form106' && counts.payslips === 0 ? 'upload.summary.form106None' : 'upload.summary', counts);
      return tr(counts.payslips ? 'upload.summary.form106AndPayslips' : 'upload.summary.form106', counts);
    };
    const months = [...new Set(entries.map((e) => e.fields.payMonth).filter((m): m is NonNullable<typeof m> => m !== undefined))].sort();
    const span = months.length === 0 ? '' : months.length === 1 ? namedMonth(months[0] ?? '') : `${namedMonth(months[0] ?? '')} – ${namedMonth(months.at(-1) ?? '')}`;
    const years = uploads.flatMap((u) => (u.form106?.fields.taxYear ? [u.form106.fields.taxYear] : []));
    const resultText = (): string => {
      if (mode === 'cannotRead') return rows.length === 1 ? tr('upload.cannotRead', { name: `\u2068${rows[0]?.name ?? ''}\u2069` }) : tr('upload.cannotRead.many', { count: rows.length });
      if (counts.forms === 0) return span ? tr('upload.result.payslipsIn', { payslips: counts.payslips, months: span }) : tr('upload.result.payslips', { payslips: counts.payslips });
      if (counts.payslips) return tr('upload.result.form106AndPayslips', counts);
      return counts.forms === 1 && years.length === 1 ? tr('upload.result.form106Year', { year: String(years[0]) }) : tr('upload.result.form106', counts);
    };
    const showResult = mode !== 'plain' && state.pending.length === 0;
    const clearAll = uploads.length && (mode === 'plain' || uploads.length >= 3) ? button(tr('upload.clearAll'), () => void guardLoss('clearAll', state.lang, LANGUAGES[state.lang].dir, filesLoss(), () => {
      clearFiles();
      document.getElementById('pdf-choose')?.focus();
    }, 'clear'), 'secondary') : '';
    // A file we can't read: typing the numbers in is the way forward (the step's primary; sending its layout is secondary).
    // In the Form 106 goal that's the Form 106 form (enterByHand), not a payslip's.
    // Beside what was read, Continue is the primary and typing the unread file's numbers in is secondary.
    const enter = unrecognised ? button(tr('upload.enterManually'), enterByHand, mode === 'message' ? 'secondary' : 'primary') : '';
    if (d) return { result: '', list: [el('ul', { class: 'fd-files file-list', 'aria-label': tr('upload.listHeading', { count: rows.length }) }, ...items)], enter, clear: clearAll };
    return {
      result: showResult ? el('h3', { class: 'upload-result', id: 'upload-result', tabindex: '-1' }, resultText()) : '',
      list: [
        el('h3', { id: 'file-list-heading', ...(mode === 'plain' ? {} : { class: 'visually-hidden' }) }, tr('upload.listHeading', { count: rows.length })),
        el('ul', { class: 'file-list', 'aria-labelledby': 'file-list-heading' }, ...items),
        state.pending.length ? el('p', { role: 'status' }, tr('upload.reading', { count: state.pending.length }))
          : mode === 'cannotRead' || (mode !== 'plain' && counts.payslips + counts.forms === 0) ? ''
          : el('p', { role: 'status', class: mode === 'plain' ? 'help' : 'visually-hidden' }, summary()),
      ],
      enter,
      clear: clearAll,
    };
  }

  /**
   * OCR cards: offer (with the one-time download size), progress and cancel, waiting, or the confirmation screen. More
   * than one file to read: one offer for all of them, "Read all files", read one at a time (Matt, 2026-10-21); a file's
   * own button then only retries that file.
   */
  function renderOcr(): HTMLElement[] {
    const tr = (key: string, params?: Record<string, string | number>) => t(state.lang, key, params);
    const download = () => el('p', { class: 'help small' }, tr(state.flow.goal === 'form106' ? 'ocr.download.form106' : 'ocr.download', { mb: (__OCR_DOWNLOAD_BYTES__ / 1e6).toFixed(1) }));
    // A file with some pages read and the others without text has its own card (its offer names the pages).
    const toRead = [...state.ocr].filter(([i, j]) => (j.status === 'offer' || j.status === 'cancelled') && !noTextPagesAt(i).length).map(([i]) => i);
    const together = toRead.length > 1;
    const busy = [...state.ocr].filter(([, j]) => j.status === 'running' || j.status === 'queued');
    // "Cancel all" once, on the card being read (or the first waiting one), when more than one file is in line.
    const cancelAllOn = busy.length > 1 ? (busy.find(([, j]) => j.status === 'running') ?? busy[0])?.[0] : undefined;
    const focusAfterStop = (index: number) => document.getElementById(state.ocr.has(index) && document.getElementById(`ocr-job-${index}`) ? `ocr-job-${index}` : 'ocr-all')?.focus();
    const cancelButtons = (index: number, job: OcrJob): HTMLElement => {
      const cancel = button(tr('ocr.cancel'), () => {
        cancelOcr(job);
        render();
        focusAfterStop(index);
      }, 'secondary');
      cancel.id = `ocr-cancel-${index}`;
      const all = index === cancelAllOn ? button(tr('ocr.cancelAll'), () => {
        stopOcr();
        render();
        focusAfterStop(index);
      }, 'secondary') : '';
      if (all) all.id = 'ocr-cancel-all';
      return el('div', { class: 'row' }, cancel, all);
    };
    // Design D screen 8 (src/ui/flowD/errors.ts): a download that didn't finish, or a photo with nothing read, gets its
    // card there, then the PDF tip and "Enter the numbers by hand" once; other failures keep the cards below.
    const errorsD: HTMLElement[] = [];
    const cards = [...state.ocr.entries()].flatMap(([index, job]) => {
      if (together && toRead.includes(index)) return [];
      const name = state.uploads?.[index]?.name ?? '';
      const errorD = __FLOW_D__ && job.status === 'failed' ? state.fdErrors?.readErrorOf(job.failure, job.kind) : undefined;
      if (errorD && state.fdErrors) {
        errorsD.push(state.fdErrors.readErrorCard({
          el, tr, kind: errorD, name, id: `ocr-card-${index}`, headingId: `ocr-job-${index}`, form106: state.flow.goal === 'form106', primary: errorsD.length === 0, repeat: errorsD.some((c) => c.dataset['error'] === errorD),
          // A file that wasn't read gave nothing: removed without asking (guardLoss runs it at once, inside the tap).
          onPrimary: errorD === 'download' ? () => void runOcr([index]) : () => void guardLoss('removeFile', state.lang, LANGUAGES[state.lang].dir, {}, () => {
            // A new photo replaces this one: the camera on a phone, else the file picker.
            removeFile(index);
            // Focus on the step's heading first, so a cancelled picker leaves it there, not on the page.
            document.getElementById('upload-heading')?.focus();
            (document.getElementById('camera-photo') ?? document.getElementById('pdf-files'))?.click();
          }, 'remove'),
          onRemove: () => void guardLoss('removeFile', state.lang, LANGUAGES[state.lang].dir, {}, () => {
            removeFile(index);
            document.getElementById('upload-heading')?.focus();
          }, 'remove'),
        }));
        return [];
      }
      const offered = job.status === 'offer' || job.status === 'cancelled';
      // Some pages have no text, the others were read: the offer names them and sits beside Continue, not instead of it.
      const noText = offered ? noTextPagesAt(index) : [];
      // The offer is the screen's message: the question as the heading, the file in small print (upload.md, scan-offered).
      const heading = offered
        ? el('h3', { id: `ocr-job-${index}`, tabindex: '-1', class: noText.length ? '' : 'upload-result' }, tr(noText.length ? 'ocr.question.pages' : 'ocr.question'))
        // Design D, a file that couldn't be read: its name alone heads the card (the message says what it is; at most 40
        // words above the fold).
        : __FLOW_D__ && job.status === 'failed' ? el('h3', { id: `ocr-job-${index}`, tabindex: '-1' }, el('bdi', { dir: 'ltr' }, name))
        : el('h3', { id: `ocr-job-${index}`, tabindex: '-1' }, el('bdi', { dir: 'ltr' }, name), ': ', tr(`ocr.kind.${job.kind}`));
      const card = el('section', { class: 'ocr-job', id: `ocr-card-${index}`, 'aria-labelledby': `ocr-job-${index}` }, heading);
      if (offered) {
        card.append(
          el('p', { class: 'help small' }, el('bdi', { dir: 'ltr' }, name), noText.length ? '' : `: ${tr(`ocr.kind.${job.kind}`)}`),
          job.status === 'cancelled' ? el('p', { role: 'status' }, tr('ocr.cancelled')) : '',
          el('p', {}, noText.length ? tr('ocr.offer.pages', { pages: noText.join(', '), count: noText.length }) : tr(state.flow.goal === 'form106' ? 'ocr.offer.form106' : 'ocr.offer')),
          el('div', { class: 'row upload-actions' }, button(tr(noText.length ? 'ocr.readPages' : 'ocr.read'), () => void runOcr([index]), noText.length ? 'secondary' : 'primary'), noText.length ? '' : button(tr('upload.enterManually'), enterByHand, 'secondary')),
          // Under the button: the button no longer says "on this device", so this note does.
          download(),
        );
      } else if (job.status === 'queued') {
        card.append(el('p', { id: `ocr-progress-${index}`, role: 'status' }, tr('ocr.waiting')), cancelButtons(index, job));
      } else if (job.status === 'running') {
        const bar = el('progress', { id: `ocr-bar-${index}`, max: '1', 'aria-labelledby': `ocr-progress-${index}` });
        card.append(el('p', { id: `ocr-progress-${index}`, role: 'status' }, ''), bar, cancelButtons(index, job));
        queueMicrotask(() => showOcrProgress(index));
      } else if (job.status === 'failed') {
        // A download or engine failure may pass on a second try (back online); a file that can't be opened or read won't.
        const retry = job.failure === 'download' || job.failure === 'error' || job.failure === 'timeout' || job.failure === undefined ? button(tr('ocr.retry'), () => void runOcr([index])) : '';
        const enter = button(tr('upload.enterManually'), enterByHand, 'secondary');
        card.append(job.failure === 'tooLarge'
          ? eng().photoTooLargeBlock({ el, tr, platform: devicePlatform(), actions: [enter] })
          : el('div', { class: 'errors', role: 'alert' }, el('p', {}, tr(`ocr.failed.${job.failure ?? 'error'}`)), el('div', { class: 'row' }, retry, enter)));
      } else {
        job.pages.forEach((p, n) => card.append(renderOcrPage(index, n, p, job.pages.length)));
      }
      return [card];
    });
    if (together) {
      // The offer for every file not read yet is the screen's message (as one file's offer is).
      cards.unshift(el('section', { class: 'ocr-job', id: 'ocr-card-all', 'aria-labelledby': 'ocr-all' },
        el('h3', { id: 'ocr-all', tabindex: '-1', class: 'upload-result' }, tr('ocr.questionAll', { count: toRead.length })),
        el('p', {}, tr('ocr.offerAll')),
        el('div', { class: 'row upload-actions' }, Object.assign(button(tr('ocr.readAll'), () => void runOcr(toRead)), { id: 'ocr-read-all' }), button(tr('upload.enterManually'), enterByHand, 'secondary')),
        download()));
    }
    // "Report this error" (every build) once under the read-error cards (src/beta/errorReportOffer.ts; closed until
    // opened), after the PDF tip and "Enter the numbers by hand" so the remedies come first.
    if (errorsD.length && state.fdErrors) cards.push(el('div', { class: 'rv fd fd-errors' }, ...errorsD, state.fdErrors.pdfTip(el, tr), state.fdErrors.byHandLink(el, tr, enterByHand), __ERROR_REPORTS__ ? errorReportOffer(tr) : ''));
    return cards;
  }

  /**
   * The confirmation screen for one page (docs/phase2-plan.md 4.4): every key number next to a crop of
   * where it was read, editable; the reconciliation gate; one "these match my payslip" confirmation.
   */
  function renderOcrPage(index: number, n: number, p: OcrPage, pages: number): HTMLElement {
    const tr = (key: string, params?: Record<string, string | number>) => t(state.lang, key, params);
    const id = (k: string) => `ocr-${index}-${n}-${k}`;
    // A finished step collapses to one line; Edit opens it again.
    if (p.confirmed) {
      const name = state.uploads?.[index]?.name ?? '';
      const month = p.draft.values.payMonth.trim();
      const edit = fd().pencilButton(el, { id: id('edit'), label: tr('ocr.editFor', { file: name }), onClick: () => {
        p.confirmed = false;
        if (state.uploads) analyseUploads(state.uploads);
        render();
        document.getElementById(id('grossPay'))?.focus();
      } });
      return el('section', { class: 'ocr-review confirmed', 'aria-labelledby': id('status') },
        el('p', { id: id('status'), class: 'ocr-done', tabindex: '-1' }, el('span', { 'aria-hidden': 'true' }, '✓ '), tr('ocr.numbersChecked'), ' — ', el('bdi', { dir: 'ltr' }, name),
          month ? `, ${month}` : '', pages > 1 || state.uploads?.[index]?.ocrPages ? `, ${tr('results.page', { page: p.page })}` : '', edit));
    }
    const g = eng().gate(p.draft, eng().defaultParams);
    const { quality } = p.result;
    // The values the OCR was least sure of: marked in words (not colour alone) and linked to their input.
    const doubtful = eng().doubtfulValues(p.original);
    const hard = (k: string) => (doubtful.has(k) ? [el('span', { id: id(`${k}-hard`), class: 'hard-to-read' }, tr('ocr.hardToRead'))] : []);
    const describedBy = (k: string) => (doubtful.has(k) ? { 'aria-describedby': id(`${k}-hard`) } : {});
    const changed = () => {
      if (p.confirmed) return;
      renderKeepingFocus();
    };
    const crop = (box: OcrDraft['sources'][ConfirmKey], label: string) =>
      box ? Object.assign(cropOf(p.result, box), { role: 'img', ariaLabel: tr('ocr.cropAlt', { field: label }) }) : el('span', { class: 'help' }, tr('ocr.noCrop'));
    // Pension values outside the plausible range, and an hourly rate or hours that don't look right: flagged, and kept
    // out of the checks until vouched for or corrected.
    const pensionOff = eng().implausibleValues(p.draft, eng().defaultParams);
    const implausible: HeldKey[] = [...pensionOff, ...eng().unusualPayValues(p.draft, eng().defaultParams)];
    const removed = (k: string) => p.draft.removed?.includes(k) ?? false;
    const setRemoved = (k: string, on: boolean, restore: () => void, clear: () => void) => {
      p.draft.removed = (p.draft.removed ?? []).filter((x) => x !== k);
      if (on) {
        p.draft.removed.push(k);
        clear();
      } else restore();
      renderKeepingFocus();
      document.getElementById(id(on ? `${k}-undo` : k))?.focus();
    };
    /** "This is the wrong line / not on my payslip": clears the value, which then never reaches the checks. */
    const removedRow = (k: string, label: string, restore: () => void) =>
      el('div', { class: 'ocr-field removed' }, el('div', {}, el('span', {}, label)), el('p', { class: 'help', id: id(`${k}-removed`) }, tr('ocr.removed')),
        p.confirmed ? '' : Object.assign(button(tr('ocr.undoRemove'), () => setRemoved(k, false, restore, () => undefined), 'secondary'), { id: id(`${k}-undo`) }));
    const removeButton = (k: string, label: string, clear: () => void) => {
      if (p.confirmed) return '';
      const b = button(tr('ocr.removeLine'), () => setRemoved(k, true, () => undefined, clear), 'secondary');
      b.setAttribute('aria-label', tr('ocr.removeLineFor', { field: label }));
      return b;
    };
    const field = (key: ConfirmKey) => {
      const label = tr(`ocr.field.${key}`);
      if (removed(key)) return removedRow(key, label, () => (p.draft.values[key] = p.original.values[key]));
      const flagged = (implausible as ConfirmKey[]).includes(key);
      const vouched = flagged && (p.draft.vouched?.includes(key as HeldKey) ?? false);
      const warn = flagged
        ? [el('p', { id: id(`${key}-implausible`), class: 'implausible' }, tr(vouched ? 'ocr.implausibleVouched' : (pensionOff as ConfirmKey[]).includes(key) ? 'ocr.implausible' : 'ocr.unusual')),
           vouched || p.confirmed ? '' : button(tr('ocr.vouch'), () => {
             p.draft.vouched = [...(p.draft.vouched ?? []), key as HeldKey];
             renderKeepingFocus();
           }, 'secondary')]
        : [];
      const input = el('input', { id: id(key), type: 'text', inputmode: key === 'payMonth' ? 'text' : 'decimal', dir: 'ltr', autocomplete: 'off', ...describedBy(key), ...(flagged ? { 'aria-describedby': [doubtful.has(key) ? id(`${key}-hard`) : '', id(`${key}-implausible`)].filter(Boolean).join(' ') } : {}), ...(g.invalid.includes(key) ? { 'aria-invalid': 'true' } : {}) });
      input.value = p.draft.values[key];
      input.readOnly = p.confirmed;
      input.addEventListener('input', () => {
        p.draft.values[key] = input.value;
        // A corrected value is judged afresh: an earlier "this is right" applied to the old number.
        if (p.draft.vouched) p.draft.vouched = p.draft.vouched.filter((k) => k !== key);
      });
      input.addEventListener('change', changed);
      return el('div', { class: `ocr-field${doubtful.has(key) || flagged ? ' doubtful' : ''}` }, el('div', {}, el('label', { for: id(key) }, label), ...hard(key)), crop(p.draft.sources[key], label), input, ...warn,
        removeButton(key, label, () => (p.draft.values[key] = '')));
    };
    // Every key number, found or not: a blank one can be filled in from the payslip.
    const shown = eng().CONFIRM_KEYS;
    const lines = p.draft.lines.map((l, i) => {
      const label = l.label || tr('ocr.unnamedLine');
      if (removed(`line${i}`)) return removedRow(`line${i}`, label, () => undefined);
      const input = el('input', { id: id(`line${i}`), type: 'text', inputmode: 'decimal', dir: 'ltr', autocomplete: 'off', ...describedBy(`line${i}`) });
      input.value = l.amount;
      input.readOnly = p.confirmed;
      input.addEventListener('input', () => (l.amount = input.value));
      input.addEventListener('change', changed);
      return el('div', { class: `ocr-field${doubtful.has(`line${i}`) ? ' doubtful' : ''}` }, el('div', {}, el('label', { for: id(`line${i}`) }, el('bdi', {}, label)), ...hard(`line${i}`)), crop(l.source, label), input,
        removeButton(`line${i}`, label, () => undefined));
    });
    const confirm = button(tr('ocr.confirm'), () => {
      if (!eng().gate(p.draft, eng().defaultParams).ok) return render();
      p.confirmed = true;
      if (state.uploads) analyseUploads(state.uploads);
      // On to the questions, then the results (the confirmed numbers are now one line).
      if (!uploadNeedsUser()) goTo(afterUpload());
      else render();
    });
    if (!g.ok) confirm.setAttribute('aria-disabled', 'true');
    confirm.setAttribute('aria-describedby', id('gate'));
    const held = eng().heldBack(p.draft, eng().defaultParams);
    return el(
      'section',
      { class: `card ocr-review${p.confirmed ? ' confirmed' : ''}`, 'aria-labelledby': id('h') },
      el('h4', { id: id('h') }, pages > 1 || state.uploads?.[index]?.ocrPages ? tr('ocr.pageHeading', { page: p.page }) : tr('ocr.reviewHeading'), ' ', el('span', { class: 'badge' }, tr(state.uploads?.[index]?.labelsRead ? 'ocr.labelsBadge' : 'ocr.experimental'))),
      el('p', { class: 'help' }, tr('ocr.reviewHelp')),
      doubtful.size ? el('p', { class: 'note' }, tr('ocr.hardToReadSummary')) : '',
      held.length ? el('p', { class: 'note', role: 'note' }, (held.every((k) => (pensionOff as HeldKey[]).includes(k)) ? tr('ocr.implausibleSummary', { count: held.length }) : t(state.lang, 'ocr.unusualSummary', { count: held.length }))) : '',
      quality.blurred || quality.lowResolution ? el('div', { class: 'note', role: 'note' }, el('p', {}, tr(quality.blurred ? 'ocr.blurred' : 'ocr.lowResolution')), photoTipsList(true)) : '',
      g.ok
        ? el('p', { id: id('gate'), class: 'note' }, tr(g.linesToGross === 'pass' ? 'ocr.gatePass' : 'ocr.gatePassGrossToNet'))
        : el('div', { id: id('gate'), class: 'errors', role: 'alert' }, el('p', {}, tr('ocr.gateFail')), el('ul', {}, ...g.failures.map((f) => el('li', {},
            // Name what's still blank: "Still needed: Pay month, Total deductions".
            f === 'missing' && g.missing.length ? tr('ocr.gate.missingNamed', { fields: g.missing.map((k) => tr(`ocr.field.${k}`)).join(', ') }) : tr(`ocr.gate.${f}`))))),
        p.draft.vendor === null ? el('p', { class: 'note', role: 'note' }, tr('ocr.unknownLayoutNote')) : '',
      g.minimumWageHeld.length ? el('p', { class: 'note', role: 'note' }, tr('ocr.minimumWageHeld')) : '',
      el('fieldset', {}, el('legend', {}, tr('ocr.keyNumbers')), ...shown.map(field)),
      lines.length ? el('fieldset', {}, el('legend', {}, tr('ocr.paymentLines')), ...lines) : '',
      ...neededForChecks(index, n, p),
      el('div', { class: 'row' }, confirm),
    );
  }

  /**
   * Inputs the checks need that a payslip image didn't give (start date, pay basis), asked right
   * here so the checks can run after confirming. Optional: without them a check says what's missing.
   */
  function neededForChecks(index: number, n: number, p: OcrPage): HTMLElement[] {
    const tr = (key: string) => t(state.lang, key);
    const asks: HTMLElement[] = [];
    if (!p.original.fields.startDate) {
      asks.push(dateQuestion(`ocr-${index}-${n}-startDate`, tr('ocr.needed.startDate'), p.draft.fields.startDate, (v) => {
        if (v) p.draft.fields.startDate = v;
        else delete p.draft.fields.startDate;
        if (p.confirmed && state.uploads) analyseUploads(state.uploads);
        renderKeepingFocus();
      }));
    }
    if (!p.original.fields.payBasis) {
      // The same answer as the pay-basis question below (it applies to every payslip loaded).
      asks.push(radioGroup(`ocr-${index}-${n}-payBasis`, tr('questions.payBasis'), [
        ...payBasisOptions(tr),
        ['', tr('questions.dontKnow')],
      ], state.answers.payBasis ?? '', (v) => {
        setAnswer('payBasis', v === '' ? undefined : (v as PayBasisAnswer));
        document.querySelector<HTMLInputElement>(`input[name="ocr-${index}-${n}-payBasis"][value="${v}"]`)?.focus();
      }, 5));
    }
    return asks.length ? [el('fieldset', { class: 'ocr-needed' }, el('legend', {}, tr('ocr.needed.heading')), el('p', { class: 'help' }, tr('ocr.needed.help')), ...asks)] : [];
  }

  /** Crops are cut on demand from the page kept in memory (never stored). */
  function cropOf(result: PageResult, box: NonNullable<OcrDraft['sources'][ConfirmKey]>): HTMLCanvasElement {
    return eng().cropCanvas(result.gray, box);
  }

  /** Every payslip the questions should consider: the uploads', or the pasted one. */
  const fieldsInPlay = (): PayslipFields[] => {
    if (state.uploads) return entriesOf(state.uploads).map((e) => e.fields);
    if (state.source === 'form') {
      const r = eng().manualToFields(state.manual, state.manualVouched);
      return r.status === 'ok' ? [r.fields] : [];
    }
    const f = currentFields();
    return f ? [f] : [];
  };

  /** Switches language; everything entered stays (it lives in memory, and only the text changes). */
  async function setLang(next: Lang): Promise<void> {
    // Languages other than Hebrew and English are loaded when chosen (src/i18n): once, then from memory.
    await loadLanguage(next);
    if (state.f106) await state.f106.loadMessages(next, addMessages);
    if (state.tv) await state.tv.loadMessages(next, addMessages);
    if (state.rv) await state.rv.loadMessages(next, addMessages);
    if (state.fd) await state.fd.loadMessages(next, addMessages);
    if (calcMod) await calcMod.loadMessages(next, addMessages);
    if (state.fdErrors) await state.fdErrors.loadErrorMessages(next, addMessages);
    state.lang = next;
    saveLang(next);
    state.langMenuOpen = false;
    // A notice said in the old language is not left in the live region.
    announce('');
    render();
    welcome?.refresh();
  }

  /**
   * The language switcher, always in the header: a globe and the current language in its own script;
   * it opens a menu of every language, each in its own script (machine translations marked beta).
   * Esc or choosing closes it and returns focus to the switcher.
   */
  function languageMenu(lang: Lang): HTMLElement {
    const toggle = el('button', {
      type: 'button', id: 'lang-switch', class: 'lang-switch secondary', lang,
      'aria-expanded': String(state.langMenuOpen), 'aria-controls': 'lang-menu',
      // The visible name is part of the accessible name (WCAG 2.5.3).
      'aria-label': `${t(lang, 'app.language')}: ${LANGUAGES[lang].nativeName}`,
    }, globeIcon(), el('span', { class: 'lang-full' }, LANGUAGES[lang].nativeName),
    // Under 400 px the language code stands for the name (CSS); the full name stays in the accessible name.
    el('span', { class: 'lang-code', 'aria-hidden': 'true' }, lang.split('-')[0]?.toUpperCase() ?? lang));
    toggle.addEventListener('click', () => {
      state.langMenuOpen = !state.langMenuOpen;
      render();
      (state.langMenuOpen ? document.querySelector<HTMLElement>('#lang-menu [aria-current="true"]') : document.getElementById('lang-switch'))?.focus();
    });
    const menu = el('ul', { id: 'lang-menu', class: 'lang-menu', ...(state.langMenuOpen ? {} : { hidden: '' }) },
      ...LANGS.map((l) => {
        const b = el('button', { type: 'button', lang: l, dir: LANGUAGES[l].dir, ...(l === lang ? { 'aria-current': 'true' } : {}) },
          el('span', { class: 'lang-name' }, LANGUAGES[l].nativeName),
          !LANGUAGES[l].reviewed && LANGUAGES[l].betaNote ? el('span', { class: 'lang-beta' }, LANGUAGES[l].betaNote) : '');
        b.addEventListener('click', () => {
          void setLang(l).then(() => document.getElementById('lang-switch')?.focus());
        });
        return el('li', {}, b);
      }));
    const wrap = el('div', { class: 'lang' }, toggle, menu);
    wrap.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && state.langMenuOpen) {
        state.langMenuOpen = false;
        render();
        document.getElementById('lang-switch')?.focus();
      }
    });
    wrap.addEventListener('focusout', (e) => {
      // Closed in place, not redrawn: a redraw would remove the control the focus is moving to (docs/review/keyboard-walk.md K2).
      if (state.langMenuOpen && !wrap.contains(e.relatedTarget as Node | null) && e.relatedTarget !== null) {
        state.langMenuOpen = false;
        menu.hidden = true;
        toggle.setAttribute('aria-expanded', 'false');
      }
    });
    return wrap;
  }

  /**
   * Answers asked under the results this render, by key: an answer applies to every payslip, so it's asked once per
   * payslip (or per Form 106 block: `scope`), under the first result there that needs it; another result of the same
   * payslip asks it again, so its box is never empty (persona walk 2026-10-23: P04 recuperation, P08 notice). A later
   * payslip's result points to the first box with a link that opens and focuses it (persona audit M6; walk P23: the
   * pointer alone gave no way to the box, which can sit in a folded payslip section).
   */
  let askedOnPage: AskedOnPage = new Map();
  function askOnce(key: string, name: string, level: ChoiceSpec['level'], scope: string, box: string): HTMLElement | undefined {
    const plan = planAsk(askedOnPage, key, scope, box);
    if ('pointTo' in plan) {
      const heading = `${plan.pointTo}-heading`;
      const link = el('a', { href: `#${heading}`, class: 'asked-above-link' }, t(state.lang, 'missing.askedAbove'));
      link.addEventListener('click', (e) => {
        const target = document.getElementById(heading);
        if (!target) return;
        e.preventDefault();
        // The first box may sit in a folded payslip section or under "Also checked": those open first.
        for (let d = target.closest('details'); d; d = d.parentElement?.closest('details') ?? null) d.open = true;
        target.tabIndex = -1;
        target.scrollIntoView?.({ block: 'center' });
        target.focus();
      });
      return el('p', { class: 'help asked-above' }, link);
    }
    return answerQuestion(key, name, level);
  }

  /**
   * The other-income question of one tax year inside a Form 106 result's fill-in box (the questions step's own question,
   * src/form106/ui.ts form106Questions). Several boxes may be ticked, so the ticks are kept here until "Check again with
   * these" saves them (each tick would otherwise rerun the checks and close the box after the first one). The button
   * appears once something is ticked ("None of these" counts), so it can't be used with nothing chosen.
   */
  const incomeDrafts = new Map<string, IncomeSource[] | undefined>();
  function incomeSourcesHere(year: number | undefined, name: string, level: ChoiceSpec['level']): HTMLElement {
    const tr = (k: string, p?: Record<string, string | number>) => t(state.lang, k, p);
    const key = incomeSourcesKey(year);
    const wrap = el('div', { class: 'income-sources-here' });
    const draw = () => {
      const q = choiceQuestion(el, {
        name, idPrefix: `${name}-`, type: 'checkbox', level, className: 'status-question',
        question: year === undefined ? tr('f106.q.incomeSourcesNoYear') : tr('f106.q.incomeSources', { year: String(year) }),
        help: tr('f106.q.incomeSources.help'), none: tr('f106.q.incomeSources.none'), selected: incomeDrafts.get(name),
        options: INCOME_SOURCES.map((s) => ({ value: s, label: tr(`f106.source.${s}`) })),
        onChange: (v) => {
          incomeDrafts.set(name, v as IncomeSource[] | undefined);
          draw();
        },
      });
      const chosen = incomeDrafts.get(name);
      if (chosen === undefined) {
        wrap.replaceChildren(q);
        return;
      }
      const use = button(tr('missing.use'), () => {
        incomeDrafts.delete(name);
        setAnswer('incomeSources', { ...state.answers.incomeSources, [key]: chosen });
        // One primary-styled button per screen (the form's "Check these numbers" box has it).
      }, 'secondary');
      use.id = `${name}-use`;
      wrap.replaceChildren(q, el('div', { class: 'row missing-actions' }, use));
    };
    draw();
    return wrap;
  }

  /** The results step's choices card goes after the results, before the closing bottom bar if there is one. */
  function withBefore(body: (Node | string)[], card: (Node | string)[]): (Node | string)[] {
    const last = body[body.length - 1];
    return last instanceof HTMLElement && last.classList.contains('fd-bar') ? [...body.slice(0, -1), ...card, last] : [...body, ...card];
  }

  /** Every redraw: the keyboard keeps its place when the focused control is redrawn or removed (src/ui/focusKeeper.ts). */
  function render(): void {
    const mark = markFocus(document, root);
    drawPage();
    restoreFocus(document, root, mark);
  }

  function drawPage(): void {
    // Design D: a later step shown before the flow's chunk is loaded (a reload, then the browser's Forward): load it (with
    // the check engine, which those steps need too), then draw; the page stays as it is meanwhile.
    if (__FLOW_D__ && !state.fd && !pageFromHash(location.hash) && stage() !== 'goal') {
      void loadEngine().then(() => render());
      return;
    }
    askedOnPage = new Map();
    const { lang } = state;
    const tr = (key: string, params?: Record<string, string | number>) => t(lang, key, params);
    document.documentElement.lang = lang;
    document.documentElement.dir = LANGUAGES[lang].dir;
    document.title = tr('app.name');
    const page = pageFromHash(location.hash);

    // Beta build indicator (src/build/indicator.ts): which build this is, at a glance. Remove at public launch.
    const animal = buildAnimal(BUILD_ID);

    // The first stop for the keyboard and screen readers: past the header's controls to <main> (WCAG 2.4.1); inside the header so it sits in a landmark. A button-like
    // link that moves the focus itself: an href to "#main" would change the hash the app routes by.
    const skipLink = el('a', { class: 'skip-link', href: '#/' }, tr('a11y.skip'));
    skipLink.addEventListener('click', (e) => {
      e.preventDefault();
      const main = document.querySelector<HTMLElement>('main');
      if (!main) return;
      main.tabIndex = -1;
      main.focus();
    });

    const header = el(
      'header',
      { class: 'header' },
      skipLink,
      el('div', { class: 'build-stripe', style: `background:${animal.background};color:${animal.text}`, title: BUILD_ID },
        el('span', { class: 'visually-hidden' }, `${tr('build.stripe')}: `),
        el('span', { 'aria-hidden': 'true' }, animal.emoji), ' ', tr(`build.animal.${animal.id}`), buildHash(BUILD_ID) ? ` · ${buildHash(BUILD_ID)}` : ''),
      el(
        'div',
        { class: 'header-row' },
        el('div', { class: 'brand' },
          // The logo and the name: home, the start screen (a new check), on every screen; asks first when something would be lost.
          el('h1', {}, homeLink(), ' ', el('span', { class: 'beta' }, tr('app.beta')))),
        // Brand C: the theme control beside the language picker (src/ui/theme.ts).
        __BRAND__ === 'c' ? el('div', { class: 'header-tools' }, themeMenu(el, tr), languageMenu(lang)) : languageMenu(lang),
      ),
      // Design D (__FLOW_D__): no tagline (the start screen says what the app does) and no privacy line at the top of every
      // screen: the full approved line is on the start screen, the short one in each flow step's bottom bar (Matt, 2026-10-22
      // 16:36 item 7); after a pick, "‹ Payslip check" back to the start and the four-step bar.
      ...(__FLOW_D__ ? [page || stage() === 'goal' ? '' : el('div', { class: 'rv fd' }, fd().backLink(el, tr(backKeyD()), () => void guardLoss('logo', state.lang, LANGUAGES[state.lang].dir, everythingLoss(), startOver))),
        // The translate flow has two screens (add, translation): no step bar.
        page || stage() === 'goal' || translating() ? '' : stepBarD()] : [
      // The intro (what the app does) belongs on the start screen only; after a pick: a Back link (Matt, 2026-10-19, UI fix batch item 4).
      page || stage() === 'goal' ? el('p', { class: 'tagline' }, tr(firstKey(lang, 'app.tagline'))) : backToStart(),
      // Right under the tagline (Matt, 2026-10-18; docs/claims-register.md): what leaves the device. The tally half only
      // in a build that has the counter, with the link to exactly what it sends.
      // On the brand C start screen the hero's strip already says the short form: on phones the header line, which
      // shows that same short form there, is hidden so it isn't said twice (reviewer, 2026-10-22).
      el('p', { class: `privacy${__BRAND__ === 'c' && !page && stage() === 'goal' ? ' beside-strip' : ''}` }, ...privacyLine(tr)),
      // Not on the start screen: "1 Start" there says nothing (ux-expert, start.md change 1).
      page || stage() === 'goal' ? '' : renderSteps()]),
      // The browser offers installing (Android Chrome, Samsung Internet): one tap to its own prompt. At the top of the
      // home screen only; every other screen and page has it in the footer (Matt, G23, 2026-10-26).
      installOffer() && !page && stage() === 'goal' ? el('div', { class: 'install-row' }, installButton('install-header')) : '',
    );

    const analysed = state.result && 'payslips' in state.result ? state.result.payslips : [];
    // Before the engine is loaded (the start screen) nothing is analysed: only the data's age can raise the banner.
    const banner = !engine ? (DATA_VALIDITY.entries.some((e) => today() > e.value.validUntil) ? 'stale' : undefined) : engine.dataBanner(today(), analysed.map((p) => ({ ...(p.fields.payMonth ? { payMonth: p.fields.payMonth } : {}), findings: p.findings })), eng().defaultParams);
    // The text names the latest payslip whose printed minimum wage differs from ours.
    const mismatchMonth = analysed.filter((p) => p.fields.payMonth && p.findings.some((f) => f.ruleId === 'minimumWageData' && f.status === 'verify')).map((p) => p.fields.payMonth ?? '').sort().at(-1);
    const dataNotice = banner ? el('div', { class: 'update', role: 'status' }, banner === 'stale' ? tr('app.dataStale') : tr('app.dataMismatch', { month: mismatchMonth ? namedMonth(mismatchMonth) : '' })) : '';
    const notice = state.updateAvailable
      ? el('div', { class: 'update', role: 'status' }, button(tr('app.updateAvailable'), reloadGuarded))
      : '';

    const textarea = el('textarea', { id: 'payslip-json', rows: '14', spellcheck: 'false', dir: 'ltr', 'aria-describedby': 'json-help' });
    textarea.value = state.json;
    textarea.addEventListener('input', () => {
      const hadBasis = currentFields()?.payBasis !== undefined;
      state.json = textarea.value;
      state.source = 'json';
      state.exampleLoaded = false;
      // Re-render only when the pay-basis question should appear or disappear, so typing isn't interrupted.
      if ((currentFields()?.payBasis !== undefined) !== hadBasis) renderKeepingFocus();
    });

    // Digital PDF upload (docs/phase2-plan.md 4.1a): picker, or drop files anywhere on this card.
    // The native input's own text ("Choose Files / No file chosen") is in the browser's language, not the
    // app's: it stays hidden and a real button opens it; the status line says what was chosen, in i18n.
    const picker = el('input', { type: 'file', id: 'pdf-files', class: 'file-input', accept: 'application/pdf,.pdf,image/*,.jpg,.jpeg,.png,.heic,.heif,.webp,application/octet-stream', multiple: '', tabindex: '-1', 'aria-hidden': 'true' });
    picker.addEventListener('change', () => {
      const files = filesOf(picker.files);
      // Their bytes first, before anything else awaits (src/upload/fileBytes.ts).
      files.forEach(startBytes);
      void readFiles(files);
    });
    // The one primary action until files are chosen or the manual form is open (its "Check these numbers" is then the
    // step's action); then "Add more files" steps back (secondary). Screen review 2026-10-18: one primary per step.
    const hasFiles = state.files.length > 0 || state.pending.length > 0;
    const form106 = state.flow.goal === 'form106';
    const choose = button(tr(hasFiles ? 'upload.chooseMore' : form106 ? 'upload.choose.form106' : 'upload.choose'), () => picker.click(), hasFiles || state.manualOpen ? 'secondary' : 'primary');
    choose.id = 'pdf-choose';
    choose.setAttribute('aria-describedby', 'upload-help');
    // Phones: the camera straight away (the back camera; the photo goes to OCR like any other image).
    const camera = el('input', { type: 'file', id: 'camera-photo', class: 'file-input', accept: 'image/*', capture: 'environment', tabindex: '-1', 'aria-hidden': 'true' });
    camera.addEventListener('change', () => {
      const files = filesOf(camera.files);
      files.forEach(startBytes);
      void readFiles(files);
    });
    const phone = isPhone();
    const takePhoto = phone ? Object.assign(button(tr('upload.takePhoto'), () => camera.click(), 'secondary'), { id: 'take-photo' }) : '';
    /**
     * Design D's upload step (Matt approved the mockups 2026-10-22): the empty step (a drop area with a slow moving dotted
     * border until files are added, one tip, what we check), reading (one summary: n of N, the overall bar, a time left
     * only from measured reading times; one row per file; Add more files and Stop), and read (a green summary with the
     * count and months, a row per file, the unknown-format offer folded and optional, Continue in the bottom bar).
     * Every other state of the step (a password, a scan to offer, a file that couldn't be read, the forms typed in)
     * keeps its layout, with the bottom bar's privacy line. Undefined: not one of the three.
     */
    /**
     * "Which one should we use?" for each document read from more than one file (a PDF and a photo of it; Matt,
     * 2026-10-23 19:29, item E; src/upload/duplicates.ts): one question each, the files as its options. Asked on the upload
     * step as soon as both are read; Continue says what is missing until each is answered (the missing-input pattern).
     */
    const duplicateQuestions = (): { nodes: HTMLElement[]; needed: FlowNeeded[] } => {
      const merged = withOcr(state.uploads ?? []);
      const groups = dupGroups(merged);
      // Two questions with the same heading are told apart by what differs: the employer, else the files (Matt, 2026-10-26).
      const baseHeading = (g: Engine.DuplicateGroup) => (g.kind === 'form106' ? tr('upload.dup.form106', { year: g.period }) : tr('upload.dup.payslip', { month: namedMonth(g.period) }));
      const employerOf = (g: Engine.DuplicateGroup) => merged[g.members[0]?.file ?? -1]?.form106?.fields.employerName?.trim() ?? '';
      const filesOf = (g: Engine.DuplicateGroup) => [...new Set(g.members.map((m) => merged[m.file]?.name ?? ''))].join(', ');
      const heading = (g: Engine.DuplicateGroup): string => {
        const same = groups.filter((o) => baseHeading(o) === baseHeading(g));
        if (same.length < 2) return baseHeading(g);
        const employers = same.map(employerOf);
        const detail = employers.every(Boolean) && new Set(employers).size === employers.length ? employerOf(g) : filesOf(g);
        return tr('upload.dup.qualified', { question: baseHeading(g), detail: `\u2068${detail}\u2069` });
      };
      const nodes = groups.map((g, n) => {
        const kept = keptOf(g);
        const oneFileTwice = new Set(g.members.map((m) => m.file)).size < g.members.length;
        const label = (m: Engine.DocRef): string => {
          const name = merged[m.file]?.name ?? '';
          return m.page !== undefined && (oneFileTwice || (merged[m.file]?.payslips.length ?? 0) > 1) ? tr('upload.dup.page', { name, page: m.page }) : name;
        };
        return el('div', { id: `fd-dup-${n}`, class: 'fd-dup' }, choiceQuestion(el, {
          name: `dup-${n}`, idPrefix: `dup-${n}-`, type: 'radio', level: 3,
          question: heading(g),
          help: tr('upload.dup.help'),
          // A copy read from a scan or photo says so (ux-expert 2026-10-23: a photo may read fewer lines than the PDF).
          options: g.members.map((m, k) => {
            const u = merged[m.file];
            const scanned = Boolean(u?.ocr || u?.form106?.scan || u?.payslips.find((p) => p.page === m.page)?.ocr);
            return { value: String(k), label: label(m), ...(scanned ? { help: tr('upload.status.scanRead') } : {}) };
          }),
          selected: kept ? String(g.members.findIndex((m) => eng().sameRef(m, kept))) : undefined,
          onChange: (v) => {
            const m = typeof v === 'string' ? g.members[Number(v)] : undefined;
            if (!m) return;
            const before = formsCounted();
            state.dupKeep.set(dupKey(g), refKey(m));
            keepForm106Fixes(before);
            if (state.uploads) analyseUploads(state.uploads);
            render();
          },
        }));
      });
      // Each named by its month or year (two sets never read "which copy to use, which copy to use").
      const needed = groups.flatMap((g, n): FlowNeeded[] => (keptOf(g) ? [] : [{
        id: `fd-dup-${n}`, name: `upload.dup.needed.${g.kind}`, params: g.kind === 'form106' ? { year: g.period } : { month: namedMonth(g.period) }, message: 'upload.dup.neededMessage',
      }]));
      return { nodes, needed };
    };
    /** A file added twice (the same name and date, or the same bytes under any name): one line each, said at once. */
    const addedTwiceNotes = (): HTMLElement[] => [...state.skipped, ...state.twice].map((n) => {
      // The name inside the sentence, isolated for right-to-left text (a marker split, as the questions step's missing line).
      const [before = '', after = ''] = tr('upload.addedTwice', { name: '\u0000' }).split('\u0000');
      return el('p', { class: 'fd-small', role: 'status' }, before, el('bdi', {}, n), after);
    });
    const renderUploadD = (readNow: boolean): HTMLElement | undefined => {
      const ocrJobs = [...state.ocr.values()];
      const readingNow = state.pending.length > 0 || ocrJobs.some((j) => j.status === 'running' || j.status === 'queued');
      /** The Form 106's "enter the numbers yourself" link centred like the payslip one (its typed-in form untouched). */
      const linkRow = (parts: HTMLElement[]): HTMLElement[] => parts.map((x) => (x.tagName === 'P' && x.querySelector('.form106-manual-link') ? Object.assign(x, { className: 'fd-link-row' }) : x));
      // The ready list kept short (src/ui/flowD/steps.ts shortFileList, with the flow's chunk).
      const shortList = (parts: (HTMLElement | string)[]) => fd().shortFileList(el, tr, parts, foldState('fd-files-more'));
      const foldState = (id: string): FlowSteps.FoldState => ({ open: state.moreOpen.has(id), onToggle: (open) => (open ? state.moreOpen.add(id) : state.moreOpen.delete(id)) });
      /**
       * The offer to send a format we don't read, folded and optional (at most 40 words above the fold). `ownWords`: the
       * section keeps its heading and text (the read-error screen, where nothing was read: the fold's title is its heading).
       */
      const unknownFoldD = (ownWords = false): HTMLElement | '' => {
        const unknown = renderUnknownFormat();
        const as106 = unknownFormatIndices().every(unknownAs106);
        return unknown.length ? fd().optionalFold(el, tr, tr(as106 ? 'unknownFormat.heading.form106' : ownWords ? 'unknownFormat.heading' : 'readyD.unknownTitle'), as106 || ownWords ? unknown : [el('p', { class: 'fd-small' }, tr('readyD.unknownText')), ...unknown.map((u) => {
          // The fold says it in its own words: the section's own heading and text would repeat it.
          u.querySelector('#unknown-format-heading')?.remove();
          u.querySelector('#unknown-format-text')?.remove();
          const send = u.querySelector('button[aria-describedby="unknown-format-text"]');
          if (send) {
            send.textContent = tr('readyD.unknownSend');
            send.removeAttribute('aria-describedby');
          }
          return u;
        })], state.moreOpen.has('unknownFold'), (open) => (open ? state.moreOpen.add('unknownFold') : state.moreOpen.delete('unknownFold'))) : '';
      };
      const bar = (action?: Parameters<typeof FlowSteps.bottomBar>[0]['action']) => fd().bottomBar({ el, tr, privacyHref: '#/privacy', ...(action ? { action, sticky: true } : {}) });
      // The whole step takes the drop; only the dashed drop box shows it (`visual`), so no second outline crosses the text (Matt 02:50, 2026-10-24).
      const dropHandlers = (target: HTMLElement, visual: HTMLElement = target) => {
        target.addEventListener('dragover', (e) => {
          e.preventDefault();
          if (!state.dragging) {
            state.dragging = true;
            visual.classList.add('dragging');
          }
        });
        target.addEventListener('dragleave', (e) => {
          // Moving onto a child of the step is not leaving it.
          if (e.relatedTarget instanceof Node && target.contains(e.relatedTarget)) return;
          state.dragging = false;
          visual.classList.remove('dragging');
        });
        target.addEventListener('drop', (e) => {
          e.preventDefault();
          state.dragging = false;
          const files = filesOf(e.dataTransfer?.files);
          files.forEach(startBytes);
          void readFiles(files);
        });
      };
      // A Form 106 typed in counts as something added: its row, Change, Remove and Continue (below), never this empty step.
      if (!hasFiles && !state.manualOpen && !state.form106Open && !state.form106Manual) {
        choose.textContent = tr(form106 ? 'upload.choose.form106' : 'uploadD.choose');
        choose.className = 'primary';
        choose.setAttribute('aria-describedby', 'fd-drop-lines');
        if (takePhoto) {
          takePhoto.replaceChildren(fd().flowIcon('camera'), tr('upload.takePhoto'));
        }
        const manual = (() => {
          if (form106) return linkRow(renderForm106Entry());
          // A translation has nothing to type in by hand (the numbers it would translate are the user's own).
          if (translating()) return [];
          const b = button(tr('uploadD.byHand'), openManual, 'secondary');
          b.className = 'link-button manual-link';
          return [el('p', { class: 'fd-link-row' }, b)];
        })();
        const { section, drop } = fd().uploadEmpty({
          el, tr, dragging: state.dragging, ...(canDragDrop() ? { dragHint: tr('uploadD.drag') } : {}),
          heading: tr(form106 ? 'uploadD.heading.form106' : translating() ? 'uploadT.heading' : 'uploadD.heading'),
          // One short line visible; the second line and the tip in a closed fold (at most 40 words above the fold).
          line: tr(form106 ? 'flow.upload.form106' : translating() ? 'uploadT.line' : 'uploadD.lines1'),
          choose, photo: takePhoto, inputs: [picker, ...(phone ? [camera] : [])],
          folds: foldState,
          ...(form106 ? {} : { more: { label: tr('uploadD.more'), lines: translating() ? [] : [tr('uploadD.lines2')], tip: { lead: tr('uploadD.tipLead'), rest: tr('uploadD.tip') } }, ...(translating() ? {} : { checks: { label: tr('uploadD.checks'), items: CHECK_TOPICS_D.filter((k) => k !== 'pension' || !state.flow.goal || possibleChecks(state.flow.goal, situation(), earlyOptions()).possible.includes('pension')).map((k) => tr(`uploadD.check.${k}`)) } }) }),
          after: [
            // The Form 106's other income can only be asked once the forms are read (the tax years): said here what may not be checkable.
            // Audit 2026-10-24 item 9: one line to open, the full sentence inside (the 40-word rule).
            form106 ? fd().moreFold(el, tr('flow.early.uploadNoteFold'), [el('p', { id: 'early-upload-note', class: 'fd-small' }, tr('flow.early.uploadNote'))], 'early-upload-fold') : '',
            ...manual,
          ],
        });
        drop.querySelector('p:not(.fd-drag-hint)')?.setAttribute('id', 'fd-drop-lines');
        dropHandlers(section, drop);
        return el('div', { class: 'fd fd-upload' }, section, bar());
      }
      if (readingNow) {
        const list = renderFileList('plain', 'reading');
        const rows = [...(list?.list[0] as HTMLElement | undefined)?.querySelectorAll('li') ?? []];
        const total = rows.length;
        const doneCount = rows.filter((r) => !r.classList.contains('reading') && !r.classList.contains('waiting')).length;
        const running = ocrJobs.find((j) => j.status === 'running');
        const fraction = total ? (doneCount + (running?.progress?.stage === 'recognize' ? running.progress.progress ?? 0 : 0)) / total : 0;
        const left = timeLeftMs(readTimes, ocrJobs.filter((j) => j.status === 'queued').length, running?.readStart !== undefined ? performance.now() - running.readStart : undefined);
        const scans = ocrJobs.some((j) => (j.status === 'running' || j.status === 'queued') && j.kind !== 'labels');
        const stop = button(tr('readingD.stop'), () => {
          // Stops the scans and photos being read or waiting (each can be read again from its card); files still being
          // opened are read to the end (seconds, and nothing already read is thrown away).
          stopOcr();
          render();
          document.getElementById('upload-heading')?.focus();
        }, 'secondary');
        stop.id = 'fd-stop';
        const more = button(tr('upload.chooseMore'), () => picker.click(), 'secondary');
        more.id = 'pdf-choose';
        const section = el('section', { class: 'rv fd fd-screen upload' },
          fd().readingSummary({
            el, tr, heading: tr(form106 ? 'readingD.heading.form106' : 'readingD.heading'), done: doneCount, total, fraction,
            ...(left !== undefined ? { timeLeft: minutesToSay(left) ? tr('readingD.timeLeft', { count: minutesToSay(left) }) : tr('readingD.underMinute') } : {}),
            ...(scans ? { note: { line: tr('readingD.keepOpen'), whyLabel: tr('uploadD.why'), why: tr('readingD.why'), fold: foldState('fd-reading-why') } } : {}),
          }),
          // The file being read; the others under "Show N more files" (less-text mockups).
          el('div', { class: 'fd-card fd-summary' }, ...fd().shortFileList(el, tr, list?.list ?? [], foldState('fd-files-more'), true)),
          el('div', { class: 'fd-actions' }, picker, ...(phone ? [camera] : []), more, ocrJobs.some((j) => j.status === 'running' || j.status === 'queued') ? stop : ''),
          // Files already read that wait for their numbers to be checked (a scan's confirmation screen), under the reading.
          ...renderOcr().filter((card) => !/^ocr-card-(all|\d+)$/.test(card.id) || ['review', 'failed'].includes(state.ocr.get(Number(card.id.slice(9)))?.status ?? '')));
        return el('div', { class: 'fd fd-upload' }, section, bar());
      }
      // Scans or photos added and not read yet (or reading stopped): the step keeps its heading; the question in the
      // summary card, one row per file, why and the download size in a closed fold, "Read all files" the bar's primary.
      const toRead = [...state.ocr].filter(([, j]) => j.status === 'offer' || j.status === 'cancelled').map(([i]) => i);
      if (!readNow && toRead.length && toRead.length === ocrJobs.length && !state.manualOpen && !state.form106Open) {
        const many = toRead.length > 1;
        const list = renderFileList('message', 'ready');
        const enter = button(tr('upload.enterManually'), enterByHand, 'secondary');
        enter.className = 'link-button manual-link';
        const section = el('section', { class: 'rv fd fd-screen upload', 'aria-labelledby': 'upload-heading' },
          el('h2', { id: 'upload-heading', tabindex: '-1', class: 'fd-title' }, tr(form106 ? 'uploadD.heading.form106' : translating() ? 'uploadT.heading' : 'uploadD.heading')),
          el('div', { class: 'fd-card fd-summary' },
            el('div', { class: 'fd-summary-top' },
              el('h3', { id: many ? 'ocr-all' : `ocr-job-${toRead[0]}`, tabindex: '-1', class: 'fd-summary-big', style: 'color:inherit' }, many ? tr('ocr.questionAll', { count: toRead.length }) : tr('ocr.question'))),
            ...(list?.list ?? [])),
          !many && ocrJobs.some((j) => j.status === 'cancelled') ? el('p', { class: 'fd-small', role: 'status' }, tr('ocr.cancelled')) : '',
          fd().moreFold(el, tr('uploadD.offerMore'), [
            el('p', {}, tr(many ? 'ocr.offerAll' : form106 ? 'ocr.offer.form106' : 'ocr.offer')),
            el('p', {}, tr(form106 ? 'ocr.download.form106' : 'ocr.download', { mb: (__OCR_DOWNLOAD_BYTES__ / 1e6).toFixed(1) })),
          ], 'fd-offer-more', foldState('fd-offer-more')),
          el('div', {}, picker, ...(phone ? [camera] : []), fd().wideButton(el, 'plus', tr('upload.chooseMore'), () => picker.click(), 'pdf-choose')),
          el('p', { class: 'fd-link-row' }, enter));
        return el('div', { class: 'fd fd-upload' }, section, bar({ label: tr(many ? 'ocr.readAll' : 'ocr.read'), id: many ? 'ocr-read-all' : `ocr-read-${toRead[0]}`, onClick: () => void runOcr(toRead) }));
      }
      // Screen 8, every file added couldn't be read (Matt approved 2026-10-22 18:00): the step's heading, a card per file
      // (the reason's first sentence, the rest folded), the PDF tip folded, "Enter the numbers yourself"; no file list
      // (each card names its file and has Remove): at most 40 words above the fold.
      const uploadsNow = state.uploads ?? [];
      if (!readNow && uploadsNow.length > 0 && state.pending.length === 0 && uploadsNow.every((_, i) => state.ocr.get(i)?.status === 'failed') && !state.manualOpen && !state.form106Open) {
        const section = el('section', { class: 'rv fd fd-screen upload', 'aria-labelledby': 'upload-heading' },
          el('h2', { id: 'upload-heading', tabindex: '-1', class: 'fd-title' }, tr(form106 ? 'uploadD.heading.form106' : translating() ? 'uploadT.heading' : 'uploadD.heading')),
          ...renderOcr(),
          el('div', {}, picker, ...(phone ? [camera] : [])));
        return el('div', { class: 'fd fd-upload' }, section, bar());
      }
      // Screen 8 for files that aren't scans: every file added wasn't read (a PDF in a format we don't read, not a PDF,
      // damaged…) and nothing else is in: a card per file (its reason, "Try another file", "Remove file"), "Enter the numbers
      // yourself" once, then the offer to send the format (never the earlier "Couldn't read" screen; 2026-10-23).
      const unreadRows = !readNow && uploadsNow.length > 0 && state.pending.length === 0 && state.ocr.size === 0 ? fileRows(uploadsNow, () => undefined, []) : [];
      if (unreadRows.length && unreadRows.every((r) => r.status === 'notRecognised') && !state.manualOpen && !state.form106Open && state.fdErrors) {
        const errors = state.fdErrors;
        const dir = LANGUAGES[state.lang].dir;
        const reasons: string[] = [];
        const cards = unreadRows.map((r, n) => {
          const index = r.index ?? -1;
          const reason = tr(r.problem === 'unknownLayout' ? unknownReasonKey(r.index) : `upload.reason.${r.problem}`);
          // The same reason as a card above: said once.
          const repeat = reasons.includes(reason);
          reasons.push(reason);
          // A file that wasn't read gave nothing: removed without asking (guardLoss runs it at once, inside the tap).
          return errors.readErrorCard({
            el, tr, kind: 'notRead', reason, name: r.name, id: `fd-error-${index}`, primary: n === 0, repeat,
            onPrimary: () => void guardLoss('removeFile', state.lang, dir, {}, () => {
              // Another file in its place: the step's heading first (a cancelled picker leaves the focus there), then the picker.
              removeFile(index);
              document.getElementById('upload-heading')?.focus();
              document.getElementById('pdf-files')?.click();
            }, 'remove'),
            onRemove: () => void guardLoss('removeFile', state.lang, dir, {}, () => {
              removeFile(index);
              document.getElementById('upload-heading')?.focus();
            }, 'remove'),
          });
        });
        const section = el('section', { class: 'rv fd fd-screen upload', 'aria-labelledby': 'upload-heading' },
          el('h2', { id: 'upload-heading', tabindex: '-1', class: 'fd-title' }, tr(form106 ? 'uploadD.heading.form106' : translating() ? 'uploadT.heading' : 'uploadD.heading')),
          // Nothing to continue with: typing the numbers in, then the offer to send the format (folded, optional).
          el('div', { class: 'rv fd fd-errors' }, ...cards, errors.byHandLink(el, tr, enterByHand), __ERROR_REPORTS__ ? errorReportOffer(tr) : ''),
          unknownFoldD(true),
          ...addedTwiceNotes(),
          el('div', {}, picker, ...(phone ? [camera] : [])));
        return el('div', { class: 'fd fd-upload' }, section, bar());
      }
      if (readNow) {
        const list = renderFileList('message', 'ready');
        // A file that couldn't be read has its own card under the summary (reason and way on): not a row as well.
        for (const [i, j] of state.ocr) if (j.status === 'failed') for (const part of list?.list ?? []) if (typeof part !== 'string') part.querySelector(`#fd-file-${i}`)?.remove();
        // A Form 106 typed in: a row like a file read (its tax year, "Form 106 · numbers you entered yourself"), with
        // Change and ✕; in the list's count too. Never an empty step with no Continue (dead ends).
        const typed = typedForm106Row();
        const listParts: (HTMLElement | '')[] = list?.list ?? [];
        if (typed) {
          let ul = listParts.find((x): x is HTMLElement => x !== '' && x.tagName === 'UL');
          if (!ul) listParts.push(ul = fd().fileRows(el, [], ''));
          ul.append(typed);
          ul.setAttribute('aria-label', tr('upload.listHeading', { count: ul.children.length }));
        }
        const uploads = state.uploads ?? [];
        const entries = entriesOf(uploads);
        const forms = uploads.filter((u, i) => u.form106 && !formNotCounted(i));
        const formsCount = forms.length + (state.form106Manual ? 1 : 0);
        const months = [...new Set(entries.map((e) => e.fields.payMonth).filter((m): m is NonNullable<typeof m> => m !== undefined))].sort();
        const allYears = [...new Set([...forms.flatMap((u) => (u.form106?.fields.taxYear ? [u.form106.fields.taxYear] : [])), ...(state.form106Manual?.taxYear ? [state.form106Manual.taxYear] : [])])].sort();
        // A year before the refund window is said once, and left out of the years checked (audit 2026-10-24 item 5).
        const oldYears = allYears.length > 1 ? allYears.filter((y) => y < deadlineYears(new Date()).taxYear) : [];
        const years = allYears.filter((y) => !oldYears.includes(y));
        const figure = [
          entries.length ? tr('readyD.payslips', { count: entries.length }) : '',
          formsCount ? tr('readyD.forms106', { count: formsCount }) : '',
        ].filter(Boolean).join(' · ');
        const period = [
          months.length === 1 ? namedMonth(months[0] ?? '') : months.length > 1 ? tr('readyD.monthRange', {
            // "April to June 2026" when both are in one year, else each with its year.
            first: (months[0] ?? '').slice(0, 4) === (months.at(-1) ?? '').slice(0, 4) ? monthName(Number((months[0] ?? '').slice(5, 7))) : namedMonth(months[0] ?? ''),
            last: namedMonth(months.at(-1) ?? ''),
          }) : '',
          years.length ? tr('readyD.taxYears', { years: years.join(', '), count: years.length }) : '',
          oldYears.length ? tr('readyD.taxYearsTooOld', { years: oldYears.join(', '), count: oldYears.length }) : '',
        ].filter(Boolean).join(' · ');
        const dups = duplicateQuestions();
        const section = el('section', { class: 'rv fd fd-screen upload' },
          el('div', { class: 'fd-card fd-summary' },
            fd().readySummary({ el, label: dups.needed.length ? tr('readyD.labelQuestion', { count: dups.needed.length }) : tr(translating() ? 'readyT.label' : 'readyD.label'), figure, period }),
            ...shortList(listParts)),
          // What these documents take away (a Form 106 set all from before the refund window: a stop): said here, where we learn it.
          uploadNotice(),
          // The same document in two files: which one to use, asked here, where we learn it.
          ...dups.nodes,
          // A file that couldn't be read beside the ones that were: its card right under the summary (whose h2 heads the
          // step), with the reason and the way on (never only a row saying it failed; screen 8 and the too-big photo refusal).
          ...renderOcr().filter((card) => !/^ocr-card-(all|\d+)$/.test(card.id) || state.ocr.get(Number(card.id.slice(9)))?.status === 'failed' || noTextPagesAt(Number(card.id.slice(9))).length > 0),
          ...addedTwiceNotes(),
          // A file we couldn't read: typing its numbers in is the way forward (visible, never only in a fold).
          list?.enter ? el('p', { class: 'fd-link-row' }, Object.assign(list.enter, { className: 'link-button manual-link', id: 'fd-enter-yourself' })) : '',
          el('div', {}, picker, ...(phone ? [camera] : []), Object.assign(fd().wideButton(el, 'plus', tr(form106 ? 'upload.chooseMore' : 'readyD.addMore'), () => picker.click(), 'pdf-choose'), {})),
          // Form 106 read without a December payslip: adding it is an action (upload.md, form106-read).
          ...(form106 && hasForm106() && !payslipsRead() ? [el('p', { class: 'fd-small' }, tr('flow.upload.form106December')), el('div', {}, button(tr('flow.upload.form106DecemberButton'), () => picker.click(), 'secondary'))] : []),
          unknownFoldD(),
          // The typed-in numbers' "Change" is on their row; the form itself (when open) or "Or enter the numbers yourself" here.
          ...(typed && !state.form106Open ? [] : linkRow(renderForm106Entry())));
        // "Continue": the step bar names the next step (less-text mockups, MATT GO 2026-10-23 13:28).
        if (dupNeededShown && dups.needed.length) queueMicrotask(() => fd().showNeeded(el, tr, dups.needed, false));
        if (!dups.needed.length) dupNeededShown = false;
        // Every Form 106 from before the refund window: a stop, no Continue (form106StopNotice).
        if (allFormsTooOld() !== undefined) return el('div', { class: 'fd fd-upload' }, section, bar());
        return el('div', { class: 'fd fd-upload' }, section, fd().bottomBar({
          el, tr, privacyHref: '#/privacy', sticky: true, needed: dups.needed, showMissing: dupNeededShown,
          action: { label: tr('flow.continue'), id: 'upload-continue', onClick: () => goTo(afterUpload()) },
          onNeeded: () => {
            dupNeededShown = true;
            render();
            fd().showNeeded(el, tr, dups.needed, true);
          },
        }));
      }
      return undefined;
    };
    const renderUpload = (): HTMLElement => {
    // Which of the step's states this is (docs/review/ux-expert/upload.md): the message first and the one primary action
    // right under it when files were read, couldn't be read, or are a scan to offer reading for; else the older order.
    const uploads = state.uploads ?? [];
    const readNow = (payslipsRead() || hasForm106()) && !uploadNeedsUser() && state.source !== 'json';
    if (__FLOW_D__) {
      const d = renderUploadD(readNow);
      if (d) return d;
    }
    // Scans to read, or being read (one at a time, others waiting): their cards are the message, at the top.
    const offering = !readNow && [...state.ocr.values()].some((j) => j.status === 'offer' || j.status === 'cancelled' || j.status === 'running' || j.status === 'queued');
    const cannotRead = !readNow && !offering && state.pending.length === 0 && state.ocr.size === 0 && uploads.length > 0
      && fileRows(uploads, () => undefined, []).every((r) => r.status === 'notRecognised');
    const mode = readNow ? 'message' : cannotRead ? 'cannotRead' : offering ? 'message' : 'plain';
    const listParts = renderFileList(mode);
    const cannotRows = cannotRead ? fileRows(uploads, () => undefined, []) : [];
    const reasonKey = cannotRows.length === 1 ? (cannotRows[0]?.problem === 'unknownLayout' ? unknownReasonKey(cannotRows[0]?.index) : `upload.reason.${cannotRows[0]?.problem}`) : '';
    if (hasFiles) choose.removeAttribute('aria-describedby');
    const messageFirst = readNow || cannotRead || offering;
    const useChoose = (label: string, variant: 'primary' | 'secondary'): HTMLElement => {
      choose.textContent = label;
      choose.className = variant;
      return choose;
    };
    const continueRow = readNow ? el('div', { class: 'row upload-actions' }, Object.assign(button(tr('flow.continue'), () => goTo(afterUpload())), { id: 'upload-continue' })) : '';
    const entryForm = renderForm106Entry();
    // Form 106 read without a December payslip: adding it is an action now, not a paragraph (upload.md, form106-read).
    const decemberOffer = readNow && form106 && hasForm106() && !payslipsRead()
      ? [el('p', { class: 'help small' }, tr('flow.upload.form106December')), el('div', { class: 'row upload-actions' }, button(tr('flow.upload.form106DecemberButton'), () => picker.click(), 'secondary'))] : [];
    const chooseRow = (label: string): HTMLElement => el('div', { class: 'row file-row upload-actions' }, picker, useChoose(label, 'secondary'), ...(phone ? [camera, takePhoto] : []), ...(readNow && listParts?.enter ? [listParts.enter] : []), ...(readNow && listParts?.clear ? [listParts.clear] : []));
    // The reading cards, made once; a file that wasn't read is the step's message, so its card comes first (design D's
    // screen-8 cards, the too-big photo refusal, every other "couldn't read" card): on the first phone screen.
    const ocrCards = renderOcr();
    const failedCard = (c: HTMLElement) => c.classList.contains('fd-errors') || (/^ocr-card-\d+$/.test(c.id) && state.ocr.get(Number(c.id.slice(9)))?.status === 'failed');
    const errorsFirst = ocrCards.filter(failedCard);
    const otherOcr = ocrCards.filter((c) => !errorsFirst.includes(c));
    const upload = el(
      'section',
      { class: `card upload${state.dragging ? ' dragging' : ''}${messageFirst ? ' message-first' : ''}` },
      el('h2', { id: 'upload-heading', tabindex: '-1' }, tr(form106 ? 'upload.heading.form106' : 'upload.heading')),
      ...errorsFirst,
      // Choosing instructions are stale once there are files.
      hasFiles ? '' : el('p', { id: 'upload-help', class: `help${form106 ? ' small' : ''}` }, tr(state.flow.goal === 'several' ? 'flow.upload.several' : state.flow.goal === 'left' ? 'flow.upload.left' : state.flow.goal === 'form106' ? 'flow.upload.form106' : 'upload.help')),
      // The message and its action first.
      ...(messageFirst ? [
        // Offered scans: their card is the message (its question is the heading).
        ...(offering ? otherOcr : []),
        offering ? '' : listParts?.result ?? '',
        reasonKey ? el('p', { class: 'upload-reason' }, tr(reasonKey)) : '',
        continueRow,
        cannotRead ? el('div', { class: 'row upload-actions' }, listParts?.enter ?? '') : '',
        cannotRead ? el('div', { class: 'row file-row upload-actions' }, picker, useChoose(tr('upload.chooseOther'), 'secondary'), ...(phone ? [camera, takePhoto] : [])) : '',
      ] : [
        el('div', { class: 'row file-row upload-actions' }, picker, choose, ...(phone ? [camera, takePhoto] : [])),
        // Before anything is chosen: the scanner and photo tips for this device (desktops: the payroll portal).
        hasFiles ? '' : photoTipsList(),
        el('p', { class: 'help drop-hint' }, tr('upload.drop')),
        // The Form 106's other income can only be asked once the forms are read (the tax years): said here what may not be checkable.
        form106 && !hasFiles ? el('p', { id: 'early-upload-note' }, tr('flow.early.uploadNote')) : '',
      ]),
      state.skipped.length || state.twice.length ? el('p', { class: 'note', role: 'status' }, tr('upload.skipped'), ' ', ...[...state.skipped, ...state.twice].flatMap((n, i) => [i ? ', ' : '', el('bdi', {}, n)])) : '',
      ...(listParts?.list ?? []),
      // The plain layout keeps its buttons under the list.
      !messageFirst ? el('div', { class: 'row upload-actions' }, listParts?.enter ?? '', listParts?.clear ?? '') : '',
      // Read: adding more files is secondary and sits after the rows; so do the optional extras.
      ...(readNow ? [chooseRow(tr('upload.chooseMore')), ...decemberOffer] : []),
      offering ? el('div', { class: 'row file-row upload-actions' }, picker, useChoose(tr('upload.chooseMore'), 'secondary')) : '',
      ...renderUnknownFormat(),
      ...(offering ? [] : otherOcr),
      // A Form 106's typed-in form, or its small link (not under a "couldn't read" message: that one's primary opens it).
      ...(cannotRead && !state.form106Open ? [] : entryForm),
      // Manual entry is hidden until asked for (or a file can't be read, or a scan offer, a cancelled or failed reading
      // shows: that one's own button opens it; screen review #27). The Form 106 goal has its own "type it in"
      // (renderForm106Entry): no payslip form offered there. After a read it stays, small, at the very end.
      state.manualOpen || state.flow.goal === 'form106' || (state.uploads ?? []).some((u) => u.problem && !u.ocr)
        || [...state.ocr.values()].some((j) => j.status === 'offer' || j.status === 'cancelled' || j.status === 'failed') ? '' : (() => {
        const b = button(tr('upload.manualLink'), openManual, 'secondary');
        b.className = 'link-button manual-link';
        return el('p', {}, b);
      })(),
    );
    upload.addEventListener('dragover', (e) => {
      e.preventDefault();
      if (!state.dragging) {
        state.dragging = true;
        upload.classList.add('dragging');
      }
    });
    upload.addEventListener('dragleave', () => {
      state.dragging = false;
      upload.classList.remove('dragging');
    });
    upload.addEventListener('drop', (e) => {
      e.preventDefault();
      state.dragging = false;
      // Every dropped file: its bytes decide what it is (readOne), never its type or name. Read at once.
      const files = filesOf(e.dataTransfer?.files);
      files.forEach(startBytes);
      void readFiles(files);
    });
    // Design D: the bottom bar's privacy line under the step, in its other states too.
    return __FLOW_D__ ? el('div', { class: 'fd-upload' }, upload, fd().bottomBar({ el, tr, privacyHref: '#/privacy' })) : upload;
    };

    // Pasted data (JSON) is for development and testing only: dev builds or ?dev (src/ui/devMode.ts).
    const input = !isDevMode(location.search, import.meta.env.DEV) ? '' : el(
      'details',
      { class: 'card advanced', ...(state.source === 'json' || state.exampleLoaded ? { open: '' } : {}) },
      el('summary', {}, tr('input.advanced')),
      el('h2', {}, el('label', { for: 'payslip-json' }, tr('input.heading'))),
      el('p', { id: 'json-help', class: 'help' }, tr('input.help')),
      textarea,
      el(
        'div',
        { class: 'row' },
        button(tr('input.loadExample'), () => void guardLoss('loadExample', state.lang, LANGUAGES[state.lang].dir, { typed: state.json.trim() !== '' && !state.exampleLoaded }, () => {
          state.json = eng().exampleJson();
          state.exampleLoaded = true;
          state.source = 'json';
          state.result = undefined;
          render();
        }), 'secondary'),
      ),
      state.exampleLoaded ? el('p', { class: 'stamp' }, tr('input.exampleNotice'), ' ', el('span', { dir: 'ltr' }, eng().TEST_STAMP)) : '',
    );

    // Each step's section is built only when that step is shown (the check engine is loaded by then).
    const renderQuestions = (): HTMLElement => {
    const inPlay = fieldsInPlay();
    const statuses = state.answers.specialStatuses ?? [];
    const situations = (() => {
        const ticked = statuses;
        const d = el('details', { class: 'situations' },
          el('summary', {}, el('span', {}, tr('questions.situations'), ' ', el('span', { class: 'help' }, ticked.length ? ticked.map((x) => tr(`questions.specialStatus.${x}`)).join(', ') : tr('questions.specialStatus.none')),fd().pencilMark(el))),
          renderStatusQuestion(),
          // Under 18: asked (required) right under that option. For anyone else it's optional and only
          // matters under 21 (pension minimum age).
          statuses.includes('youth') ? '' : dateQuestion('birthDate', tr('questions.birthDateOptional'), state.answers.birthDate, (v) => setAnswer('birthDate', v)));
        // Answered on "Who are you?": a closed summary with "Change" (never asked twice; persona audit M9), open only when
        // asked for, or when something under it is still needed (an under-18's birth date, the adjusted percentage, the sector).
        const stillNeeded = (ticked.includes('youth') && !state.answers.birthDate)
          || (ticked.includes('adjustedMinimum') && state.answers.adjustedMinimumPercent === undefined && !notSureChosen.has('adjustedMinimumPercent'))
          || (ticked.includes('foreignWorker') && state.answers.workerType === undefined && !notSureChosen.has('workerType'));
        d.open = stillNeeded || state.moreOpen.has('situations') || (ticked.length > 0 && !state.flow.who);
        d.addEventListener('toggle', () => (d.open ? state.moreOpen.add('situations') : state.moreOpen.delete('situations')));
        return d;
      })();
    // Only a Form 106 (no payslip): only its questions; the payslip questions would ask about nothing.
    // A payslip counts only from a file or the payslip form with something in it (with a typed-in 106 and no file,
    // the empty payslip form and the sample JSON aren't payslips).
    const realPayslips = (state.uploads !== undefined || state.source === 'form') && inPlay.some((f) => Object.keys(f).length > 0);
    if (hasForm106() && !realPayslips) {
      // A Form 106 only: the other-income answers can void a tax year (the early-stop table), said right here.
      const f106Early = earlyNotice('questions', true);
      const f106Card = (withContinue: boolean) => el('section', { class: 'card' },
        f106Early.stop ? '' : el('h2', { id: 'questions-heading', tabindex: '-1' }, tr('questions.heading')),
        f106Early.node,
        ...form106Questions(inPlay),
        f106Early.stop || !withContinue ? '' : el('div', { class: 'row' }, Object.assign(button(tr('flow.continue'), continueFromQuestions), { id: 'questions-continue' })));
      if (!__FLOW_D__) return f106Card(true);
      // Design D (audit 2026-10-24 item 2): Continue is held until the other-income question is answered (each year's, or the one
      // question for several years with a year chosen for every item ticked); the bar names what is missing.
      const needed = f106Early.stop ? [] : form106Needed();
      if (state.flow.neededShown && !needed.length) delete state.flow.neededShown;
      const queue = state.flow.neededShown && needed.length;
      if (queue) queueMicrotask(() => fd().showNeeded(el, (k, p) => t(state.lang, k, p), needed, false));
      return el('div', { class: 'fd-q-wrap' }, f106Card(false), f106Early.stop ? '' : fd().bottomBar({
        el, tr: (k, p) => t(state.lang, k, p), privacyHref: '#/privacy', sticky: true, needed,
        action: { label: tr('flow.continue'), id: 'questions-continue', onClick: continueFromQuestions },
        onNeeded: () => {
          state.flow.neededShown = true;
          render();
          queueMicrotask(() => fd().showNeeded(el, (k, p) => t(state.lang, k, p), needed, true));
        },
      }));
    }
    // A stop found here (a status ticked or the sector chosen on this step): the answers that caused it stay changeable,
    // and nothing else is asked (docs/specs/early-stop-table.md).
    const early = earlyNotice('questions', true);
    // The questions only some payslips need (shared by the classic step and design D's cards).
    // Never asked when every check it feeds is already lost for this person (src/model/possibleChecks.ts QUESTION_FEEDS).
    const feeds = (q: keyof typeof QUESTION_FEEDS) => feedsSomething(q, state.flow.goal ?? 'month', situation(), earlyOptions());
    const sexQ = underPensionAgeForMen(inPlay) && feeds('sex')
      ? radioGroup('sex', tr('questions.sex'), [
          ['male', tr('questions.sex.male')],
          ['female', tr('questions.sex.female')],
          ['', tr('questions.preferNotToSay')],
        ], state.answers.sex ?? '', (v) => setAnswer('sex', v === '' ? undefined : (v as Sex)))
      : '';
    // Asked only when the payslip doesn't show the pay basis (docs/architecture.md: payBasis). The manual form asks pay basis and job % itself.
    const askPayBasis = !manualInUse() && inPlay.some((f) => !f.payBasis);
    const askRecuperation = inPlay.some((f) => f.recuperationAmount !== undefined || (f.paymentLines ?? []).some((l) => l.kind === 'recuperation')) && feeds('recuperationPayment');
    // Asked only when the payslip doesn't print it (docs/architecture.md: jobPercent); the answer overrides any inference.
    const askJobPercent = !manualInUse() && inPlay.some((f) => f.jobPercent === undefined);
    // Below the monthly minimum with no paid hours shown: unpaid leave would lower the minimum (src/rules/minimumWage.ts).
    const askUnpaid = inPlay.some((f) => eng().asksUnpaidLeave(f, eng().defaultParams, state.answers));
    const unpaidQ = askUnpaid
      ? radioGroup('unpaidLeave', tr('questions.unpaidLeave', { count: new Set(inPlay.map((f) => f.payMonth)).size }), [
          ['yes', tr('questions.yes')],
          ['no', tr('questions.no')],
          ['', tr('questions.dontKnow')],
        ], state.answers.unpaidLeave === undefined ? '' : state.answers.unpaidLeave ? 'yes' : 'no', (v) => {
          // Ticked months belong to "yes" only.
          // Kept aside, not lost: back if "yes" is chosen again.
          if (v !== 'yes' && state.answers.unpaidLeaveMonths) {
            unpaidMonthsKept = state.answers.unpaidLeaveMonths;
            state.answers = Object.fromEntries(Object.entries(state.answers).filter(([k]) => k !== 'unpaidLeaveMonths')) as UserAnswers;
          } else if (v === 'yes' && unpaidMonthsKept) {
            state.answers = { ...state.answers, unpaidLeaveMonths: unpaidMonthsKept };
            unpaidMonthsKept = undefined;
          }
          setAnswer('unpaidLeave', v === '' ? undefined : v === 'yes');
        })
      : '';
    // "Yes" with several months loaded: which months (unticked months count as "no"; none ticked yet → every month).
    const unpaidMonthsQ = state.answers.unpaidLeave === true && askUnpaid ? unpaidLeaveMonthsQuestion(inPlay) : '';
    // A foreign worker's housing deduction: the cap depends on the region (asked only when there is one).
    const housingQ = foreignWorkerType(state.answers) && inPlay.some((f) => (f.deductionLines ?? []).some((l) => l.kind === 'housing' || l.kind === 'ancillary'))
      ? radioGroup('housingRegion', tr('questions.housingRegion'), [
          ...HOUSING_REGIONS.map((r) => [r, tr(`questions.housingRegion.${r}`)] as [string, string]),
          ['', tr('questions.dontKnow')],
        ], state.answers.housingRegion ?? '', (v) => setAnswer('housingRegion', v === '' ? undefined : (v as HousingRegion)))
      : '';
    // Annual leave and sick days: the work week decides the days (not given or varying → the safer default for each
    // check). Not asked when the payslip's standard days show it ("ימי תקן", resolveWorkWeek).
    const workWeekQ = inPlay.some((f) => (f.vacationBalance || f.sickBalance) && (state.answers.workWeekDays !== undefined || !eng().resolveWorkWeek(f, {})))
      ? radioGroup('workWeekDays', tr('questions.workWeekDays'), [
          ['5', tr('questions.workWeekDays.5')],
          ['6', tr('questions.workWeekDays.6')],
          ['varies', tr('questions.workWeekDays.varies')],
          ['', tr('questions.dontKnow')],
        ], state.answers.workWeekDays === undefined ? '' : String(state.answers.workWeekDays),
        (v) => setAnswer('workWeekDays', v === '' ? undefined : v === 'varies' ? 'varies' : (Number(v) as 5 | 6)), 3, tr('questions.workWeekDays.help'))
      : '';
    // Pasted data (developer mode) has no button of its own; the manual form has its own.
    const devCheck = () => (state.source === 'json' && !state.uploads ? el('div', { class: 'row' }, button(tr('input.check'), () => {
      check();
      goTo('results');
    })) : '');
    // The optional date of birth (only matters under 21: the pension's minimum age) lived in the special-situations box, which
    // design D's questions step no longer shows (less-text mockups): its own closed card there, while the pension check is possible.
    const askBirthDate = __FLOW_D__ && !statuses.includes('youth') && feeds('hadPensionAtHire');
    // A stop because the goal doesn't fit "I still work there": that question stays, changeable, under the notice.
    const stopByLeaving = early.stop && possibleChecks(state.flow.goal ?? 'month', situation(), earlyOptions()).lost[0]?.kind === 'goal';
    // Design D (src/ui/flowD/questions.ts): a card per question, the same answers; a stop here shows only what caused it.
    if (__FLOW_D__) {
      return fd().questionsScreenD({
        el, tr, radio: radioGroup, answers: state.answers, notSure: (n) => notSureChosen.has(n), set: setAnswer, save: saveAnswer, early, situations,
        left: { value: leftJobOpen() ? 'yes' : state.moreOpen.has('leftJobNo') ? 'no' : undefined, set: (v) => setLeftJob(v === 'yes'), followUps: leftJobFollowUps,
          complete: state.answers.employmentEndDate !== undefined && (state.answers.leavingReason !== undefined || notSureChosen.has('leavingReason')) },
        refresh: renderKeepingFocus,
        ask: { sector: !sectorAnsweredOnWho(), fund: feeds('hadPensionAtHire'), payBasis: askPayBasis, recuperation: askRecuperation, jobPercent: askJobPercent, birthDate: askBirthDate },
        stopByLeaving,
        extras: [sexQ, ...startDateQuestions(), pensionCheckQuestion(), unpaidQ, unpaidMonthsQ, housingQ, workWeekQ, devCheck()], form106: form106Questions(inPlay), aboutYou: sexQ !== '',
        ...(askBirthDate ? { birthDate: { node: dateQuestion('birthDate', tr('questions.birthDateOptional'), state.answers.birthDate, (v) => setAnswer('birthDate', v)), shown: state.answers.birthDate ? formatDate(state.answers.birthDate) : '' } } : {}),
        canContinue: (payslipsRead() || hasForm106()) && state.source !== 'json',
        neededShown: state.flow.neededShown === true, onNeeded: () => { state.flow.neededShown = true; render(); }, onComplete: () => { delete state.flow.neededShown; },
        onContinue: continueFromQuestions, privacyHref: '#/privacy',
      });
    }
    if (early.stop) {
      // The stop is the page's news: its heading is the step's heading (the first screen of a phone holds the sentence and the buttons).
      return el('section', { class: 'card' }, early.node, stopByLeaving ? leftJobQuestion() : '', situations, ...(sectorAnsweredOnWho() ? [] : [answerQuestion('sector') ?? '']), ...(hasForm106() ? form106Questions(inPlay) : []));
    }
    const questions = el(
      'section',
      { class: 'card' },
      el('h2', { id: 'questions-heading', tabindex: '-1' }, tr('questions.heading')),
      // Screening (phase 2 plan 1.1): "none of these" by default, so it's one line until changed;
      // it opens by itself once a situation is ticked. The optional date of birth sits with it.
      situations,
      early.node,
      leftJobQuestion(),
      sexQ,
      sectorAnsweredOnWho() ? '' : answerQuestion('sector') ?? '',
      feeds('hadPensionAtHire') ? answerQuestion('hadPensionAtHire') ?? '' : '',
      ...startDateQuestions(),
      pensionCheckQuestion(),
      askPayBasis ? answerQuestion('payBasis') ?? '' : '',
      askRecuperation
        ? radioGroup('recuperationPayment', tr('questions.recuperationPayment'), [
            ['yearly', tr('questions.recuperationPayment.yearly')],
            ['instalments', tr('questions.recuperationPayment.instalments')],
            ['monthly', tr('questions.recuperationPayment.monthly')],
            ['', tr('questions.dontKnow')],
          ], state.answers.recuperationPayment ?? '', (v) => setAnswer('recuperationPayment', v === '' ? undefined : (v as RecuperationPayment)))
        : '',
      unpaidQ,
      unpaidMonthsQ,
      housingQ,
      workWeekQ,
      askJobPercent ? numberQuestion('jobPercent', tr('questions.jobPercent.optional'), state.answers.jobPercent, (v) => setAnswer('jobPercent', v), tr('questions.jobPercent.help')) : '',
      devCheck(),
      // The step's one primary action: on to the results (answers can be changed afterwards with Edit).
      ...form106Questions(inPlay),
      (payslipsRead() || hasForm106()) && state.source !== 'json' ? el('div', { class: 'row' }, Object.assign(button(tr('flow.continue'), continueFromQuestions), { id: 'questions-continue' })) : '',
    );
    return questions;
    };

    // Every results screen (payslips and Form 106): a way to tell us the results look wrong, even when the read succeeded.
    const reportLink = () => {
      const b = Object.assign(button(tr('results.reportProblem'), () => openReport(false), 'secondary'), { id: 'results-report-problem' });
      b.className = 'link-button';
      // The question and the action are two stacked lines in every language, so a break never falls inside the question (Fable r1):
      // the text is split after its first question mark; the whole stays one button (one name, one target).
      const parts = splitQuestion(tr('results.reportProblem'));
      if (parts) b.replaceChildren(el('span', { class: 'results-report-q' }, parts.q), parts.gap, el('span', { class: 'results-report-a' }, parts.a));
      return el('p', { class: 'results-report' }, b);
    };
    // "Check more" (audit 2026-10-24 item 6): what else this person can check, keeping the files and answers. Not on a translation.
    const checkMoreCard = (): HTMLElement | '' => {
      if (state.translateAsked || !state.flow.goal || !(state.result && ('payslips' in state.result || hasForm106()))) return '';
      const slips = state.result && 'payslips' in state.result ? state.result.payslips : [];
      const hasSlips = slips.length > 0;
      const has106 = hasForm106();
      const months = new Set(slips.map((p) => p.fields.payMonth).filter((m) => m !== undefined)).size;
      // A different document (the other goal) goes by "Who are you?": its required answers and its early notices are told on that step.
      const go = (goal: Goal, id: string, key: string) => Object.assign(button(tr(key), () => {
        const other = (goal === 'form106') !== (state.flow.goal === 'form106');
        state.flow.goal = goal;
        void Promise.all([loadEngine(), ...(goal === 'form106' ? [loadForm106()] : [])]).then(() => goTo(other ? 'who' : 'upload'));
      }, 'secondary'), { id });
      const buttons: HTMLElement[] = [
        ...(has106 ? [go('form106', 'more-another-year', 'results.more.anotherYear')] : []),
        ...(hasSlips && months <= 2 ? [go(state.flow.goal === 'form106' ? 'month' : state.flow.goal, 'more-payslips', 'results.more.morePayslips')] : []),
        ...(has106 && !hasSlips ? [go('month', 'more-check-payslips', 'results.more.checkPayslips')] : []),
        ...(hasSlips && !has106 && !COMING_SOON.includes('form106') ? [go('form106', 'more-form106', 'results.more.form106')] : []),
      ];
      return buttons.length ? el('section', { class: 'card check-more', id: 'check-more' }, el('h2', {}, tr('results.more.heading')), el('div', { class: 'row' }, ...buttons)) : '';
    };
    // Share (MATT GO 2026-10-25 01:06; src/share): at the end of the results, after "What else you can check", and in the footer.
    // A phone's share sheet opens inside the tap (src/share/payload.ts); the panel with the QR code is a lazy chunk.
    const shareButton = (id: string, link = false) => {
      const b = button(tr('share.button'), () => startShare(tr, () => void import('../share/panel').then((m) => m.openSharePanel(el, tr), () => undefined)), 'secondary');
      b.id = id;
      if (link) b.className = 'link-button';
      return b;
    };
    const shareCard = (): HTMLElement | '' => {
      // Not on a translation, and not under a crash or "nothing read": only where there is a result to be glad of.
      const slips = state.result && 'payslips' in state.result && !state.result.crashed ? state.result.payslips.length : 0;
      if (state.translateAsked || !(slips > 0 || hasForm106())) return '';
      const only106 = hasForm106() && slips === 0;
      return el('section', { class: 'card share-card', id: 'share-card' }, el('p', { class: 'share-line' }, tr(only106 ? 'share.line.form106' : 'share.line.payslip')), el('div', { class: 'row' }, shareButton('share-results')));
    };
    // Print / Save as PDF (G34): the browser's own print window (window.print, the print stylesheet in styles.css); both
    // buttons open it, a PDF is the print window's "Save as PDF". Nothing is sent; no library. Same conditions as the share card.
    const printCard = (): HTMLElement | '' => {
      const slips = state.result && 'payslips' in state.result && !state.result.crashed ? state.result.payslips.length : 0;
      if (state.translateAsked || !(slips > 0 || hasForm106())) return '';
      const printNow = () => {
        // The title is the PDF's suggested file name; restored after the print window closes.
        const before = document.title;
        document.title = tr('print.title');
        window.addEventListener('afterprint', () => { document.title = before; }, { once: true });
        window.print();
      };
      return el('section', { class: 'card print-card', id: 'print-card' }, el('p', { class: 'print-line' }, tr('print.line')),
        el('div', { class: 'row' }, Object.assign(button(tr('print.button'), printNow, 'secondary'), { id: 'print-results' }), Object.assign(button(tr('print.pdfButton'), printNow, 'secondary'), { id: 'save-pdf-results' })));
    };
    // Results, top line (Matt, 2026-10-26 14:08): whose documents these are, when the files hold more than one person's.
    const whoseFormsLine = (): HTMLElement | '' => {
      const who = peopleSpec();
      return who && who.view.people.length >= 2
        ? el('div', { class: 'results-whose', id: 'results-whose' }, el('p', {}, eng().checkingText({ tr, view: who.view, period: who.period })),
          ...eng().claimWarnings({ tr, view: who.view, period: who.period, file: who.file }).map((w) => el('p', { class: 'fd-people-claim-warning', role: 'note' }, w)))
        : '';
    };
    const renderResultsStep = () => translating() ? renderTranslation(lang) : [el('section', { class: 'card results', 'aria-live': 'polite' }, el('h2', { id: 'results-heading', tabindex: '-1' }, tr('results.heading')), whoseFormsLine(), ...renderResults(lang), diagnosticInput() ? reportLink() : ''), diagnostics(), checkMoreCard(), printCard(), shareCard()];
    const diagnostics = () => {
      const from = diagnosticInput();
      return from ? renderDiagnostics(from.input, from.payslips) : '';
    };

    /**
     * "Check your answers" (Matt, 2026-10-18; src/ui/checkAnswers.ts): before the results, every value read and every
     * answer, each with Change. A value read changes in place (the fill-in box, validated as under a "can't check"
     * result); an answer on the questions step; who and the documents on their own steps. A scan's numbers are
     * confirmed here: the results show only after "Confirm and see the results".
     */
    /**
     * The confirm step's "whose documents" block (src/ui/flowD/people.ts; src/upload/people.ts): the people found, the
     * documents whose ID wasn't read, and the same document in two files. Documents are named by kind, period and file.
     */
    const peopleSpec = (): Omit<FlowPeople.PeopleSpec, 'el' | 'tr' | 'level'> | undefined => {
      if (!state.uploads) return undefined;
      const merged = withOcr(state.uploads);
      const name = (r: Engine.DocRef): string => {
        const u = merged[r.file];
        const file = u?.name ?? '';
        if (r.page === undefined) {
          const year = u?.form106?.fields.taxYear;
          return year === undefined ? tr('people.name.form106NoYear', { file }) : tr('people.name.form106', { year: String(year), file });
        }
        const month = u?.payslips.find((p) => p.page === r.page)?.fields.payMonth;
        return month ? tr('people.name.payslip', { month: namedMonth(month), file }) : tr('people.name.payslipNoMonth', { file });
      };
      const period = (r: Engine.DocRef): string => {
        const u = merged[r.file];
        if (r.page === undefined) return u?.form106?.fields.taxYear === undefined ? '' : String(u.form106.fields.taxYear);
        const month = u?.payslips.find((p) => p.page === r.page)?.fields.payMonth;
        return month ? namedMonth(month) : '';
      };
      const copies = dupGroups(merged).flatMap((g) => {
        const kept = keptOf(g) ?? g.members[0];
        return kept ? g.members.filter((m) => !eng().sameRef(m, kept)).map((copy) => ({ kept, copy, kind: g.kind })) : [];
      });
      // A new answer moves which forms are counted: values typed for a form stay with it (as the copy chosen, keepForm106Fixes).
      const answer = (next: Engine.PeopleAnswers) => {
        const before = formsCounted();
        state.people = next;
        keepForm106Fixes(before);
        if (state.uploads) analyseUploads(state.uploads);
        renderKeepingFocus();
      };
      const view = peopleIn(merged);
      return {
        view, name, period, file: (r: Engine.DocRef) => merged[r.file]?.name ?? '', key: refKey, copies,
        onPick: (person) => answer({ ...state.people, person }),
        onClaim: (doc, on) => answer({ ...state.people, claimed: [...(state.people.claimed ?? []).filter((k) => k !== doc), ...(on ? [doc] : [])] }),
        onSame: (same) => answer({ ...state.people, unknown: { key: view.unknownKey, same } }),
        onOne: (one) => answer({ ...state.people, one }),
      };
    };
    const renderConfirm = (): HTMLElement | DocumentFragment => {
      // Read by OCR: a photo or scan says so; a PDF says why it wasn't read from its text (as src/ui/flowD/checkWire.ts).
      const scanNote = (file: string | undefined) => el('p', { class: 'note' }, tr((state.uploads ?? []).find((u) => u.name === file)?.fileType === 'pdf' ? 'cya.scanNotePdf' : 'cya.scanNote'));
      const deps: ConfirmDeps = { el, tr, state, eng, render, check, analyseUploads, monthName, prefillOf, radioGroup, payBasisOptions, setAnswer, numberQuestion, button,
        loaded, filesSummary, whoSummary, goTo, renderQuestions, sectorAnsweredOnWho, notSureChosen };
      // Design D (__FLOW_D__ builds only; the block is dropped from the others): "Check what we read" (src/ui/flowD/check.ts).
      if (__FLOW_D__) {
        // Gathered in src/ui/flowD/checkWire.ts (with the engine's lazy chunk, so the start screen keeps to its size budget).
        return eng().renderCheckD({
          ...deps, D: fd(), lang, form106Docs, heldEntries, useForm106Fix, usePayslipFix, monthsRowOf, shownValue, ocrAgain, loadTranslation, renderKeepingFocus, docsSig,
          formatMoney, formatDate, namedMonth, MISSING_INPUTS, HELD_KINDS, COUNT_KEYS, CONFIRM_ROWS, MANUAL_FIX, people: peopleSpec,
        });
      }
      // The helpers (src/ui/confirmRows.ts) come with the check engine's chunk, never with the start screen.
      const { texts, typable, valueRow, changeBox, answerBox, useLine, answerSummaryRows } = eng().confirmHelpers(deps);
      const out: (Node | string)[] = [];
      // Form 106s: their values, as the results list them (the employer in the heading, like the file).
      if (state.f106) {
        const f106 = state.f106;
        form106Docs().forEach((d, i) => {
          // Read but not for sure, or unusual (src/form106/review.ts): never shown as plainly read.
          const review = 'typed' in d ? [] : f106.form106Review(d.fields).filter((it) => it.suggestion !== undefined);
          const current = f106.withSuggestions(d.fields, review);
          const shown = (key: string, v: number | number[]) => (Array.isArray(v) ? v.join(', ') : MISSING_INPUTS[key] === 'money' ? formatMoney(v, lang) : String(v));
          const rows = f106.form106ValueRows(el, tr, (a) => formatMoney(a, lang), d.fields).filter((r) => r.key !== 'employerName').map((r) => {
            const text = r.label.map((n) => (typeof n === 'string' ? n : n.textContent ?? '')).join('');
            const id = `confirm-f${i}-${r.key}`;
            const doubt = review.find((it) => it.key === r.key);
            const editor = (done: () => void) => changeBox(id, r.key, text, current as Record<string, unknown>, (values) => useForm106Fix([i], values), done, undefined, monthsRowOf(i));
            const value = doubt?.suggestion !== undefined ? tr(doubt.why === 'unusual' ? 'cya.readUnusual' : 'cya.readUnsure', { value: shown(r.key, doubt.suggestion) }) : r.value;
            const row = valueRow(id, r.key, r.label, value, editor);
            // The box open with what was read, and why it needs checking, until the person confirms (Matt, 2026-10-18):
            // the months (src/form106/monthsRow.ts) "We may have misread this", a value that doesn't fit "looks unusual".
            const note = doubt?.why === 'unusual' ? 'missing.unusual' : r.key === 'monthsWorked' ? 'f106.monthsUnsure' : 'missing.pleaseCheck';
            const how = doubt?.fromTotal ? ` ${tr('f106.pointsFromTotalHow', { ...doubt.fromTotal, points: doubt.suggestion as number })}` : '';
            return doubt && state.confirmEdit !== id
              ? { ...row, editor: el('div', {}, el('p', { class: 'note', id: `${id}-unsure` }, tr(note) + how), editor(() => { state.confirmEdit = undefined; render(); })) }
              : row;
          });
          out.push(el('h3', {}, d.fields.taxYear ? tr('f106.heading', { year: String(d.fields.taxYear) }) : tr('f106.headingNoYear'),
            d.fields.employerName ? el('span', { class: 'source' }, ' · ', el('bdi', {}, d.fields.employerName)) : '', d.file ? el('span', { class: 'source' }, ' · ', el('bdi', { dir: 'ltr' }, d.file)) : ''),
            d.scan ? scanNote(d.file) : '',
            eng().summaryList(el, rows, texts));
        });
      }
      // Payslips: the values the checks use; one not read is listed when a check needs it (it can be typed in here).
      const r = state.result;
      if (r && 'payslips' in r) {
        const several = r.payslips.length > 1;
        r.payslips.forEach((p, i) => {
          const key = p.source ? `${p.source.file}#${p.source.page}` : MANUAL_FIX;
          const needed = new Set(p.findings.flatMap((f) => (f.status === 'cannot_check' ? f.missing ?? [] : [])));
          const rows: Engine.SummaryRow[] = CONFIRM_ROWS.flatMap((k) => {
            const value = shownValue(k, p.fields);
            if (value === undefined && !(typable(k) && needed.has(k))) return [];
            const label = tr(`cya.field.${k}`);
            const id = `confirm-p${i}-${k}`;
            return [valueRow(id, k, [label], value, (done) => (k === 'payBasis' || k === 'jobPercent' ? answerBox(k, label, done)
              : changeBox(id, k, label, p.fields as Record<string, unknown>, (values) => usePayslipFix(key, p.fields, values), done)))];
          });
          // Payment and deduction lines as printed, each amount changeable.
          for (const which of ['paymentLines', 'deductionLines'] as const) {
            (p.fields[which] ?? []).forEach((l, n) => {
              const k = `${which === 'paymentLines' ? 'line' : 'deduction'}${n}`;
              const id = `confirm-p${i}-${k}`;
              const label = l.label || tr('ocr.unnamedLine');
              // Outside Hebrew, the line's term in the page's language first (the translated view's), the printed label
              // after it, muted (screen review 2026-10-18); the terms come with the translation chunk, loaded here.
              const concept = which === 'paymentLines' && lang !== 'he' ? eng().paymentConcept(l as Parameters<typeof Engine.paymentConcept>[0]) : undefined;
              const term = concept && state.tv ? tr(`tv.term.${concept}`) : undefined;
              if (concept && !state.tv) void loadTranslation().then(() => renderKeepingFocus());
              const shownLabel = term ? [term, ' ', el('span', { class: 'source' }, el('bdi', {}, label))] : [el('bdi', {}, label)];
              rows.push(valueRow(id, k, shownLabel, formatMoney(l.amount, lang),
                (done) => changeBox(id, k, label, { [k]: l.amount }, (values) => useLine(key, p.fields, which, n, values), done, { [k]: 'money' })));
            });
          }
          // Never "unknown": the month not found is said so (its row below has Change).
          const title = [p.fields.payMonth ? tr('results.payslipHeading', { month: namedMonth(p.fields.payMonth) }) : tr('results.payslipHeadingNotFound'), p.source ? el('span', { class: 'source' }, ' · ', el('bdi', { dir: 'ltr' }, p.source.file), ` · ${tr('results.page', { page: p.source.page })}`) : ''];
          const again = p.ocr && p.source ? ocrAgain(p.source.file, p.source.page) : '';
          const body = [p.ocr ? scanNote(p.source?.file) : '', eng().summaryList(el, rows, texts), again];
          // Several payslips fold like the results; a scan's stay open (their numbers are being confirmed).
          out.push(several
            ? el('details', { class: 'payslip-section', ...(p.ocr || rows.some((x) => x.editor) ? { open: '' } : {}) }, el('summary', {}, el('h3', { class: 'payslip-heading' }, ...title)), ...body)
            : el('h3', { class: 'payslip-heading' }, ...title), ...(several ? [] : body));
        });
      }
      const confirm = Object.assign(button(tr('cya.confirm'), () => {
        state.confirmedSig = docsSig();
        state.confirmEdit = undefined;
        goTo('results');
      }), { id: 'see-results' });
      return el('section', { class: 'card confirm-answers' },
        el('h2', { id: 'confirm-heading', tabindex: '-1' }, tr('cya.heading')),
        el('p', { class: 'help' }, tr('cya.intro')),
        ...out,
        el('h3', {}, tr('cya.answersHeading')),
        // One list: who, the documents, then every question (screen review 2026-10-18: two lists left a gap).
        eng().summaryList(el, answerSummaryRows(), { ...texts, empty: tr('cya.notAnswered') }),
        el('p', {}, tr('cya.confirmHelp')),
        el('div', { class: 'row' }, confirm));
    };

    const footer = el(
      'footer',
      { class: 'footer' },
      el('nav', { 'aria-label': tr('footer.nav') },
        // Design D: the start screen's how-it-works content in a dialog (src/ui/flowD/opening.ts), no longer the old welcome.
        Object.assign(button(tr('footer.howItWorks'), () => (__FLOW_D__ ? void import('./flowD/opening').then((m) => m.openHowItWorks(el, tr)) : welcome?.open()), 'secondary'), { className: 'link-button', id: 'how-it-works' }),
        ...PAGE_NAMES.map((p) => el('a', { href: `#/${p}`, ...(page === p ? { 'aria-current': 'page' } : {}) }, tr(`footer.${p}`))),
        shareButton('share-footer', true)),
      installOffer() && (page || stage() !== 'goal') ? el('div', { class: 'install-row' }, installButton('install-footer')) : '',
      // The build id is isolated left to right (in Hebrew its parts reordered; screen review 2026-10-18).
      el('p', { class: 'help version' }, el('span', {}, tr('footer.version', { version: APP_VERSION, build: `\u2066${BUILD}\u2069` })),
        Object.assign(button(tr('footer.checkUpdates'), () => void runUpdateCheck(), 'secondary'), { className: 'link-button', id: 'check-updates', disabled: state.updateCheck === 'checking' })),
      // A new version ready while something is entered: the update button here too, not only in the banner at the top.
      state.updateAvailable
        ? el('div', { class: 'update-ready' },
          el('p', { class: 'help', role: 'status', id: 'update-check-status' }, tr('footer.update.ready')),
          Object.assign(button(tr('footer.update.now'), reloadGuarded), { id: 'update-now' }),
          el('p', { class: 'help' }, tr('footer.update.reloadNote')))
        // A live region that is always there (so a screen reader announces the change), empty once the message cleared itself.
        : el('p', { class: 'help', role: 'status', 'aria-live': 'polite', id: 'update-check-status' }, state.updateCheck ? tr(`footer.update.${state.updateCheck}`) : ''),
    );

    if (page) {
      document.title = `${tr(`page.${page}.title`)} · ${tr('app.name')}`;
      // Design D (redesign part 2, screen 10): the privacy page's own layout once its chunk is here (the old one meanwhile).
      if (__FLOW_D__ && page === 'privacy' && !pvD) void Promise.all([import('./flowD/privacy'), loadFlowSteps()]).then(([m]) => {
        pvD = m;
        render();
        // The new page replaced the one that had the focus: "turn off" (#/privacy/counter) lands on the switch's card, else the title.
        if (pageFromHash(location.hash) === 'privacy') (document.getElementById(/^#\/privacy\/counter$/.test(location.hash) ? 'privacy-counter' : 'page-title'))?.focus();
      });
      root.replaceChildren(header, el('main', { class: 'page' }, ...(__FLOW_D__ && page === 'privacy' && pvD ? [privacyPageD(pvD)] : renderPage(page))), footer);
      // Beta builds: the screen code and "Report a problem on this page" (src/beta).
      if (__BETA__) markScreen({ root, key: screenKey({ page, step: 'goal' }), lang, tr });
      else markStripeCode(root, screenKey({ page, step: 'goal' }));
      glueSeparators(root);
      return;
    }
    // Beta builds: the preview test checklist (#/beta-test, src/beta/checklist.ts).
    if (__BETA__ && isChecklistHash(location.hash)) {
      document.title = `Preview test checklist · ${tr('app.name')}`;
      root.replaceChildren(header, el('main', { class: 'page' }, renderChecklist(el)), footer);
      return;
    }
    // The take-home pay calculator (`__TAKE_HOME__`, on unless MAGIA_TAKE_HOME=0, which drops this branch and the chunk): the take-home pay
    // calculator, a page of its own (#/take-home, src/calculator), its chunk loaded the first time the page is opened.
    if (__TAKE_HOME__ && TAKE_HOME_HASH.test(location.hash)) {
      if (!calcMod) void import('../calculator').then(async (m) => {
        m.registerMessages(addMessages);
        await m.loadMessages(state.lang, addMessages);
        calcMod = m;
        render();
        document.getElementById('page-title')?.focus();
      });
      document.title = calcMod ? `${tr('calc.title')} · ${tr('app.name')}` : tr('app.name');
      root.replaceChildren(header, el('main', { class: 'page' }, ...(calcMod ? calcMod.calculatorPage({
        el, tr, money: (a, whole) => formatMoney(a, lang, { whole: whole === true }), number: (n) => n.toLocaleString(LANGUAGES[lang].locale), today: today(), params: calcMod.defaultParams, rerender: renderKeepingFocus,
      }) : [])), footer);
      glueSeparators(root);
      return;
    }
    // Notices and the privacy line live inside <main>, so no content sits outside a landmark.
    // The guided flow: one step open at a time; the finished ones before it are a line each, with Edit.
    const open = stage();
    const body: (Node | string)[] =
      open === 'goal' ? [renderGoal()]
      : open === 'who' ? (__FLOW_D__ ? renderWhoD() : [renderWho()])
      : open === 'upload' ? [renderUpload(), renderIosInstall(), state.manualOpen ? renderManual() : '', input, diagnostics()]
      : open === 'questions' ? ((q: HTMLElement) => [q, ...(__FLOW_D__ && !q.matches('.fd-questions, .fd-q-wrap') ? [fd().bottomBar({ el, tr, privacyHref: '#/privacy' })] : [])])(renderQuestions())
      : open === 'confirm' ? [renderConfirm()]
      // Design D: the payslip results carry the privacy line in their own footer (UX-1); a Form 106 alone gets the bar's.
      : [...renderResultsStep(), ...(__FLOW_D__ && (translating() || !(state.result && 'payslips' in state.result && state.result.payslips.length)) ? [fd().bottomBar({ el, tr, privacyHref: '#/privacy' })] : [])];
    // The file pickers stay in the page on every step (files shared or dropped in go straight to reading).
    const pickers = open === 'upload' ? '' : el('div', { hidden: '' }, picker, camera);
    root.replaceChildren(
      header,
      el('main', __FLOW_D__ ? { class: 'fd-tone' } : {}, notice, dataNotice, debug && debugTextMod ? renderDebugPanel(debugTextMod.debugText) : '', // A stop on the questions step is the page's news: the finished steps' summaries would push it down a phone's first screen (its Back goes there).
        ...(open === 'results' || (open === 'questions' && state.flow.goal && possibleChecks(state.flow.goal, situation(), earlyOptions()).stop) ? [] : stepSummaries(open)), ...(open === 'results' ? withBefore(body, stepSummaries(open)) : body), pickers), // Results: the choices card sits below the results (ux-expert 2026-10-24: above them it pushed the "Results" heading down)
      footer,
    );
    // Beta builds: the screen code (flow, step, what is open) and "Report a problem on this page" (src/beta).
    const screen = screenKey({
      step: open, goal: state.flow.goal,
      foreign: state.flow.who === 'foreign' || (state.answers.specialStatuses ?? []).includes('foreignWorker'),
      translate: Boolean(state.translateAsked) || (open === 'results' && state.translated.size > 0),
      manualOpen: state.manualOpen, form106Open: state.form106Open,
      scanReview: [...state.ocr.values()].some((j) => j.status === 'running' || (j.status === 'review' && j.pages.some((p) => !p.confirmed))),
    });
    if (__BETA__) markScreen({ root, lang, tr, key: screen });
    else markStripeCode(root, screen); // production: only the code, in the build stripe (src/beta/stripeCode.ts)
    glueSeparators(root);
    // WCAG 2.4.2: the tab and history name the step, from the step's own heading (a figure heading is not a name).
    const stepHeading = root.querySelector<HTMLElement>('main h2[tabindex="-1"]:not(.fd-summary-big)')?.textContent?.trim();
    if (stepHeading && stepHeading.length < 90) document.title = `${stepHeading} · ${tr('app.name')}`;
  }

  /** Design D's privacy page (src/ui/flowD/privacy.ts): the approved line, the cards, the counter's switch. */
  function privacyPageD(m: typeof import('./flowD/privacy')): HTMLElement {
    const tr = (key: string, params?: Record<string, string | number>) => t(state.lang, key, params);
    return m.privacyPage({
      el, tr, counter: Boolean(COUNTER_URL), beta: __BETA__, cover: __COVER__, readAloud: __READ_ALOUD__, brandC: __BRAND__ === 'c', keys: Object.keys(MESSAGES.en),
      approved: tr(COUNTER_URL ? 'app.privacyLine.counter' : 'app.privacyLine.noCounter'), ...(COUNTER_URL ? { offLink: tr('app.privacyLink') } : {}),
      draft: DRAFT_PAGES.includes('privacy'), email: CONTACT_EMAIL, countingOff: countingOff(),
      onCountingOff: (off) => {
        setCountingOff(off);
        // Ticked: the total read earlier isn't shown either (the help text says so), without a reload.
        if (off) delete state.checksTotal;
      },
    });
  }

  /** An information page (privacy, disclaimer, accessibility, terms): structure in pages.ts, text in i18n; drafts say so. */
  function renderPage(page: PageName): HTMLElement[] {
    const tr = (key: string) => t(state.lang, key);
    const out: HTMLElement[] = [el('h2', { id: 'page-title', tabindex: '-1' }, tr(`page.${page}.title`))];
    if (DRAFT_PAGES.includes(page)) out.push(el('p', { class: 'draft', role: 'note' }, tr('page.draft')));
    let list: HTMLUListElement | undefined;
    for (const b of PAGES[page]) {
      if (b.tag === 'li') {
        if (!list) out.push((list = el('ul', {})));
        list.append(el('li', {}, tr(b.key)));
        continue;
      }
      list = undefined;
      if (b.tag === 'optOut') {
        // "Don't count my checks": kept on this device only (src/ui/counter.ts); ticked, no counter request at all.
        const box = el('input', { type: 'checkbox', id: 'no-count', 'aria-describedby': 'no-count-help' });
        box.checked = countingOff();
        box.addEventListener('change', () => {
          setCountingOff(box.checked);
          // Ticked: the total read earlier isn't shown either (the help text says so), without a reload.
          if (box.checked) delete state.checksTotal;
        });
        out.push(el('div', { class: 'question-option' }, el('label', { class: 'choice' }, box, tr(b.key)), el('p', { id: 'no-count-help', class: 'help' }, tr(`${b.key}Help`))));
        continue;
      }
      if (b.tag === 'link') {
        out.push(el('p', {}, el('a', { href: LICENCES_FILE, target: '_blank', rel: 'noopener noreferrer' }, tr(b.key))));
        continue;
      }
      // The counter's section has an anchor of its own: "What exactly is sent?" under the tagline opens it.
      const attrs = b.key === 'page.privacy.counterHeading' ? { id: 'privacy-counter', tabindex: '-1' } : {};
      out.push(b.tag === 'email' ? el('p', {}, `${tr(b.key)} `, el('a', { href: `mailto:${CONTACT_EMAIL}`, dir: 'ltr' }, CONTACT_EMAIL)) : el(b.tag, attrs, ...paragraphChildren(el, b.key, tr(b.key), tr('more.newTab'))));
    }
    out.push(el('p', {}, el('a', { href: '#/' }, tr('page.back'))));
    return [el('article', { class: 'card' }, ...out)];
  }

  function renderResults(lang: Lang): (Node | string)[] {
    const r = state.result;
    // A Form 106 (with or without payslips): its section first (docs/form106.md). A translation asked for ("Translate a
    // payslip", or a payslip's translation opened) has nothing for a Form 106 yet: said above it (doc-type audit; its
    // text `results.form106NotTranslated` waits for the text rewrite, and shows once it exists).
    const payslipsToo = Boolean(r && !('errors' in r) && r.payslips.length > 0);
    // Both together: the block of the goal chosen at the start leads (Matt 2026-10-24 21:37): the Form 106 goal puts the Form 106
    // first; any payslip goal puts the payslips first and the Form 106 under its own heading.
    const f106First = state.flow.goal === 'form106';
    // With payslips below the Form 106 results: one disclaimer set at the end of the page (ux-expert, 2026-10-19, item 1).
    const forms = renderForm106Results(lang, payslipsToo && f106First, !payslipsToo || f106First);
    const untranslated = forms.length && (state.translateAsked || state.translated.size > 0) && hasMessage('en', 'results.form106NotTranslated')
      ? [el('p', { class: 'note tv-unsupported' }, t(lang, 'results.form106NotTranslated'))] : [];
    const f106 = [...untranslated, ...forms];
    if (f106.length && (!r || 'errors' in r || r.payslips.length === 0)) return f106;
    // The Form 106 disclaimer (the form's second element) goes after the payslips' own lines.
    if (!f106First) {
      const pay = renderPayslipResults(lang, false);
      if (!forms.length) return [...untranslated, ...pay];
      // The Form 106 block comes right after the payslip block, so it does not read as a footer (Fable r1, 2026-10-25): payslip
      // cards, that block's own "Also checked" fold, the Form 106 block, then translate, disclaimer, privacy. Inside the payslip
      // results when they have the folded list (the fold stays with the payslip checks), else before the first of the rest.
      const block = [el('div', { class: 'rv f106-section' }, el('h2', { class: 'f106-section-heading' }, t(lang, 'f106.results.sectionHeading'))), ...forms];
      // The payslip block is one `.rv` div whose last child is its folded list (tail empty): the Form 106 block follows that div as a
      // sibling, so no `.rv` is nested in another (styles and the tone are not applied twice). A block with a tail inside (a typed-in
      // payslip) takes the block after its folded list instead.
      const foldedAt = pay.findIndex((n) => n instanceof HTMLElement && n.querySelector(':scope > .other-findings') !== null);
      const holder = foldedAt >= 0 ? pay[foldedAt] : undefined;
      if (holder instanceof HTMLElement) {
        const list = holder.querySelector(':scope > .other-findings');
        if (list && holder.lastElementChild === list) pay.splice(foldedAt + 1, 0, ...block);
        else list?.after(...block);
        return [...untranslated, ...pay];
      }
      const at = pay.findIndex((n) => n instanceof HTMLElement && (n.matches('.legal-minimums, .disclaimer') || (n.classList.contains('row') && n.querySelector('[id^="tv-toggle-"]') !== null) || n.matches('.rv') && n.querySelector('.rv-privacy') !== null));
      pay.splice(at < 0 ? pay.length : at, 0, ...block);
      return [...untranslated, ...pay];
    }
    const [first, ...tail] = forms;
    return [...untranslated, ...(first ? [first] : []), ...renderPayslipResults(lang, forms.length > 0), ...tail];
  }

  const seededResults = new WeakSet<object>();

  /** `afterForm106`: a Form 106's results are above (its headline leads): this block's heading first, the total as a plain line, one disclaimer set at the end. */
  /** Set while the payslip results are drawn under a Form 106's: the UX-1 total then uses that headline's size. */
  let underForm106 = false;
  function renderPayslipResults(lang: Lang, afterForm106 = false): (Node | string)[] {
    underForm106 = afterForm106;
    const r = state.result;
    if (!r || 'errors' in r) return renderView(eng().resultsView(lang, r), 'h3', 'r');
    // "Report this error" (every build; opt-in; src/beta/errorReportOffer.ts), closed until the user opens it.
    if (r.crashed) return [el('div', { class: 'errors', role: 'alert' }, el('p', {}, t(lang, 'results.crashed'))), __ERROR_REPORTS__ ? errorReportOffer((k) => t(lang, k)) : ''];
    if (r.payslips.length === 0) return [el('p', { class: 'help' }, t(lang, 'upload.nothingRead'))];
    const [only] = r.payslips;
    if (r.payslips.length === 1 && only && !only.source) {
      return [...(r.enteredByHand ? [el('p', { class: 'note entered-by-hand' }, t(lang, 'results.enteredByHand'))] : []), ...renderView(eng().resultsView(lang, { findings: only.findings }, { enteredByHand: r.enteredByHand === true }), 'h3', 'p0', payslipFix(MANUAL_FIX, only.fields), 'payslip', !afterForm106, true, only.fields.payMonth ? namedMonth(only.fields.payMonth) : undefined), ...translatedView(only, 0, lang)];
    }
    // Several payslips (or a PDF): the legal-minimums header and disclaimer once, a heading per payslip.
    const views = r.payslips.map((p) => eng().resultsView(lang, { findings: p.findings }));
    const several = r.payslips.length > 1;
    // Several payslips: the overview first, each payslip folded under its heading (opened with a tap).
    const worth = r.payslips.reduce((n, p) => n + p.findings.filter((f) => f.status === 'verify' && eng().isShown(f)).length, 0);
    const total = r.payslips.reduce((sum, p) => sum + claimableTotal(p.findings.filter(eng().isShown)), 0);
    const overviewKey = eng().overviewKey(r.payslips.map((p) => p.findings));
    const overview = several
      ? el('div', { class: afterForm106 ? 'results-overview quiet' : 'results-overview' },
          // "Nothing worth checking was found" only when some check compared something (false-reassurance audit).
          hasMessage('en', overviewKey) ? el('p', {}, t(lang, overviewKey, { payslips: r.payslips.length, worth })) : '',
          total > 0 ? el('p', { class: 'amount' }, el('span', { class: 'amount-label' }, t(lang, 'flow.results.total')), ' ', el('bdi', { class: 'amount-value' }, formatMoney(total, lang))) : '',
          el('p', { class: 'help' }, t(lang, 'flow.results.openPayslip')))
      : '';
    // Brand C: one payslip gets the total bar too: "total worth checking", an estimate, on a gold chip.
    // The summary card (src/results) carries the total of a single payslip: no second total bar.
    const brandTotal = state.rv && !several ? ''
      : afterForm106 && !several && total > 0
      ? el('p', { class: 'payslip-total' }, t(lang, 'brand.totalLabel'), ' ', el('bdi', { class: 'amount-num' }, formatMoney(total, lang)))
      : __BRAND__ === 'c' && !several && total > 0
      ? el('div', { class: 'brand-total' },
          el('p', { class: 'amount' }, el('span', { class: 'amount-label' }, t(lang, 'brand.totalLabel')), ' ', el('bdi', { class: 'amount-value' }, formatMoney(total, lang))),
          el('p', { class: 'help' }, t(lang, 'brand.totalNote')))
      : '';
    // Several payslips: the card with the largest amount opens once per results view (Matt, 2026-10-19); ties go to the first, no amount opens nothing. Later the user's choices win.
    if (several && !seededResults.has(r)) {
      seededResults.add(r);
      for (const k of [...state.moreOpen]) if (k.startsWith("payslip-")) state.moreOpen.delete(k);
      const bestAt = largestAmountIndex(r.payslips.map((p) => claimableTotal(p.findings.filter(eng().isShown))));
      if (bestAt >= 0) state.moreOpen.add(`payslip-${bestAt}`);
    }
    const header = legalHeader([...eng().scopeLines(lang, r.payslips.flatMap((p) => p.findings)), ...(afterForm106 ? [] : [t(lang, 'results.notAdvice')])]);
    return [
      ...(afterForm106 && !several ? [] : [overview, brandTotal, ...(state.rv ? [] : [header])]),
      ...r.payslips.flatMap((p, i) => {
        const view = views[i];
        const heading = el('h3', { class: 'payslip-heading' }, p.fields.payMonth ? t(lang, 'results.payslipHeading', { month: namedMonth(p.fields.payMonth) }) : t(lang, 'results.payslipHeadingNotFound'), p.source ? el('span', { class: 'source' }, ' · ', el('bdi', { dir: 'ltr' }, p.source.file), ` · ${t(lang, 'results.page', { page: p.source.page })}`) : '');
        const body = [
          p.ocr ? el('p', { class: 'note ocr-note' }, t(lang, p.source?.vendor === OCR_GENERIC ? 'ocr.resultsNoteGeneric' : p.source?.vendor === LABELS_GENERIC ? 'ocr.resultsNoteLabels' : 'ocr.resultsNote')) : '',
          p.betaVendor ? el('p', { class: 'note beta-vendor-note' }, t(lang, 'results.betaVendor', { vendor: p.source?.vendor ?? '' })) : '',
          ...(view ? renderView(view, 'h4', `p${i}`, payslipFix(p.source ? `${p.source.file}#${p.source.page}` : MANUAL_FIX, p.fields), 'payslip', true, false, p.fields.payMonth ? namedMonth(p.fields.payMonth) : undefined).filter((n) => !(n instanceof HTMLElement && (n.classList.contains('legal-minimums') || n.classList.contains('disclaimer')))) : []),
          ...translatedView(p, i, lang),
        ];
        if (!several) return afterForm106 ? [heading, brandTotal, header, ...body] : [heading, ...body];
        const key = `payslip-${i}`;
        const count = p.findings.filter((f) => f.status === 'verify' && eng().isShown(f)).length;
        // Nothing compared on this payslip: "– can't check", never "✓ nothing worth checking" (false-reassurance audit).
        const glance = count ? `! ${t(lang, 'results.status.verify')}: ${count}` : eng().payslipGlance(p.findings) === 'ok' ? `✓ ${t(lang, 'flow.results.nothingHere')}` : `${STATUS_ICON.cannot_check} ${t(lang, 'results.status.cannot_check')}`;
        const monthTotal = claimableTotal(p.findings.filter(eng().isShown));
        const d = el('details', { class: 'payslip-section' },
          // Audit 2026-10-24 item 7: the month's amount worth checking is visible without opening the row.
          el('summary', {}, heading, el('span', { class: `payslip-count${count ? ' verify' : ''}` }, glance, ...(monthTotal > 0 ? [' · ', el('bdi', { class: 'amount-value' }, formatMoney(monthTotal, lang))] : []))),
          ...body);
        d.open = state.moreOpen.has(key);
        d.addEventListener('toggle', () => (d.open ? state.moreOpen.add(key) : state.moreOpen.delete(key)));
        return [d];
      }),
      // The UX-1 screen (src/results) puts what was checked at the end, once for the page.
      ...(state.rv && !afterForm106 ? [header] : []),
      el('p', { class: 'help disclaimer' }, t(lang, 'results.disclaimer')),
      // The advice line shows once per page: with a Form 106 above, its results carry it.
      ...(afterForm106 ? [] : [el('p', { class: 'help disclaimer' }, t(lang, 'brand.footer'))]),
      ...(state.rv && !afterForm106 ? [el('div', { class: 'rv' }, privacyFooter())] : []),
      ...(afterForm106 ? [el('p', { class: 'help disclaimer' }, t(lang, 'results.notAdvice'), ' ', el('a', { href: '#/disclaimer' }, t(lang, 'results.fullDisclaimer')))] : []),
    ];
  }

  /**
   * "Translate this payslip, line by line" (docs/specs/payslip-translation.md): the payslip's lines in a table, label as
   * printed | translation | amount | meaning, from the dictionary (src/translate). Offered for payroll systems whose labels
   * the dictionary has; the fields are the ones the checks use (a scan's only once the user confirmed them).
   */
  function translatedView(p: Analysed, i: number, lang: Lang): HTMLElement[] {
    const open = state.translated.has(i) && state.tv;
    const toggle = button(t(lang, open ? 'tv.hide' : 'tv.showShort'), () => {
      if (open) {
        state.translated.delete(i);
        return renderKeepingFocus();
      }
      void loadTranslation().then(() => {
        state.translated.add(i);
        renderKeepingFocus();
      });
    }, 'secondary');
    toggle.id = `tv-toggle-${i}`;
    toggle.setAttribute('aria-expanded', String(Boolean(open)));
    if (!open) return [el('div', { class: 'row' }, toggle)];
    toggle.setAttribute('aria-controls', `tv-${i}`);
    const table = translationTable(p, i, lang);
    return [el('div', { class: 'row' }, toggle), __FLOW_D__ && tvD
      ? withOverlay(tvD.translateView(translateSpec(table, p.fields.payMonth, lang)), p, i, lang, table.rows)
      : open.translatedTable(table)];
  }

  /** The translated-overlay view's chunk (src/translate/overlay.ts; `__TRANSLATED_OVERLAY__` builds), loaded with the first translated view. */
  let overlayMod: typeof import('../translate/overlay') | undefined;
  /**
   * G11 slice 1 (on in production since Matt G24, 2026-10-27; MAGIA_TRANSLATED_OVERLAY=0 turns it off): a document's
   * translated view gets the closed box "Your payslip page, labels translated" above its line boxes, which stay as the
   * fallback and the screen-reader equivalent. A Shiklulit digital payslip: its page is drawn from the bytes in page
   * memory only when the box is opened (src/translate/overlay.ts: never stored or sent); a scan or photo (slice 3) from
   * its page image as the OCR read it (scanPictures, memory only). A document the view can't show yet
   * (src/translate/view.ts overlayCase, or a scan whose page image isn't in memory): the box says so, then the
   * anonymous-layout offer (layoutOffer).
   */
  function withOverlay(view: HTMLElement, p: Analysed, i: number, lang: Lang, rows: ReturnType<typeof translationTable>['rows']): HTMLElement {
    // Off: nothing below is in the build, nor the chunk it loads.
    if (!__TRANSLATED_OVERLAY__) return view;
    if (!p.source) return view;
    const source = p.source;
    // By the upload it was read from: two files can share a name.
    const at = source.fileIndex ?? state.uploads?.findIndex((u) => u.name === source.file) ?? -1;
    const file = at >= 0 ? state.files[at] : undefined;
    if (!file) return view;
    const kind = eng().overlayCaseOf({ kind: 'payslip', ...(p.ocr ? { scan: true } : {}), vendor: source.vendor });
    if (kind !== 'overlay') return withNotYet(view, kind, `tv-${i}`, at, lang);
    // A scan or photo (slice 3): its page image as read, kept in memory while the file is; without it, the note.
    const picture = p.ocr ? scanPictures.get(file)?.get(source.page) : undefined;
    if (p.ocr && !picture) return withNotYet(view, 'notYet.scan', `tv-${i}`, at, lang);
    // A covered page with no label placed (none printed once, as one item): no box, the line boxes have every line.
    if (!rows.some((r) => r.box)) return view;
    if (!overlayMod) {
      // A chunk that fails to load leaves the view as it is: its line boxes have every line.
      import('../translate/overlay').then((m) => {
        overlayMod = m;
        renderKeepingFocus();
      }, () => undefined);
      return view;
    }
    return placeBox(view, overlayBox(view, overlayMod, { key: `tvo-${i}`, id: `tv-${i}`, rows, pageNumber: source.page, file, lang, password: state.passwords.get(at), format: eng().overlayFormat(source.vendor, !!p.ocr && (source.vendor === OCR_GENERIC || source.vendor === LABELS_GENERIC)), ...(picture ? { picture } : {}) }));
  }

  /** The overlay's closed box for one page of one uploaded file (a payslip or, slice 2, a Form 106). */
  function overlayBox(view: HTMLElement, mod: typeof import('../translate/overlay'), o: { key: string; id: string; rows: ReturnType<typeof translationTable>['rows']; pageNumber: number; file: File; lang: Lang; password: string | undefined; form106?: true; numbered?: true; picture?: PageResult; format?: string }): HTMLElement {
    const { lang, file, pageNumber, picture } = o;
    const chevron = view.querySelector('.tvd-box > summary > svg:last-child')?.cloneNode(true);
    return mod.translatedOverlay({
      el, tr: (k: string, params?: Record<string, string | number>) => t(lang, k, params), id: o.id, lang, dir: LANGUAGES[lang].dir,
      rows: o.rows, pageNumber, file, ...(o.format ? { format: o.format } : {}), ...(o.form106 ? { form106: true as const } : {}), ...(o.numbered ? { numbered: true as const } : {}), ...(chevron ? { chevron } : {}),
      // A scan's boxes are pixels of its page image (src/translate/scanBoxes.ts): the overlay scales them to points.
      ...(picture ? { pointsPerUnit: picture.pointsPerPixel } : {}),
      open: state.moreOpen.has(o.key), onToggle: (open) => (open ? state.moreOpen.add(o.key) : state.moreOpen.delete(o.key)),
      draw: async (scale) => {
        // A scan or photo: the processed (upright, deskewed) page image the OCR read, at its own resolution.
        if (picture) {
          const { drawGray } = await import('../cover/pages');
          const canvas = document.createElement('canvas');
          drawGray(canvas, picture.gray);
          return { canvas, width: picture.gray.width * picture.pointsPerPixel, height: picture.gray.height * picture.pointsPerPixel };
        }
        const { bytes } = await bytesOf(file);
        if (!bytes) throw new Error('no bytes');
        const [{ renderPdfPage }, { pdfjs }] = await Promise.all([import('../cover/pages'), loadPdfjs(noteError)]);
        return renderPdfPage(pdfjs, bytes, { pageNumber, scale, ...(o.password !== undefined ? { password: o.password } : {}) });
      },
    });
  }

  /**
   * A Form 106's translated view (G11 slice 2): a digital form gets the closed box with its page and the labels the reader
   * placed (as a payslip's: src/translate/view.ts form106WithBoxes keeps only a label the page prints as one item); a scan
   * or photo (slice 3) gets its page image with the labels its OCR items box; a typed-in form has no page: nothing.
   */
  function withForm106Overlay(view: HTMLElement, d: { file?: string; typed?: boolean; scan?: boolean }, rows: ReturnType<typeof translationTable>['rows'], i: number, lang: Lang): HTMLElement {
    if (!__TRANSLATED_OVERLAY__) return view;
    // The upload it was read from, by the forms' order (two files can share a name).
    const at = d.typed ? -1 : formsCounted()[i] ?? -1;
    const kind = eng().overlayCaseOf({ kind: 'form106', ...(d.typed ? { typed: true } : {}), ...(d.scan ? { scan: true } : {}) });
    if (kind !== 'overlay') return withNotYet(view, kind, `tv-f${i}`, at, lang, true);
    const file = at >= 0 ? state.files[at] : undefined;
    const pages = at >= 0 ? state.uploads?.[at]?.pages : undefined;
    if (!file || !pages || !state.f106) return view;
    // A scan or photo (slice 3): each page image the OCR read, its labels boxed from its OCR items (pixels of that image),
    // page by page (a row boxed on one page keeps that box); without the images in memory, the note.
    const pictures = d.scan ? scanPictures.get(file) : undefined;
    if (d.scan && !pictures?.size) return withNotYet(view, 'notYet.scan', `tv-f${i}`, at, lang, true);
    const boxed = pictures
      ? [...pictures].sort(([a], [b]) => a - b).reduce((r, [number, pic]) => eng().scanBoxesAllPhrases(pic.items, r, { number, width: pic.gray.width, height: pic.gray.height, pointsPerPixel: pic.pointsPerPixel }), [...rows])
      : eng().form106Boxed(rows, state.f106.form106LabelBoxes(pages), pages);
    // Every page with a label placed, stacked (page 2 of a Form 106 too), each with its own overlay, numbers and key.
    const pageNumbers = eng().overlayPages(boxed);
    // No label placed (a layout the reader found no label item for): no box, the line boxes have every line.
    if (!pageNumbers.length) return view;
    if (!overlayMod) {
      import('../translate/overlay').then((m) => {
        overlayMod = m;
        renderKeepingFocus();
      }, () => undefined);
      return view;
    }
    const mod = overlayMod;
    // The first page keeps the single-page ids and keys; later pages add "-p<n>".
    // placeBox puts each box just above the line boxes, so ascending calls stack in page order.
    return pageNumbers.reduce((v, pageNumber, k) => {
      const first = k === 0;
      const picture = pictures?.get(pageNumber);
      const suffix = first ? '' : `-p${pageNumber}`;
      return placeBox(v, overlayBox(v, mod, { key: `tvo-tv-f${i}${suffix}`, id: `tv-f${i}${suffix}`, rows: boxed, pageNumber, file, lang, password: state.passwords.get(at), form106: true, ...(pageNumbers.length > 1 ? { numbered: true as const } : {}), format: picture ? 'scan' : 'form106', ...(picture ? { picture } : {}) }));
    }, view);
  }

  /** The overlay's box goes above the view's line boxes (or at its end in a view without them). */
  function placeBox(view: HTMLElement, box: HTMLElement): HTMLElement {
    const boxes = view.querySelector('.tvd-boxes');
    if (boxes) boxes.before(box);
    else view.append(box);
    return view;
  }

  /**
   * The same box for a document whose page the view can't show with translated labels yet (Matt G24): opened, it says
   * so (the line boxes below have every line), then the anonymous-layout offer for that file. Never an empty or half page.
   */
  function withNotYet(view: HTMLElement, kind: string, id: string, at: number, lang: Lang, form106 = false): HTMLElement {
    if (!kind.startsWith('notYet.') || at < 0) return view;
    // The offer only where the report carries the files' layout (a session with files read; a Form 106 alone builds a
    // report without one, src/ui/app.ts buildDiagnosticReport): elsewhere the note alone, never a promise it can't keep.
    const offer = diagnosticInput()?.input === 'digitalPdf';
    const key = `tvo-${id}`;
    const chevron = view.querySelector('.tvd-box > summary > svg:last-child')?.cloneNode(true);
    const box = el('details', { class: 'fd-card tvd-card tvd-box tvo tvo-not-yet', id: `${id}-overlay` },
      el('summary', { class: 'fold-own', 'aria-label': t(lang, form106 ? 'tvO.heading.form106' : 'tvO.heading') }, el('span', { class: 'tvd-box-name' }, t(lang, 'tvO.short')), ...(chevron ? [chevron] : [])),
      offer ? layoutOffer({ id: `${id}-not-yet`, text: [t(lang, `tvO.${kind}`), t(lang, 'tvO.notYet.send')], indices: [at], sendLabel: t(lang, 'readyD.unknownSend') }) : el('div', { class: 'layout-offer' }, el('p', {}, t(lang, `tvO.${kind}`))));
    box.open = state.moreOpen.has(key);
    box.addEventListener('toggle', () => (box.open ? state.moreOpen.add(key) : state.moreOpen.delete(key)));
    return placeBox(view, box);
  }

  /** The translation view's languages names and formatting, as the view needs them (design D). */
  function translateSpec(table: ReturnType<typeof translationTable>, payMonth: string | undefined, lang: Lang, extra: Partial<Parameters<NonNullable<typeof tvD>['translateView']>[0]> = {}): Parameters<NonNullable<typeof tvD>['translateView']>[0] {
    return { ...table, ...(payMonth ? { monthName: namedMonth(payMonth) } : {}), languageName: LANGUAGES[lang].nativeName, lang, explainShekel: lang !== 'he', onChangeLanguage: () => {
      state.langMenuOpen = true;
      render();
      document.querySelector<HTMLElement>('#lang-menu [aria-current="true"]')?.focus();
    }, ...extra };
  }

  /** What the payslip translation's table (src/translate) needs for payslip `i`: its rows, the page's other text, the formatting. */
  function translationTable(p: Analysed, i: number, lang: Lang) {
    const vendor = p.source?.vendor ?? eng().MANUAL_SOURCE;
    const number = new Intl.NumberFormat(LANGUAGES[lang].locale, { maximumFractionDigits: 2 });
    const read = eng().translatedRows(p.fields, vendor);
    // A digital PDF's page text is in memory: the rest of the printed page is listed too (your details, printed labels,
    // codes), and what still isn't is shown as printed. A scan's isn't (OCR text the user didn't confirm), so it keeps
    // the "only the lines read" note.
    // By the upload it was read from (two files can share a name), else by name.
    const upload = p.source?.fileIndex !== undefined ? state.uploads?.[p.source.fileIndex] : state.uploads?.find((u) => u.name === p.source?.file);
    const page = p.source && !p.ocr ? upload?.pages[p.source.page - 1] : undefined;
    // A label the page prints for a field already shown with its value is not listed again, but its text still counts as read.
    const onPage = page && p.source ? eng().pageRowsFor(p.source.vendor, page, read, true) : [];
    const kept = eng().withoutDuplicateLabels(read, onPage);
    // The translated-overlay view (G11 slice 1, on since Matt G24): each printed label's place on its page.
    const both = [...kept.read, ...kept.page];
    // A scan or photo (slice 3): the boxes from the OCR items of its page, in pixels of the page image the overlay draws.
    const scanFile = __TRANSLATED_OVERLAY__ && p.ocr && p.source ? state.files[p.source.fileIndex ?? state.uploads?.findIndex((u) => u.name === p.source?.file) ?? -1] : undefined;
    const picture = scanFile && p.source ? scanPictures.get(scanFile)?.get(p.source.page) : undefined;
    const rows = !__TRANSLATED_OVERLAY__ || !p.source ? both
      : page ? eng().labelBoxesFor(p.source.vendor, page, both, p.source.page)
        : picture ? eng().scanBoxesFor(picture.items, both, { number: p.source.page, width: picture.gray.width, height: picture.gray.height, pointsPerPixel: picture.pointsPerPixel })
          : both;
    const other = page && p.source ? eng().otherTextOf(p.source.vendor, page, [...read, ...onPage]) : [];
    const note = !p.source ? 'tv.typedIn' : page ? 'tv.onlyReadDigital' : 'tv.onlyRead';
    return {
      el, tr: (key: string, params?: Record<string, string | number>) => t(lang, key, params), money: (a: number) => formatMoney(a, lang), month: formatMonth, date: formatDate, number: (n: number) => number.format(n),
      rows, id: `tv-${i}`, note, otherText: other,
    };
  }

  /**
   * The translate flow's result (Matt 2026-10-23 22:59): every payslip and Form 106 read, each line as the Hebrew label
   * as printed, its translation, what it means and the value, then "Want us to check your pay too?" (the files carry over).
   */
  function renderTranslation(lang: Lang): HTMLElement[] {
    const payslipCount = state.result && 'payslips' in state.result ? state.result.payslips.length : 0;
    const one = payslipCount + form106Docs().length === 1;
    // One document: its own title is the page's heading ("Your payslip in English"); several: this heading, then each document's.
    const heading = el('h2', { id: 'results-heading', tabindex: '-1', class: 'fd-title' }, t(lang, 'tvT.heading'));
    if (!state.tv || !tvD) {
      void loadTranslation().then(() => render());
      return [el('section', { class: 'rv fd fd-screen', 'aria-busy': 'true' }, heading)];
    }
    const view = tvD;
    const payslips = state.result && 'payslips' in state.result ? state.result.payslips : [];
    const forms = form106Docs();
    const several = payslips.length + forms.length > 1;
    const number = new Intl.NumberFormat(LANGUAGES[lang].locale, { maximumFractionDigits: 2 });
    const parts: HTMLElement[] = [
      ...payslips.flatMap((p, i) => [
        ...(several ? [el('h3', { class: 'payslip-heading' }, p.fields.payMonth ? t(lang, 'results.payslipHeading', { month: namedMonth(p.fields.payMonth) }) : t(lang, 'results.payslipHeadingNotFound'),
          p.source ? el('span', { class: 'source' }, ' · ', el('bdi', {}, p.source.file)) : '')] : []),
        ((table) => withOverlay(view.translateView(translateSpec(table, p.fields.payMonth, lang, { plain: true, ...(several ? { titleLevel: 'h4' as const } : { titleLevel: 'h2' as const, titleId: 'results-heading' }) })), p, i, lang, table.rows))(translationTable(p, i, lang)),
      ]),
      ...forms.flatMap((d, i) => [
        ...(several ? [el('h3', { class: 'payslip-heading' }, d.fields.taxYear ? t(lang, 'f106.heading', { year: String(d.fields.taxYear) }) : t(lang, 'f106.headingNoYear'),
          d.file ? el('span', { class: 'source' }, ' · ', el('bdi', {}, d.file)) : '')] : []),
        ((rows) => withForm106Overlay(view.translateView(translateSpec({
          el, tr: (key: string, params?: Record<string, string | number>) => t(lang, key, params), money: (a: number) => formatMoney(a, lang), month: formatMonth, date: formatDate, number: (n: number) => number.format(n),
          rows, id: `tv-f${i}`, note: d.typed ? 'tv.typedIn' : 'tv.onlyRead106', otherText: [],
        }, undefined, lang, { plain: true, kind: 'form106', ...(several ? { titleLevel: 'h4' as const } : { titleLevel: 'h2' as const, titleId: 'results-heading' }) })), d, rows, i, lang))(eng().form106Rows(d.fields)),
      ]),
    ];
    // "Want us to check your pay too?": the same files, now a check: "Who are you?" next (nothing is asked twice, nothing re-added).
    // Every Form 106 from before the refund window and no payslip: no check is possible, said here (told on the step where we know).
    const tooOld = payslipsRead() ? undefined : formsAllTooOld();
    const { taxYear: firstYear, years } = deadlineYears(new Date());
    const check = Object.assign(button(t(lang, payslips.length === 1 && forms.length === 0 ? 'tvT.checkToo.buttonPayslip' : 'tvT.checkToo.button'), () => {
      state.translateAsked = false;
      state.translateFirst = false;
      checkInstead();
      // A Form 106 alone is the Form 106 check; anything with payslips is the payslip check (it takes the forms too).
      state.flow.goal = hasForm106() && !payslipsRead() ? 'form106' : 'month';
      goTo('who');
    }), { id: 'translate-check-too' });
    return [
      el('section', { class: 'rv fd fd-screen translate-result', 'aria-live': 'polite' }, ...(one ? [] : [heading]), ...parts),
      el('section', { class: 'rv fd fd-screen translate-check-too', 'aria-labelledby': tooOld !== undefined ? 'translate-form106-tooold' : 'check-too-heading' },
        el('div', { class: 'fd-card' },
          ...(tooOld !== undefined
            // No check is possible: the reason and the way on (a newer form can be added right here and is translated too), not the question.
            ? [el('p', { id: 'translate-form106-tooold' }, t(lang, 'form106.yearTooOld', { year: String(tooOld), years, firstYear: String(firstYear) })), el('p', {}, t(lang, 'next.cantCheck.yearTooOld')), el('div', { class: 'fd-actions' }, Object.assign(button(t(lang, 'upload.chooseMore'), () => document.getElementById('pdf-files')?.click(), 'secondary'), { id: 'translate-add-file' }))]
            : [el('h3', { id: 'check-too-heading' }, t(lang, 'tvT.checkToo.heading')), el('p', {}, t(lang, 'tvT.checkToo.text')), el('div', { class: 'fd-actions' }, check)]))),
    ];
  }

  /** The approved privacy line with its shield, at the end of the results (UX-1; "turn off" links to the counter switch). */
  function privacyFooter(): HTMLElement {
    return el('div', { class: 'rv-privacy' }, state.rv ? state.rv.icon('shield-check') : '', el('p', {}, ...privacyLine((k) => t(state.lang, k))));
  }

  /**
   * Above the results: what was checked (the scope lines), the short disclaimer ("worth checking", not "owed") with a
   * link to the full page, and when our rules data was last checked.
   */
  /** "…last reviewed on 9 October 2026.": the date in a no-break span, so day, month and year never split across lines. */
  function rulesCheckedLine(date: string): HTMLElement {
    const full = t(state.lang, 'results.rulesChecked', { date });
    const at = full.indexOf(date);
    return at < 0 ? el('p', { class: 'rules-checked' }, full)
      : el('p', { class: 'rules-checked' }, full.slice(0, at), el('span', { class: 'nobreak' }, date), full.slice(at + date.length));
  }
  function legalHeader(lines: readonly string[]): HTMLElement {
    const reviewed = eng().rulesReviewedOn(eng().defaultParams);
    const last = lines.length - 1;
    return el('div', { class: 'legal-minimums' },
      ...lines.slice(0, last).map((l) => el('p', {}, l)),
      el('p', {}, lines[last] ?? '', ' ', el('a', { href: '#/disclaimer' }, t(state.lang, 'results.fullDisclaimer'))),
      reviewed ? rulesCheckedLine(formatDateWords(reviewed, state.lang)) : '');
  }

  /**
   * `fix`: the box under a "can't check" result that asks for what's missing (src/ui/missingInfo.ts), for the payslip or
   * Form 106 the results belong to. Never a dead end: can't-check results sit on the main list with their remedy.
   */
  function renderView(view: ReturnType<typeof Engine.resultsView>, outerLevel: 'h3' | 'h4' | 'h5', scope: string, fix?: Fix, doc: 'payslip' | 'form106' = 'payslip', advice = true, withTail = true, month?: string): (Node | string)[] {
    const level = outerLevel;
    switch (view.kind) {
      case 'none':
        return [el('p', { class: 'empty-state' }, view.text)];
      case 'errors':
        return [el('div', { class: 'errors', role: 'alert' }, el('p', {}, view.heading), el('ul', {}, ...view.errors.map((e) => el('li', { dir: 'ltr' }, e))))];
      case 'noPay':
        return [legalHeader(view.header), el('p', { class: 'finding cannot_check' }, view.text)];
      case 'findings': {
        const card = (c: (typeof view.cards)[number], i: number, level: 'h3' | 'h4' | 'h5' = outerLevel) =>
          el(
            'article',
            // Scope and index: where the card is after a rerun (the focus follows it: src/ui/missingInfo.ts focusCard).
            { class: `finding ${c.status}`, 'data-scope': scope, 'data-index': String(i), ...Object.fromEntries(Object.entries(c.data).map(([k, v]) => [`data-${k}`, v])) },
            el('div', { class: 'finding-head' }, el('div', { class: 'finding-id' }, el('span', { class: 'finding-icon', 'aria-hidden': 'true' }, STATUS_ICON[c.status]), el(level, {}, c.title)), el('span', { class: 'badge' }, c.badge)),
            el('p', {}, c.message),
            c.amount ? el('p', { class: 'amount' }, el('span', { class: 'amount-label' }, c.amount.label), ' ', el('bdi', { class: 'amount-value' }, c.amount.value)) : '',
            ...c.notes.map((n) => el('p', { class: 'note' }, n)),
            remedyView(c, `${scope}-${i}`, level === 'h3' ? 4 : level === 'h4' ? 5 : 6, fix),
            c.confidence ? el('p', { class: 'confidence' }, c.confidence) : '',
            renderMoreInfo(c.more, `${scope}-${i}`, level === 'h3' ? 'h4' : level === 'h4' ? 'h5' : 'h6'),
            // What was checked matters where something is worth checking; the header says it once for the rest.
            c.status === 'verify' ? el('p', { class: 'note legal-minimum' }, c.footer) : '',
          );
        // What's worth checking first, in full; what looks fine or couldn't be checked, one tap away.
        // Can't-check results with something to do (answer, add a file, the next step) come next, never folded away.
        const onMain = (c: (typeof view.cards)[number]) => c.status === 'verify' || (c.status === 'cannot_check' && c.remedy !== undefined && c.remedy.kind !== 'info');
        const worth = view.cards.map((c, i) => [c, i] as const).filter(([c]) => onMain(c)).sort(([a], [b]) => (a.status === 'verify' ? 0 : 1) - (b.status === 'verify' ? 0 : 1));
        const rest = view.cards.map((c, i) => [c, i] as const).filter(([c]) => !onMain(c));
        const restKey = `${scope}-rest`;
        const details = el('details', { class: 'other-findings' },
          el('summary', {}, el('span', {}, t(state.lang, 'results.alsoChecked', { count: rest.length }), ' ',
            // A counted list: each item carries its own "·" (styles.css .inline-list), so a wrap never costs one.
            el('span', { class: 'help inline-list', role: 'list' }, ...rest.flatMap(([c], n) => [n ? ' ' : '', el('span', { class: 'inline-list-item', role: 'listitem' }, c.title)])))),
          ...rest.map(([c, i]) => card(c, i)));
        details.open = state.moreOpen.has(restKey);
        details.addEventListener('toggle', () => (details.open ? state.moreOpen.add(restKey) : state.moreOpen.delete(restKey)));
        // The UX-1 screen (src/results; mockup D): the summary card, one detail card per finding, the rest as before.
        const rvMod = state.rv;
        if (doc === 'payslip' && rvMod) {
          const model = rvMod.buildModel(state.lang, view.cards, scope, month);
          return rvMod.renderResults({
            el, tr: (k, p) => t(state.lang, k, p), level, scope, model, compact: underForm106,
            renderCard: (c, i) => card(c, i),
            renderFolded: () => (rest.length ? details : ''),
            // The "Please check" note to payroll: preview builds only (docs/ux/ux1-notes.md refinement 2).
            ...(__BETA__ && state.note ? { renderAction: (det: import('../results').Detail) => state.note?.renderNote(el, (k, p) => t(state.lang, k, p), det) ?? '' } : {}),
            renderWhy: (det, lv) => renderMoreInfo(det.card.more, `${scope}-${det.cardIndex}`, lv, { label: t(state.lang, 'rv.why'), before: [el('p', {}, det.card.message)] }),
            isOpen: (id) => state.moreOpen.has(id),
            setOpen: (id, open) => (open ? state.moreOpen.add(id) : state.moreOpen.delete(id)),
            // Under the cards: what was checked and the disclaimers, then the privacy line. Several payslips share one set at the end of the page.
            tail: withTail ? [
              legalHeader(view.header),
              el('p', { class: 'help disclaimer' }, view.disclaimer),
              advice ? el('p', { class: 'help disclaimer' }, t(state.lang, 'brand.footer')) : '',
              privacyFooter(),
            ] : [],
          });
        }
        // At a glance: how many of each, with the icon and the words (never colour alone).
        const counts = (['verify', 'ok', 'cannot_check'] as const).map((s) => [s, view.cards.filter((c) => c.status === s).length] as const).filter(([, n]) => n > 0);
        const summary = el('ul', { class: 'results-summary', 'aria-label': t(state.lang, 'results.summaryLabel') },
          ...counts.map(([s, n]) => el('li', { class: s }, el('span', { 'aria-hidden': 'true' }, STATUS_ICON[s]), `${t(state.lang, `results.status.${s}`)}: `, el('bdi', { class: 'num' }, String(n)))));
        return [
          summary,
          legalHeader(view.header),
          nothingWorthChecking(eng().nothingFoundKey(view.cards.map((c) => c.status), doc)),
          ...worth.map(([c, i]) => card(c, i)),
          rest.length ? details : '',
          el('p', { class: 'help disclaimer' }, view.disclaimer),
          // The advice line once per page: below a Form 106's results, the form's block shows it.
          advice ? el('p', { class: 'help disclaimer' }, t(state.lang, 'brand.footer')) : '',
        ];
      }
    }
  }

  /**
   * "Nothing worth checking was found in this payslip" names the payslip: a Form 106's results use their own sentence
   * (`results.nothingWorthChecking.form106`, proposed in docs/review/doc-type-audit.md, waiting for the text rewrite) and,
   * until it exists, none: the summary above already counts the results by status. Never "nothing found" when no check
   * compared anything (`results.nothingChecked`, resultsView.ts nothingFoundKey; false-reassurance audit).
   */
  function nothingWorthChecking(key: string | undefined): HTMLElement | '' {
    return key && hasMessage('en', key) ? el('p', { class: 'nothing-worth-checking' }, t(state.lang, key)) : '';
  }

  /** A can't-check result's remedy (src/model/remedies.ts): the fill-in box, a way to add a file, or the next step. */
  function remedyView(c: { remedy?: { kind: string; next?: string; missing?: string[]; link?: { href: string; text: string } } }, id: string, level: 4 | 5 | 6, fix?: Fix): HTMLElement | '' {
    const r = c.remedy;
    if (!r) return '';
    if (r.kind === 'explain' && r.next) {
      return el('p', { class: 'next-step' }, el('strong', {}, t(state.lang, 'results.nextStep')), ' ', r.next,
        ...(r.link ? [' ', el('a', { href: r.link.href, target: '_blank', rel: 'noopener noreferrer' }, r.link.text)] : []));
    }
    if (r.kind === 'addFiles') {
      const add = button(t(state.lang, 'results.addFile'), () => goTo('upload'), 'secondary');
      add.id = `${id}-add`;
      return el('div', { class: 'row next-step' }, add);
    }
    if (r.kind === 'ask' && r.missing && fix) return fix(r.missing, `${id}-fix`, level);
    return '';
  }

  /** The fill-in box for one payslip's missing values (by `file#page`, or MANUAL_FIX), validated like OCR values. */
  function payslipFix(key: string, fields: PayslipFields): Fix {
    return (missing, id, level) => eng().missingInfo({
      el, tr: (k, p) => t(state.lang, k, p), id, level, missing, current: fields as Record<string, unknown>, answer: (k, name) => askOnce(k, name, level === 4 ? 5 : 6, `payslip:${key}`, id),
      monthName, onUse: (values) => usePayslipFix(key, fields, values),
    });
  }

  /**
   * The fill-in box for a Form 106 year's missing values (the months worked from the picker, prefilled with what was
   * read; tax year, salary, tax…): each goes to the year's first form without it; checked like a typed-in 106.
   */
  /** The box a typed-in Form 106 problem is about, in the fill-in box (its keys). */
  const FIX_PROBLEM_FIELD: Record<string, string> = { taxYear: 'taxYear', months: 'monthsWorked', salary: 'salary', taxWithheld: 'taxWithheld', taxAboveSalary: 'taxWithheld', creditPoints: 'creditPoints' };
  function form106Fix(forms: readonly number[]): Fix {
    return (missing, id, level) => {
      const docs = form106Docs();
      const first = docs[forms[0] ?? 0]?.fields ?? {};
      // What was read but not for sure starts the boxes (months from a scan, a value that looks unusual).
      const review = state.f106?.form106Review(first) ?? [];
      // A number already asked in its form's "Check these numbers" box (form106ReviewBox) isn't asked a second time
      // here: a button takes the person to that box.
      const boxOf = (k: string) => forms.find((i) => {
        const d = docs[i];
        return d !== undefined && !('typed' in d) && (state.f106?.form106Review(d.fields).some((it) => it.key === k) ?? false);
      });
      const inBox = missing.filter((k) => boxOf(k) !== undefined);
      const here = missing.filter((k) => !inBox.includes(k));
      const box = here.length ? eng().missingInfo({
        el, tr: (k, p) => t(state.lang, k, p), id, level, missing: here, current: state.f106?.withSuggestions(first, review) ?? (first as Record<string, unknown>),
        suggested: suggestedOf(review, here),
        // Other income that year: its question right in the box (persona walk P26, 2026-10-23: only a button to the questions step).
        answer: (k, name) => (k === 'incomeSources' ? incomeSourcesHere(first.taxYear, name, level === 4 ? 5 : 6) : askOnce(k, name, level === 4 ? 5 : 6, 'form106', id)),
        monthName, monthsRow: monthsRowOf(forms[0] ?? 0),
        onUse: (values) => useForm106Fix(forms, values),
      }) : '';
      const target = inBox.length ? boxOf(inBox[0] ?? '') : undefined;
      // A link to the box's field (the ux-expert's proposal D: a link, not a second big button).
      const link = el('a', { href: `#f106-review-${target ?? 0}-heading`, id: `${id}-to-review` },
        t(state.lang, 'f106.review.goTo', { count: inBox.length, fields: new Intl.ListFormat(state.lang, { type: 'conjunction' }).format(inBox.map((k) => t(state.lang, `f106.short.${k}`))) }));
      link.addEventListener('click', (e) => {
        const heading = document.getElementById(`f106-review-${target ?? 0}-heading`);
        if (!heading) return;
        e.preventDefault();
        heading.tabIndex = -1;
        heading.scrollIntoView?.({ block: 'center' });
        heading.focus();
      });
      const toBox = target === undefined ? '' : el('p', { class: 'next-step' }, link);
      return box || toBox ? el('div', {}, box, toBox) : '';
    };
  }

  /** Prefills for a Form 106's fill-in boxes: what was read but not for sure, each marked why (`keys`: the boxes shown). */
  function suggestedOf(items: readonly Form106ReviewItem[], keys: readonly string[]): NonNullable<Engine.MissingInfoSpec['suggested']> {
    const prefill: Record<string, Engine.TypedValue> = {};
    const flags: Record<string, 'unsure' | 'unusual'> = {};
    const notes: Record<string, string> = {};
    for (const it of items) {
      if (it.suggestion === undefined || !keys.includes(it.key)) continue;
      Object.assign(prefill, prefillOf(it.key, it.suggestion));
      flags[it.key] = it.why === 'unusual' ? 'unusual' : 'unsure';
      if (it.fromTotal) notes[it.key] = t(state.lang, 'f106.pointsFromTotalHow', { ...it.fromTotal, points: it.suggestion as number });
    }
    return { prefill, flags, notes };
  }

  /**
   * A Form 106's numbers to check, on its results (Matt, 2026-10-18: never "some numbers were left out" without saying
   * which): each by name with its field number, what was read prefilled and marked, the checks that can't use them
   * meanwhile, and "Check again" (the checks rerun). Typed-in forms: none (the person typed what they have).
   */
  function form106ReviewBox(i: number, lang: Lang): HTMLElement | '' {
    const f106 = state.f106;
    const d = form106Docs()[i];
    if (!f106 || !d || d.typed) return '';
    const items = f106.form106Review(d.fields);
    if (!items.length) return '';
    const tr = (k: string, p?: Record<string, string | number>) => t(lang, k, p);
    const labels = Object.fromEntries(f106.form106ValueRows(el, tr, (a) => formatMoney(a, lang), {}).map((r) => [r.key, r.label]));
    const payslips = state.result && 'payslips' in state.result ? state.result.payslips : [];
    const december = d.fields.taxYear !== undefined && payslips.some((p) => p.fields.payMonth === `${d.fields.taxYear}-12`);
    const checks = f106.checksWithout(items, d.fields, { estimate: __FORM106_ESTIMATE__, december });
    const keys = items.map((it) => it.key);
    const box = eng().missingInfo({
      el, tr, id: `f106-review-${i}`, level: 4, missing: keys, answer: () => undefined, monthName, labels, suggested: suggestedOf(items, keys), monthsRow: monthsRowOf(i),
      // The months' other marks (asked) and faint cells (highlighted) come with the form's fields.
      current: f106.withSuggestions(d.fields, items),
      texts: { heading: tr('f106.review.heading'), help: tr('f106.review.help'), use: tr('f106.review.use') },
      // Not printed on the form: not asked again; the checks that need it say so (form106.notOnForm).
      notOnForm: (k) => {
        const fix: Partial<Form106Fields> = { ...state.form106Fixes.get(i) };
        fix.notOnForm = [...new Set([...(fix.notOnForm ?? []), k])];
        state.form106Fixes.set(i, fix);
        render();
        const heading = document.getElementById(`f106-review-${i}-heading`) ?? document.getElementById(`form106-r${i}`);
        if (heading) {
          heading.tabIndex = -1;
          heading.focus();
        }
      },
      onUse: (values) => {
        const problems = useForm106Fix([i], values);
        // Rerun: the box is gone (or smaller); the focus goes to this form's results, which changed (WCAG 2.4.3).
        if (!problems.length) {
          const heading = document.getElementById(`form106-r${i}`);
          if (heading) {
            heading.tabIndex = -1;
            heading.focus();
          }
        }
        return problems;
      },
    });
    if (!box) return '';
    // Which checks wait for these numbers: small print under the button (Matt, 2026-10-19, item 5).
    if (checks.length) box.querySelector('.missing-actions')?.after(el('p', { class: 'note review-checks', id: `f106-review-${i}-checks` }, tr('f106.review.checksWaiting', { checks: new Intl.ListFormat(lang, { type: 'conjunction' }).format(checks.map((c) => tr(`f106.check.${c}`))) })));
    return box;
  }

  /** Typed values for Form 106s (`forms`: one tax year's, or one form's): each to the first form without it, checked like a typed-in 106. */
  function useForm106Fix(forms: readonly number[], values: Record<string, Engine.TypedValue>): string[] {
    const docs = form106Docs();
    const problems: string[] = [];
    for (const [k, v] of Object.entries(values)) {
      const target = forms.find((i) => (docs[i]?.fields as Record<string, unknown> | undefined)?.[k === 'monthsWorked' ? 'months' : k] === undefined) ?? forms[0] ?? 0;
      const fix: Partial<Form106Fields> = { ...state.form106Fixes.get(target) };
      const text = typeof v === 'string' ? v.replace(/[₪\s,]/g, '') : '';
      if (Array.isArray(v)) {
        if (!v.length) problems.push(`${k}|missing.problem.months`);
        else Object.assign(fix, { months: v, monthsWorked: v.length });
      } else if (MISSING_INPUTS[k] === 'money') {
        const a = eng().parseAgorot(text);
        if (a === undefined || a < 0) problems.push(`${k}|missing.problem.money`);
        else Object.assign(fix, { [k]: a });
      } else {
        const n = Number(text);
        if (text === '' || !Number.isFinite(n)) problems.push(`${k}|missing.problem.count`);
        else Object.assign(fix, { [k]: n });
      }
      // The same checks as a typed-in Form 106 (src/form106/fields.ts manualProblems).
      const merged = { ...docs[target]?.fields, ...fix };
      const bad = state.f106?.manualProblems(merged, new Date().getFullYear()) ?? [];
      if (bad.length) problems.push(...bad.map((b) => `${FIX_PROBLEM_FIELD[b] ?? k}|f106.problem.${b}`));
      else if (!problems.length) {
        // A value entered for a number marked "Not on my form": it is on the form after all.
        if (fix.notOnForm?.includes(k)) fix.notOnForm = fix.notOnForm.filter((x) => x !== k);
        state.form106Fixes.set(target, fix);
      }
    }
    if (problems.length) return [...new Set(problems)];
    render();
    return [];
  }

  /** "Check your answers": a payslip value as shown (undefined: not read). */
  function shownValue(key: (typeof CONFIRM_ROWS)[number], f: PayslipFields): string | undefined {
    const lang = state.lang;
    const tr = (k: string, params?: Record<string, string | number>) => t(lang, k, params);
    const num = (n: number | undefined) => (n === undefined ? undefined : new Intl.NumberFormat(lang === 'he' ? 'he-IL' : 'en-US').format(n));
    switch (key) {
      case 'payMonth': return f.payMonth && `${Number(f.payMonth.slice(5))}/${f.payMonth.slice(0, 4)}`;
      case 'startDate': return f.startDate && formatDate(f.startDate);
      // An answer overrides what was read (src/model/payBasis.ts).
      case 'payBasis': {
        const b = state.answers.payBasis ?? f.payBasis?.value;
        return b && tr(`questions.payBasis.${b}`);
      }
      case 'jobPercent': {
        const j = state.answers.jobPercent ?? f.jobPercent;
        return j === undefined ? undefined : `${num(j)}%`;
      }
      case 'recuperationDays': return num(f.recuperationDays);
      case 'hoursPaid': return num(f.hoursPaid ?? f.hoursWorked);
      case 'daysWorked': return num(f.daysWorked);
      case 'vacationBalance':
      case 'sickBalance': {
        const b = f[key];
        return b && tr('cya.balance', { opening: num(b.opening) ?? '–', used: num(b.used) ?? '–', closing: num(b.closing) ?? '–' });
      }
      default: {
        const a = f[key];
        return typeof a === 'number' ? formatMoney(a, lang) : undefined;
      }
    }
  }

  /** "Compare with the picture again": a scan page back to its review on the upload step (its crops, the gate). */
  function ocrAgain(file: string, page: number): HTMLElement | '' {
    const index = (state.uploads ?? []).findIndex((u) => u.name === file);
    const p = state.ocr.get(index)?.pages.find((x) => x.page === page);
    if (!p) return '';
    const b = button(t(state.lang, 'cya.ocrAgain'), () => {
      p.confirmed = false;
      if (state.uploads) analyseUploads(state.uploads);
      goTo('upload');
      document.getElementById(`ocr-${index}-${state.ocr.get(index)?.pages.indexOf(p) ?? 0}-grossPay`)?.focus();
    }, 'secondary');
    b.id = `confirm-ocr-${index}-${page}`;
    return el('div', { class: 'row' }, b);
  }

  /** Typed values for a payslip: parsed like OCR values, plausibility-checked (pension), then the checks run again. */
  function usePayslipFix(key: string, fields: PayslipFields, values: Record<string, Engine.TypedValue>, kinds?: Record<string, Engine.FieldInput>): string[] {
    const fix: Partial<PayslipFields> = {};
    const problems: string[] = [];
    for (const [k, v] of Object.entries(values)) {
      const kind = kinds?.[k] ?? MISSING_INPUTS[k];
      const text = typeof v === 'string' ? v.replace(/[₪\s]/g, '') : '';
      if (kind === 'leaveBalance' && typeof v === 'object' && !Array.isArray(v)) {
        // Days as printed (decimals allowed): the balance before, the days used (0 if none), the balance now. All three:
        // the month's accrual is worked out from them (src/rules/leaveBalance.ts).
        const day = (s: string | undefined) => (s === undefined || s.trim() === '' ? undefined : Number(s.replace(/[,\s]/g, '')));
        const [opening, used, closing] = [day(v.opening), day(v.used), day(v.closing)];
        if (opening === undefined || used === undefined || closing === undefined || [opening, used, closing].some((n) => !Number.isFinite(n) || n < 0 || n > 400)) problems.push(`${k}|missing.problem.leave`);
        else (fix as Record<string, unknown>)[k] = { opening, used, closing };
      } else if (kind === 'money') {
        const a = eng().parseAgorot(text);
        if (a === undefined || a < 0) problems.push(`${k}|missing.problem.money`);
        else (fix as Record<string, unknown>)[k] = a;
      } else if (kind === 'count') {
        // "1,200" and "1200" alike (design-conventions audit).
        const n = Number(text.replace(/,/g, ''));
        if (!Number.isFinite(n) || n < 0 || text === '') problems.push(`${k}|missing.problem.count`);
        else (fix as Record<string, unknown>)[k] = n;
      } else if (kind === 'month') {
        if (!/^\d{4}-(0[1-9]|1[0-2])$/.test(text)) problems.push(`${k}|missing.problem.month`);
        else (fix as Record<string, unknown>)[k] = text;
      } else if (kind === 'date') {
        if (!isIsoDate(text)) problems.push(`${k}|missing.problem.date`);
        else (fix as Record<string, unknown>)[k] = text;
      }
    }
    if (problems.length) return [...new Set(problems)];
    // As for a scan's values: a pension figure outside the plausible range, or an amount that doesn't fit the rest of the
    // payslip (tax, National Insurance… above their share of the gross), is checked again before it's used.
    const merged = { ...fields, ...fix };
    const unusualAmounts = eng().unusualFieldValues(merged, eng().defaultParams, { scan: false }).filter((k) => MISSING_INPUTS[k] === 'money');
    const implausible = [...eng().implausibleInFields(merged, eng().defaultParams), ...unusualAmounts].filter((k) => k in fix);
    const warned = state.fixWarned.get(key) ?? '';
    const sig = JSON.stringify(fix);
    if (implausible.length && warned !== sig) {
      state.fixWarned.set(key, sig);
      return implausible.map((k) => `${k}|missing.problem.implausible`);
    }
    state.fixWarned.delete(key);
    state.fieldFixes.set(key, { ...state.fieldFixes.get(key), ...fix });
    if (state.uploads) analyseUploads(state.uploads);
    else check();
    render();
    return [];
  }

  /** Something to check: files, the manual form opened, pasted data, or results. */
  const hasInput = () => state.files.length > 0 || state.pending.length > 0 || state.manualOpen || state.source === 'json' || state.result !== undefined || hasForm106();

  const payslipsRead = () => state.result !== undefined && 'payslips' in state.result && state.result.payslips.length > 0;

  /** The no-text pages of file `index` still waiting for the offer (src/ui/fileList.ts noTextPagesOf). */
  const noTextPagesAt = (index: number): number[] => {
    const u = state.uploads?.[index];
    return u ? noTextPagesOf(u, state.ocr.get(index)?.status) : [];
  };

  /** The upload step needs the user before anything can be checked: reading, a scan to read or confirm, a password, nothing read. */
  const uploadNeedsUser = (): boolean => {
    if (state.pending.length > 0) return true;
    // Pages with no text in a file whose other pages were read wait for the offer; they never hold the user back.
    if ([...state.ocr].some(([i, j]) => (j.status === 'offer' && !noTextPagesAt(i).length) || j.status === 'running' || (j.status === 'review' && j.pages.some((p) => !p.confirmed)))) return true;
    // A file read by its labels whose pages are still being drawn for the confirmation screen.
    if ((state.uploads ?? []).some((u, i) => u.labelsRead && !state.ocr.has(i))) return true;
    if ((state.uploads ?? []).some((u) => u.problem === 'needsPassword' || u.problem === 'wrongPassword')) return true;
    return state.files.length > 0 && !payslipsRead() && !hasForm106();
  };

  /** The step shown: the user's step, unless the upload needs them or there is nothing to ask about yet. */
  /**
   * The public counter line with a link to the counter's switch ("How to turn off the count", #/privacy/counter), or empty
   * (nothing read yet, or no use counted). Phones show the short privacy line without a link, so the switch is reachable
   * from where the user learns checks are counted (ux-expert, 2026-10-22).
   */
  const checksLine = (): (Node | string)[] => {
    const c = state.checksTotal;
    // Either number above 0 shows the line (Matt, 2026-10-23 11:10).
    if (!c || (c.documents <= 0 && c.estimatedFound <= 0)) return [];
    return [t(state.lang, 'app.checksSoFar', { count: c.documents, amount: formatMoney(c.estimatedFound * 100, state.lang, { whole: true }) }), ' ',
      el('a', { href: '#/privacy/counter', id: 'checks-off' }, t(state.lang, 'app.checksSoFar.howToOff'))];
  };
  /** Design D's counter line on the start screen (the switch is linked from the privacy card right above it). */
  const checksLineD = (): (Node | string)[] => {
    const c = state.checksTotal;
    // Shown while either number is above 0 (Matt, 2026-10-23 11:10; claims register startD.checksSoFar).
    if (!c || (c.documents <= 0 && c.estimatedFound <= 0)) return [];
    // The two figures are marked in the text (<n>…</n>, counter.ts markedParts): each pair is one unbreakable, bold span.
    const text = t(state.lang, 'startD.checksSoFar', { count: c.documents, amount: formatMoney(c.estimatedFound * 100, state.lang, { whole: true }) });
    return markedParts(text).map((p) => (p.marked ? el('span', { class: 'fd-num' }, p.text) : p.text));
  };
  /** Design D's header link back to the start: named for the check under way. */
  // A translation names what it translates: Form 106 forms only, or payslips and forms together (each is explained).
  const translateKeyD = (): string => (!hasForm106() ? 'backD.translate' : payslipsRead() ? 'backD.translateBoth' : 'backD.translateForm106');
  const backKeyD = (): string => (state.flow.goal === 'form106' ? 'backD.form106' : state.translateAsked ? translateKeyD() : 'backD.payslip');
  /** What is complete so far, for the step bar (src/ui/flowD/model.ts barStates). */
  /**
   * "Does any of this apply to you?" is required on the payslip check goals (audit 2026-10-24, item 1, MATT GO 02:41): an
   * under-18, an apprentice or a rehabilitation worker who skips it would be checked as an ordinary adult. "None of these"
   * is the answer for everyone else. Not asked under "Another work arrangement", on the Form 106 goal or for a translation.
   */
  const statusesMissingD = (): boolean => {
    const statuses = (state.answers.specialStatuses ?? []).filter((x) => x !== 'foreignWorker' && x !== 'otherArrangement');
    return Boolean(state.flow.who) && state.flow.who !== 'other' && state.flow.goal !== 'form106' && !state.translateAsked
      && (state.flow.statusesUnanswered === true || (!state.flow.statusesAnswered && !statuses.length));
  };
  const progressD = () => ({
    about: Boolean(state.flow.who) && !statusesMissingD() && !state.flow.statusesUnanswered && !(state.flow.goal && possibleChecks(state.flow.goal, atStep('who', situation()), earlyOptions()).stop),
    // A Form 106 set that is all from before the refund window is a stop (form106StopNotice): the later steps stay shut.
    documents: (payslipsRead() || hasForm106()) && !uploadNeedsUser() && !(__FLOW_D__ && dupsUnanswered().length) && allFormsTooOld() === undefined,
    confirmed: state.source !== 'json' && state.confirmedSig === docsSig(),
  });
  /** Design D's four-step bar (About you · Payslips · Questions · Results). */
  const stepBarD = (): HTMLElement | '' => {
    const current = barStepOf(stage());
    if (!current) return '';
    return fd().stepBar({
      el, tr: (k, p) => t(state.lang, k, p), current, progress: progressD(),
      documentsKey: state.flow.goal === 'form106' ? 'stepBar.forms106' : 'stepBar.documents',
      go: (step: BarStep) => goTo(flowStepOf(step)),
    });
  };
  /**
   * "Translate my payslip" (Matt 2026-10-23 22:59): the goal card goes straight to the upload, no "Who are you?", no
   * questions, no "Check your answers"; the result is the translation. "Check my pay" ends it (translateAsked false).
   */
  const translating = (): boolean => state.translateAsked === true && state.flow.goal !== 'form106';
  /** The step after the upload's Continue: the translation, or the questions of the check. */
  const afterUpload = (): FlowStep => (translating() ? 'results' : 'questions');
  const stage = (): FlowStep => {
    const { step } = state.flow;
    if (translating() && step !== 'goal') {
      // Only the upload and the translation exist on this path (the other steps' addresses land on one of them).
      if (uploadNeedsUser() || !hasInput() || (__FLOW_D__ && dupsUnanswered().length)) return 'upload';
      return step === 'upload' ? 'upload' : !payslipsRead() && !hasForm106() ? 'upload' : 'results';
    }
    // Back/Forward, "Edit" and the start screen all pass here: a goal that is already impossible never reaches the upload,
    // the confirm step or the results (docs/specs/early-stop-table.md).
    const guarded = guardedStep(step, state.flow.goal, situation(), earlyOptions());
    if (guarded !== step || (state.flow.goal && possibleChecks(state.flow.goal, situation(), earlyOptions()).stop)) return guarded;
    if (uploadNeedsUser()) return 'upload';
    if ((step === 'questions' || step === 'confirm' || step === 'results') && !hasInput()) return 'upload';
    // A document read from two files and not yet said which to use: asked on the upload step (never a silent drop).
    if (__FLOW_D__ && (step === 'questions' || step === 'confirm' || step === 'results') && dupsUnanswered().length) return 'upload';
    if ((step === 'confirm' || step === 'results') && !payslipsRead() && !hasForm106()) return 'questions';
    // The results only after "Check your answers" confirmed these documents (pasted data, a developer path, skips it).
    if (step === 'results' && state.source !== 'json' && state.confirmedSig !== docsSig()) return 'confirm';
    return step;
  };

  /** What "Check your answers" confirms: the files, the scan pages accepted, a typed Form 106, how the payslip came in. */
  const docsSig = () => JSON.stringify([state.files.map((f) => [f.name, f.size]), [...state.ocr.entries()].map(([i, j]) => [i, j.pages.map((p) => p.confirmed)]),
    state.form106Manual ?? null, state.result && 'payslips' in state.result ? state.result.input : '']);

  /** Opens a step: the page starts at the top and the step's heading takes the focus. */
  function goTo(step: FlowStep, fromHistory = false): void {
    // Files dropped on a start card: read when the upload step opens; dropped when the user leaves the way there.
    const held = step === 'upload' ? heldFiles : [];
    if (step !== 'who') {
      heldFiles = [];
      heldLetGo = false;
    }
    if (held.length) void readFiles(held);
    // Each step is a history entry: the browser's Back goes to the previous step inside the app (nothing is lost).
    if (!fromHistory && (history.state as { step?: FlowStep } | null)?.step !== step) history.pushState({ step }, '');
    state.flow.step = step;
    // A completed check: the usage counter's anonymous count (src/ui/counter.ts; nothing when there's no counter).
    const r = state.result;
    // The documents read (Matt, 2026-10-22 13:05): each payslip page, each Form 106 read from a file and
    // each Form 106 typed in (Matt 22:56); a failed read isn't in the result, and a typed-in payslip isn't a document. The money: every worth-checking figure shown,
    // rounded down to ₪100, added up, plus the Form 106 refund estimate's LOW end, only for a year we estimate (0 otherwise).
    if (stage() === 'results' && ((r && 'payslips' in r && !r.crashed && r.payslips.length > 0) || hasForm106())) {
      const f106 = state.f106;
      const refund = f106 && hasForm106()
        ? f106.refundLowTotal(f106.form106Findings(form106Docs().map((d) => d.fields), state.answers, eng().defaultParams, today() as Parameters<typeof f106.form106Findings>[3], { estimate: __FORM106_ESTIMATE__, foreignWorker: state.flow.who === 'foreign' }))
        : 0;
      // Pasted data (the developer path and the built-in example) is no one's payslip: it adds nothing (checkCount).
      const payslips = r && 'payslips' in r && !r.crashed ? r.payslips.map((p) => ({ ...(p.source ? { source: p.source } : {}), findings: p.findings.filter(eng().isShown) })) : [];
      const form106Files = (state.uploads ?? []).flatMap((u, i) => (u.form106 && !formNotCounted(i) ? [u.name] : []));
      const form106Typed = state.form106Manual ? [state.form106Manual.taxYear] : [];
      countCheck(checkCount({ pasted: state.source === 'json', payslips, form106Files, form106Typed, refundLowAgorot: refund, translation: translating() })); // only what this visit hasn't counted yet
      if (state.translateFirst && r && 'payslips' in r) {
        state.translateFirst = false;
        const shown = r.payslips.length;
        void loadTranslation().then(() => {
          for (let i = 0; i < shown; i++) state.translated.add(i);
          render();
        });
      }
    }
    arriving = true;
    announce('');
    try {
      render();
    } finally {
      arriving = false;
    }
    window.scrollTo?.(0, 0);
    const heading = { goal: 'goal-heading', who: 'who-heading', upload: 'upload-heading', questions: 'questions-heading', confirm: 'confirm-heading', results: 'results-heading' }[stage()];
    document.getElementById(heading)?.focus();
  }

  /** Upload step: type in a Form 106 instead of (or as well as) uploading it (the open form is in the Form 106 chunk). */
  function renderForm106Entry(): HTMLElement[] {
    const tr = (key: string, params?: Record<string, string | number>) => t(state.lang, key, params);
    if (state.flow.goal !== 'form106' && !hasForm106()) return [];
    if (!state.form106Open || !state.f106) {
      if (state.form106Manual) return [el('p', {}, fd().pencilButton(el, { id: 'form106-open', label: tr('f106.manual.edit'), onClick: openForm106Entry }))];
      const b = button(tr('f106.manual.open'), openForm106Entry, 'secondary');
      b.id = 'form106-open';
      b.className = 'link-button form106-manual-link';
      return [el('p', {}, b)];
    }
    return [state.f106.form106EntryForm({ el, tr, button, draft: state.form106Draft, problems: state.form106Problems ?? [], onUse: () => void useTyped106(), onCancel: () => {
      state.form106Open = false;
      render();
    } })];
  }

  /**
   * Design D's upload step: a Form 106 typed in as a row of the file list, like a file read (Matt's "never a dead end",
   * 2026-10-23): its tax year, "Form 106 · numbers you entered yourself", "Edit the Form 106 numbers you entered"
   * (closed while the form is open) and ✕, which asks first (the numbers can't be brought back). Undefined: none typed.
   */
  function typedForm106Row(): HTMLLIElement | undefined {
    const typed = state.form106Manual;
    if (!typed) return undefined;
    const tr = (key: string, params?: Record<string, string | number>) => t(state.lang, key, params);
    const change = state.form106Open ? '' : fd().pencilButton(el, { id: 'form106-open', label: tr('f106.manual.edit'), onClick: openForm106Entry });
    const remove = button('', () => {
      const before = removeLabels();
      const at = before.indexOf(tr('f106.manual.remove'));
      void guardLoss('removeTyped106', state.lang, LANGUAGES[state.lang].dir, { typed: true }, () => {
        // Its fixes are keyed by its place among the forms (after the files'): they go with it.
        state.form106Fixes.delete((state.uploads ?? []).filter((u, i) => u.form106 && !formNotCounted(i)).length);
        delete state.form106Manual;
        state.form106Draft = {};
        state.form106Problems = undefined;
        state.form106Open = false;
        render();
        focusAfterRemoving(before, at);
      }, 'removeTyped');
    }, 'secondary');
    remove.classList.add('remove');
    remove.setAttribute('aria-label', tr('f106.manual.remove'));
    remove.append(fd().flowIcon('x'));
    const row = fd().fileRows(el, [{
      state: 'done', id: 'fd-file-typed106',
      title: typed.taxYear ? tr('upload.taxYear', { year: String(typed.taxYear) }) : tr('upload.type.form106'),
      sub: `${tr('upload.type.form106')} · ${tr('flow.summary.byHand')}`,
      extra: [change, remove],
    }], '').firstElementChild as HTMLLIElement;
    return row;
  }

  /** The file list's ✕ buttons in screen order, by their labels ("Remove a.pdf"): unique, as the same file isn't added twice. */
  function removeLabels(): string[] {
    return [...document.querySelectorAll<HTMLButtonElement>('.file-list .remove')].map((b) => b.getAttribute('aria-label') ?? '');
  }

  /**
   * After a row of the file list is removed (keyboard users keep their place): the ✕ of the row that came after it on the
   * screen, else of the one before it, else "Add files" (#pdf-choose). By label, as the rows are drawn again and sorted
   * (design D: rows that need attention first, the rest folded); a row inside a closed fold has its fold opened.
   */
  function focusAfterRemoving(before: readonly string[], at: number): void {
    const order = at < 0 ? [] : [...before.slice(at + 1), ...before.slice(0, at).reverse()];
    const buttons = [...document.querySelectorAll<HTMLButtonElement>('.file-list .remove')];
    for (const label of order) {
      const b = buttons.find((x) => x.getAttribute('aria-label') === label);
      if (!b) continue;
      for (let x = b.parentElement; x; x = x.parentElement) {
        if (x instanceof HTMLDetailsElement && !x.open) {
          x.open = true;
          if (x.id) state.moreOpen.add(x.id);
        }
        if (x.hidden) x.hidden = false;
      }
      b.focus();
      return;
    }
    document.getElementById('pdf-choose')?.focus();
  }

  /** The typed-in Form 106: numbers parsed, obvious typos sent back, else used. */
  async function useTyped106(): Promise<void> {
    const { fields, problems } = (await loadForm106()).typedForm106(state.form106Draft, new Date().getFullYear());
    state.form106Problems = problems.length ? problems : undefined;
    if (!problems.length) {
      state.form106Manual = fields;
      state.form106Open = false;
    }
    render();
    // Problems: the summary at the top of the form takes the focus (the error pattern).
    document.getElementById(problems.length ? 'form106-problems' : 'form106-open')?.focus();
  }

  /** The tax years the Form 106 questions step asks about: not one before the refund window or after this year. */
  function form106QuestionYears(): (number | undefined)[] {
    const now = new Date();
    return [...new Set(form106Docs().map((d) => d.fields.taxYear))].filter((y) => y === undefined || (y >= deadlineYears(now).taxYear && y <= israelYear(now))).sort((x, y) => (x ?? 0) - (y ?? 0));
  }
  /**
   * Design D, Form 106 only: the other-income question is required (the results cannot check a year without it). Several
   * known years: the one question (multiyear-question) is missing while nothing is ticked, or while an item ticked has no
   * year (its chips say "Choose the years", the first chip takes the focus). Else each year's question.
   */
  function form106Needed(): { id: string; name: string; params?: Record<string, string>; message: string }[] {
    const years = form106QuestionYears();
    const f106 = state.f106;
    if (f106 && f106.asksOnce(years)) {
      const missing = f106.multiYearMissing(years, state.answers.incomeSources, multiYearItems(years));
      const name = { name: 'questionsD.needed.income', params: { year: yearList(years) } };
      if (missing === undefined) return [];
      return [missing === 'unanswered' ? { id: 'f106-q-income-all', ...name, message: 'whoD.needed.statusesMessage' } : { id: `income-source-all-${missing}-years`, ...name, message: '' }];
    }
    return years.filter((y) => state.answers.incomeSources?.[incomeSourcesKey(y)] === undefined)
      .map((y) => ({ id: `f106-q-income-${incomeSourcesKey(y)}`, name: y === undefined ? 'questionsD.needed.incomeNoYear' : 'questionsD.needed.income', ...(y === undefined ? {} : { params: { year: String(y) } }), message: 'whoD.needed.statusesMessage' }));
  }

  let multiYearDraft: { years: string; items: IncomeSource[] } | undefined;
  /** The items ticked on the one question with no year chosen yet, for these years (a draft, not an answer). */
  const multiYearItems = (years: readonly (number | undefined)[]): IncomeSource[] => (multiYearDraft?.years === years.join(',') ? multiYearDraft.items : []);
  /** The years as a natural list in the page's language ("2023, 2024 or 2025"); each number an LTR isolate so it keeps its digits' order in Hebrew and Arabic. */
  const yearList = (years: readonly number[]): string => {
    const items = years.map((y) => `\u2066${y}\u2069`);
    try {
      return new Intl.ListFormat(state.lang, { style: 'long', type: 'disjunction' }).format(items);
    } catch {
      return items.join(', ');
    }
  };
  /** "Continue" on the questions step: while a ticked other-income item has no year chosen, it takes the person to that line instead (a missing answer, never a year left to "can't check" by accident). */
  function continueFromQuestions(): void {
    const missing = document.querySelector('.chips-missing');
    const chip = missing?.closest('fieldset.chips')?.querySelector<HTMLElement>('input');
    if (chip) {
      chip.focus();
      chip.scrollIntoView({ block: 'center', behavior: scrollMotion() });
      return;
    }
    goTo('confirm');
  }
  /** Questions for the Form 106 checks (src/form106/ui.ts): other income in each tax year, credit-point situations. */
  function form106Questions(inPlay: PayslipFields[]): HTMLElement[] {
    if (!hasForm106() || !state.f106) return [];
    // A year before the refund window or after this year is asked nothing: its result says why (form106.yearTooOld / yearInFuture), told where it is known.
    const years = form106QuestionYears();
    // Several years: the other-income question is asked once; items ticked with no year chosen yet are kept here (not an answer).
    const yearsKey = years.join(',');
    const incomeDraft = { items: multiYearItems(years), set: (items: IncomeSource[]) => { multiYearDraft = { years: yearsKey, items }; } };
    return state.f106.form106Questions({ el, tr: (k, p) => t(state.lang, k, p), question: (spec) => choiceQuestion(el, spec), answers: state.answers, setAnswer, years, incomeDraft, yearList, ...(engine ? { params: engine.defaultParams } : {}), mayAskSex: !underPensionAgeForMen(inPlay), monthName,
      // Credit points and the refund can't be checked for this group: their situations (and the sex) are not asked. Olim:
      // a credit that ended (an answer given inside the situations question itself) never hides that question, or it could not be changed.
      askStatuses: state.flow.goal === undefined || possibleChecks('form106', { ...situation(), olimAliyah: [], credit: [] }, earlyOptions()).possible.includes('creditPoints') });
  }

  let f106SeededFor = '';
  /** Results: what each Form 106 says, and its refund signals (src/form106/ui.ts). */
  function renderForm106Results(lang: Lang, disclaimerLast = false, adviceLine = true): HTMLElement[] {
    const docs = form106Docs();
    if (!docs.length || !state.f106) return [];
    // New forms: rows the person left open from other forms are closed (every row arrives closed).
    const signature = docs.map((d) => `${d.file ?? ''}|${String(d.fields.taxYear)}`).join(',');
    if (signature !== f106SeededFor) {
      f106SeededFor = signature;
      for (const id of [...state.moreOpen]) if (/^f106-\d+-year$|^f106-more-years$/.test(id)) state.moreOpen.delete(id);
    }
    const payslips = state.result && 'payslips' in state.result ? state.result.payslips.map((p) => p.fields) : [];
    return state.f106.form106Results({
      foreignWorker: state.flow.who === 'foreign',
      el, tr: (key, params) => t(lang, key, params), money: (a) => formatMoney(a, lang), wholeMoney: (a) => formatMoney(a, lang, { whole: true }), docs, payslips, answers: state.answers, params: eng().defaultParams, today: today(),
      reviewBox: (i) => form106ReviewBox(i, lang),
      reviewFields: (i) => {
        const d = docs[i];
        return !d || d.typed ? [] : (state.f106?.form106Review(d.fields) ?? []).map((it) => t(lang, `f106.short.${it.key}`));
      },
      card: (f) => {
        const view = eng().resultsView(lang, { findings: [f] });
        return view.kind === 'findings' ? view.cards[0] : undefined;
      },
      remedy: (c, id, forms, level) => remedyView(c, id, level, form106Fix(forms)),
      more: (c, id, level, o) => renderMoreInfo(c.more, id, level, o ? { ...(o.label ? { label: o.label } : {}), ...(o.before ? { before: o.before } : {}), ...(o.also ? { also: o.also.more } : {}) } : {}),
      icon: STATUS_ICON,
      plainMore: (id, nodes) => renderMoreInfo(undefined, id, 'h5', { before: nodes }),
      // The year rows are folds like the rest: open or closed as the person left them across redraws.
      fold: { isOpen: (id) => state.moreOpen.has(id), set: (id, open) => { if (open) state.moreOpen.add(id); else state.moreOpen.delete(id); } },
      disclaimerLast, adviceLine,
    });
  }

  /** Step (a): what the user wants to check. */
  function renderGoal(): HTMLElement {
    const tr = (key: string) => t(state.lang, key);
    // Design D (__FLOW_D__; Matt approved the mockups 2026-10-22): the question, three goal cards (the payslip check the
    // primary), the full approved privacy line, how it works, the public counter line.
    if (__FLOW_D__) {
      if (showOpening) {
        // Its chunk and texts first (a moment, from this site or the offline copy); the page is drawn when they are here.
        if (!openingD) {
          // Its chunk didn't load (e.g. an old copy of the app after a deploy): the start screen instead, never a blank page.
          void loadOpening().then(() => render(), () => {
            showOpening = false;
            render();
          });
          return el('section', { class: 'rv fd fd-screen fd-opening', 'aria-busy': 'true' });
        }
        return openingD.openingScreen({ el, tr: (k, params) => t(state.lang, k, params), now: new Date(), onStart: leaveOpening, checks: checksLineD() });
      }
      const start = (goal: Goal, translate = false) => {
        // A click on a card (even a different one) after a drop lets the dropped files go: they belong to the drop's own card only.
        heldFiles = [];
        heldLetGo = false;
        state.flow.goal = goal;
        state.translateFirst = translate;
        state.translateAsked = translate;
        if (!translate) checkInstead();
        void Promise.all([loadEngine(), ...(goal === 'form106' ? [loadForm106()] : [])]).then(() => goTo(translate ? 'upload' : 'who'));
      };
      // Files dropped on a goal card (computers only): the goal starts at "Who are you?" with them held; they are added as on the upload step once it is answered.
      const dropOnGoal = (goal: Goal, translate: boolean, files: File[]) => {
        if (files.length === 0) return;
        state.flow.goal = goal;
        state.translateFirst = translate;
        state.translateAsked = translate;
        if (!translate) checkInstead();
        // Their bytes are not read here: only when the upload step opens (privacy: nothing is read before "Who are you?" is answered).
        // Held until "Who are you?" is answered (told-too-late: a stop there must come before anything is read or shown).
        heldFiles = files;
        heldLetGo = false;
        void Promise.all([loadEngine(), ...(goal === 'form106' ? [loadForm106()] : [])]).then(() => goTo(translate ? 'upload' : 'who'));
      };
      const text = t(state.lang, COUNTER_URL ? 'app.privacyLine.counter' : 'app.privacyLine.noCounter');
      return startScreen({
        el, tr: (k, params) => t(state.lang, k, params), languageName: LANGUAGES[state.lang].nativeName,
        privacy: { text, ...(COUNTER_URL ? { link: { text: t(state.lang, 'app.privacyLink'), href: '#/privacy/counter' } } : {}) },
        onPayslip: () => start('month'), ...(COMING_SOON.includes('form106') ? {} : { onForm106: () => start('form106') }), onTranslate: () => start('month', true),
        onWhy: reopenOpening,
        tools: __TAKE_HOME__ ? [{ id: 'tool-take-home', title: t(state.lang, 'startD.tool.takeHome'), sub: t(state.lang, 'startD.tool.takeHome.sub'), route: 'take-home' }] : [],
        checks: checksLineD(),
        onDropFiles: (which, list) => dropOnGoal(which === 'form106' ? 'form106' : 'month', which === 'translate', filesOf(list)),
      });
    }
    // Brand direction C (docs/specs/brand-c.md): the hero, one primary action and two secondary ones.
    if (__BRAND__ === 'c') {
      const start = (goal: Goal, translate = false) => {
        state.flow.goal = goal;
        state.translateFirst = translate;
        state.translateAsked = translate;
        if (!translate) checkInstead();
        void Promise.all([loadEngine(), ...(goal === 'form106' ? [loadForm106()] : [])]).then(() => goTo(translate ? 'upload' : 'who'));
      };
      return brandHero({
        el, tr, translateKey: firstKey(state.lang, 'brand.translateInto'), onPayslip: () => start('month'), ...(COMING_SOON.includes('form106') ? {} : { onForm106: () => start('form106') }), onTranslate: () => start('month', true),
        checks: checksLine(),
      });
    }
    const options = choiceQuestion(el, {
      name: 'goal', type: 'radio', question: tr('flow.goal.heading'), level: 3, hiddenLegend: true, cards: true, selected: state.flow.goal,
      options: GOALS.map((g) => ({ value: g, label: tr(`flow.goal.${g}`), help: tr(`flow.goal.${g}.help`), ...(COMING_SOON.includes(g) ? { disabled: true, badge: tr('flow.comingSoon') } : {}) })),
      onChange: (v) => {
        const g = v as Goal;
        state.flow.goal = g;
        // The Form 106 goal's screens and texts are in its chunk: start loading it now (Continue waits for it).
        void loadEngine();
        if (g === 'form106') void loadForm106();
        renderKeepingFocus();
      },
    });
    const next = button(tr('flow.continue'), () => {
      if (!state.flow.goal) return;
      if (state.flow.goal === 'left') state.moreOpen.add('leftJob');
      // The goal's screens load with it (the check engine; Form 106's chunk too): Continue waits for them.
      void Promise.all([loadEngine(), ...(state.flow.goal === 'form106' ? [loadForm106()] : [])]).then(() => goTo('who'));
    });
    next.id = 'goal-continue';
    if (!state.flow.goal) next.setAttribute('aria-disabled', 'true');
    return el('section', { class: 'card flow-step' },
      el('h2', { id: 'goal-heading', tabindex: '-1' }, tr('flow.goal.heading')),
      options,
      el('div', { class: 'row' }, next),
      checksLine().length ? el('p', { class: 'help checks-so-far' }, ...checksLine()) : '');
  }

  /** The foreign-worker sector (and a caregiver's employer): asked under the status or on the "who" step. */
  function workerTypeQuestions(level: ChoiceSpec['level'] = 3): HTMLElement[] {
    const tr = (key: string) => t(state.lang, key);
    const out: HTMLElement[] = [radioGroup('workerType', tr('questions.workerType'),
      [...WORKER_TYPES.map((w) => [w, tr(`questions.workerType.${w}`)] as [string, string]), ['', tr('questions.dontKnow')]],
      state.answers.workerType ?? '', (v) => setAnswer('workerType', v === '' ? undefined : (v as WorkerType)), level)];
    // A caregiver: who employs them decides the deposit and pension arrangement (chunk 3).
    if (state.answers.workerType === 'foreignCaregiver') {
      out.push(radioGroup('caregiverEmployer', tr('questions.caregiverEmployer'),
        [...CAREGIVER_EMPLOYERS.map((c) => [c, tr(`questions.caregiverEmployer.${c}`)] as [string, string]), ['', tr('questions.dontKnow')]],
        state.answers.caregiverEmployer ?? '', (v) => setAnswer('caregiverEmployer', v === '' ? undefined : (v as CaregiverEmployer)), level));
    }
    return out;
  }

  /** What the early-stop table reads (src/model/possibleChecks.ts). */
  // Form 106: each tax year's other-income answers with how many employers that year's loaded forms come from, so
  // "another employer" stops taking the estimate away once that employer's Form 106 is added (src/model/possibleChecks.ts).
  // The translate flow asks nothing: no answer is read, so nothing stops it ("Who are you?" is not part of it).
  const situation = (): Situation => translating() ? { answers: {}, translate: true } : ({ answers: state.answers, workerTypeNotSure: notSureChosen.has('workerType'), ...form106Years(state.answers.incomeSources, form106Docs().map((d) => d.fields), __F106_GROUPS__ ? state.answers.visaYears : undefined,
      // Olim (preview only): what the month of aliyah means for each year (the early-stop table's aliyah row).
      __OLIM__ && state.f106 && engine ? olimEffectFor(state.f106, engine) : undefined,
      // Credit points for a degree: whether its points are over in each year (the table's credit row).
      state.f106 && engine ? creditEffectFor(state.f106, engine) : undefined),
    // "No, I still work there" (design D), or the classic "I left this job" box unticked by the person.
    ...(state.moreOpen.has('leftJobNo') ? { stillEmployed: true } : {}),
  } satisfies Situation);
  const earlyOptions = () => ({ estimate: __FORM106_ESTIMATE__ });
  /** Olim: what the month of aliyah means for a tax year, on these answers (src/rules/olim.ts olimAliyahEffect). */
  const olimEffectFor = (f106: typeof import('../form106'), e: typeof Engine) => (year: number | undefined) => f106.olimAliyahEffect(year, state.answers, e.defaultParams);
  /** Credit points for a degree: what the answers mean for a tax year (src/rules/creditExtra.ts creditExtraEffect). */
  const creditEffectFor = (f106: typeof import('../form106'), e: typeof Engine) => (year: number | undefined) => f106.creditExtraEffect(year, state.answers, e.defaultParams);

  /**
   * "Tell them on the step where we know" (Matt, 2026-10-22; docs/specs/early-stop-table.md): what the answers so far
   * mean for the goal, read from the table, never decided here. Nothing possible: the stop notice (the step's Continue
   * and everything after it are not shown). Part lost: the notice names what won't be checked, above Continue.
   */
  function earlyNotice(step: 'who' | 'questions', asPageHeading = false): { stop: boolean; node: HTMLElement | '' } {
    const goal = state.flow.goal;
    if (!goal) return { stop: false, node: '' };
    const tr = (key: string) => t(state.lang, key);
    const p = possibleChecks(goal, atStep(step, situation()), earlyOptions());
    // A stop: files dropped on a start card are let go, unread (nothing was read for the user; the stop notice says what is possible).
    if (p.stop && heldFiles.length) {
      heldFiles = [];
      heldLetGo = true;
    }
    if (!p.stop && !p.partial) {
      lastNotice = '';
      announce('');
      return { stop: false, node: '' };
    }
    // One sentence per answer that took something away (a stop names the first, as the engine's result would).
    const reasons = [...new Map(p.lost.map((l) => [`${l.kind}.${l.value}`, l])).values()];
    // Offered on a stop, and on a Form 106 that loses part of its checks (a payslip may give that person more); not on a payslip that loses only one check.
    const alt = p.stop || goal === 'form106' ? alternative(goal, atStep(step, situation()), __FORM106_READY__, earlyOptions()) : undefined;
    // The goal itself doesn't fit an answer ("I still work there" on the leaving goal): the other goal, same step, same answers and files.
    const sameStep = p.lost[0]?.kind === 'goal';
    const altButton = alt ? button(tr(alt === 'form106' ? 'flow.early.checkForm106' : sameStep ? 'flow.early.checkMonth' : 'flow.early.checkPayslip'), () => {
      state.flow.goal = alt;
      void Promise.all([loadEngine(), ...(alt === 'form106' ? [loadForm106()] : [])]).then(() => goTo(sameStep ? step : 'who'));
    }, p.stop ? 'primary' : 'secondary') : undefined;
    if (altButton) altButton.id = 'early-alternative';
    // A stop that a translation goes through (audit 2026-10-24 item 8): the one thing that works for this person is offered, on the step that says so.
    const translateButton = p.stop && !state.translateAsked && !sameStep && translationOnly({ ...situation(), translate: true })
      ? button(tr('flow.early.translateInstead'), () => {
        state.flow.goal = 'month';
        state.translateFirst = true;
        state.translateAsked = true;
        acceptForTranslation();
        // The translation asks nothing: straight to the upload (or the translation, when the files are already read).
        void loadEngine().then(() => goTo(payslipsRead() || hasForm106() ? 'results' : 'upload'));
      }, altButton ? 'secondary' : 'primary') : undefined;
    if (translateButton) translateButton.id = 'early-translate';
    const back = button(tr('flow.early.back'), () => goTo(step === 'who' ? 'goal' : 'who'), altButton || translateButton ? 'secondary' : 'primary');
    back.id = 'early-back';
    const node = el('div', { class: 'early-notice', role: 'note', id: 'early-notice' },
      el(asPageHeading && p.stop ? 'h2' : 'h3', { id: asPageHeading && p.stop ? 'questions-heading' : 'early-heading', ...(asPageHeading && p.stop ? { tabindex: '-1' } : {}) }, tr(p.stop ? stopHeadingKey(p) : 'flow.early.partial.heading')),
      ...(p.stop ? reasons.slice(0, 1) : reasons).map((l) => el('p', {}, t(state.lang, noticeKey(l, earlyOptions()), noticeParams(l)))),
      altKey(p, alt) ? el('p', { id: 'early-alt-note' }, tr(altKey(p, alt) as string)) : '',
      p.partial ? el('p', {}, tr(stillKey(p))) : '',
      // Said where the files were let go (a stop on "Who are you?"), so an empty upload step after taking the answer back is no surprise.
      p.stop && step === 'who' && heldLetGo ? el('p', { id: 'early-letgo' }, tr('flow.early.dropLetGo')) : '',
      el('div', { class: 'row' }, ...(p.stop ? [...(altButton ? [altButton] : []), ...(translateButton ? [translateButton] : []), back] : altButton ? [altButton] : [])));
    // A notice that has just appeared or changed (a stop, a new sentence) is brought into view and said to a screen reader,
    // once per change of answer, not on every re-render (told on the step: in view right after the tap).
    const seen = `${step}:${p.stop ? 'stop' : 'partial'}:${reasons.map((l) => `${l.kind}.${l.value}`).join(',')}`;
    // Arriving on the step (Continue, Back, a link) shows it from its top, its heading focused: the notice is read in order.
    if (seen !== lastNotice && !arriving) queueMicrotask(() => noticeNews(p.stop));
    lastNotice = seen;
    return { stop: p.stop, node };
  }
  let lastNotice = '';
  /** Set while goTo draws a step it is arriving on. */
  let arriving = false;
  /**
   * The notice just shown (after the re-draw and the focus put back on the answer tapped): a stop to the top of the screen;
   * a partial one only when its heading and first sentence are not fully in view, by the least movement (the sticky bar's
   * room is kept by the page's scroll padding). Instant under prefers-reduced-motion. Its heading and sentences go to the
   * page's one live region, so a screen reader hears it while the focus stays on the answer.
   */
  function noticeNews(stop: boolean): void {
    const notice = document.getElementById('early-notice');
    if (!notice) return;
    const behavior = scrollMotion();
    const shown = (e: Element | null) => {
      const r = e?.getBoundingClientRect();
      // The bottom bar (sticky) covers the screen's lower edge: what is under it is not in view.
      const bar = document.querySelector('.fd-bar.sticky, .row.sticky-action')?.getBoundingClientRect();
      const bottom = bar && bar.top > 0 && bar.top < window.innerHeight ? bar.top : window.innerHeight;
      return !r || (r.top >= 0 && r.bottom <= bottom);
    };
    if (stop) notice.scrollIntoView?.({ block: 'start', behavior });
    else if (!shown(notice.querySelector('h2, h3, summary')) || !shown(notice.querySelector('p'))) {
      // Taller than the room left above the bar: its top first (the heading and first sentence are what must show).
      const tall = notice.getBoundingClientRect().height > window.innerHeight * 0.7;
      notice.scrollIntoView?.({ block: tall ? 'start' : 'nearest', behavior });
    }
    // The heading (a fold's summary on the upload step) and every sentence, folded ones too.
    announce([...notice.querySelectorAll('h2, h3, summary, p')].map((e) => e.textContent ?? '').join(' '));
  }
  /** The page's one live region (outside the re-drawn content, so a change in it is announced); '' empties it. */
  function announce(text: string): void {
    let live = document.getElementById('app-live');
    if (!live && !text) return;
    if (!live) {
      live = el('div', { id: 'app-live', class: 'visually-hidden', role: 'status', 'aria-live': 'polite' });
      (root.parentElement ?? document.body).append(live);
    }
    live.textContent = text;
  }
  /**
   * Every Form 106 added is from a tax year before the refund window (and none is in range): said here, where the year is
   * first known, with the way on (a more recent form), and the questions step is not offered (Matt 2026-10-23 19:29 item D;
   * docs/specs/early-stop-table.md "Form 106 tax year outside the refund window"). One form in range: nothing here, the
   * questions are asked for the in-range years only. An unread year alone is not a stop.
   */
  /** A Form 106 from before the refund window can still be translated: the upload's stop does not apply there. */
  const allFormsTooOld = (): number | undefined => (translating() ? undefined : formsAllTooOld());
  function formsAllTooOld(): number | undefined {
    if (!hasForm106()) return undefined;
    const years = form106Docs().map((d) => d.fields.taxYear);
    const first = deadlineYears(new Date()).taxYear;
    const old = years.filter((y): y is number => y !== undefined && y < first);
    return old.length && !years.some((y) => y !== undefined && y >= first) ? Math.max(...old) : undefined;
  }
  function form106StopNotice(): HTMLElement | '' {
    const year = allFormsTooOld();
    if (year === undefined) {
      if (lastNotice.startsWith('upload:')) {
        lastNotice = '';
        announce('');
      }
      return '';
    }
    const { taxYear: firstYear, years } = deadlineYears(new Date());
    const seen = `upload:form106TooOld.${year}`;
    if (seen !== lastNotice && !arriving) queueMicrotask(() => noticeNews(true));
    lastNotice = seen;
    return el('div', { class: 'early-notice', role: 'note', id: 'early-notice' },
      el('p', { id: 'early-form106-tooold' }, t(state.lang, 'form106.yearTooOld', { year: String(year), years, firstYear: String(firstYear) })),
      el('p', {}, t(state.lang, 'next.cantCheck.yearTooOld')));
  }
  /**
   * The upload step: what the loaded documents themselves take away, said where we learn it ("Tell them on the step
   * where we know", docs/specs/early-stop-table.md): on the Form 106 goal, a set of forms all from before the refund
   * window (form106StopNotice). The payslip goals have nothing to learn from the payslips themselves.
   */
  function uploadNotice(): HTMLElement | '' {
    if (state.flow.goal === 'form106') return form106StopNotice();
    if (lastNotice.startsWith('upload:')) {
      lastNotice = '';
      announce('');
    }
    return '';
  }
  /** Placeholders of a notice sentence: the youth sentence names the mandatory-pension starting ages (params pensionMinimumAge). */
  function noticeParams(l: { kind: string; value: string }): Record<string, number> | undefined {
    if (l.kind !== 'status' || l.value !== 'youth') return undefined;
    const age = eng().defaultParams.entry('pensionMinimumAge', today())?.value;
    return age ? { men: age.men, women: age.women } : undefined;
  }

  /** The sector (private or public) decides whether payslips can be checked at all: asked on "Who are you?" for the payslip goals, once. */
  const sectorOnWho = (): boolean => state.flow.goal !== undefined && state.flow.goal !== 'form106';
  /** Answered there: the questions step does not ask it again ("Check your answers" shows it, its Change goes to that step). */
  const sectorAnsweredOnWho = (): boolean => sectorOnWho() && state.flow.sectorOnWho === true;
  function sectorQuestion(): HTMLElement {
    const tr = (key: string) => t(state.lang, key);
    return radioGroup('sector', tr('flow.who.sector'), [['private', tr('questions.sector.private')], ['public', tr('questions.sector.public')], ['', tr('questions.dontKnow')]],
      state.answers.sector ?? '', (v) => { state.flow.sectorOnWho = true; setAnswer('sector', v === '' ? undefined : (v as Sector)); }, 3);
  }

  /** Step (b): who the user is; only what that answer needs is asked under it. */
  function renderWho(): HTMLElement {
    const tr = (key: string) => t(state.lang, key);
    const who = state.flow.who;
    const pick = (w: Who) => {
      state.flow.who = w;
      setAnswer('specialStatuses', groupStatuses(w));
      document.getElementById(`who-${w}`)?.focus();
    };
    // The answer's follow-up right under its option: a foreign worker's sector, the special situations.
    const options = choiceQuestion(el, {
      name: 'who', type: 'radio', question: tr('flow.who.heading'), level: 3, hiddenLegend: true, cards: true, selected: who,
      options: WHO.map((w) => ({
        value: w, label: tr(`flow.who.${w}`), help: tr(`flow.who.${w}.help`),
        // The chosen card's follow-ups, the sector last (payslip goals): asked once, here, before the upload.
        ...(who === w ? { followUp: el('div', { class: 'who-follow-up' }, ...(w === 'foreign' ? workerTypeQuestions(3) : w === 'israeli' ? [renderStatusQuestion(3, ['foreignWorker'])] : []), ...(sectorOnWho() && w !== 'other' ? [sectorQuestion()] : [])) } : {}),
      })),
      onChange: (v) => pick(v as Who),
    });
    const next = button(tr('flow.continue'), () => {
      if (!state.flow.who) return;
      const to = stepAfter('who', state.flow.goal ?? 'month', situation(), (state.flow.goal === 'form106' ? hasForm106() : payslipsRead()) && heldFiles.length === 0, earlyOptions());
      if (to !== 'stop') goTo(to);
    });
    next.id = 'who-continue';
    if (!who) next.setAttribute('aria-disabled', 'true');
    // Tell them here, on the step where we know (docs/specs/early-stop-table.md): a stop replaces Continue.
    const early = earlyNotice('who');
    return el('section', { class: 'card flow-step' },
      el('h2', { id: 'who-heading', tabindex: '-1' }, tr('flow.who.heading')),
      // The news comes first, under the question's heading, so it is on the first screen of a phone (a stop with its buttons).
      early.node,
      options,
      early.stop ? '' : el('div', { class: 'row sticky-action' }, next));
  }

  /**
   * Design D's "Who are you?" (Matt approved the mockups 2026-10-22 16:36, checklist 16:40; folds for the ≤40 words above
   * the fold, 2026-10-22 evening): three group cards (MATT GO 2026-10-23 13:40: an Israeli worker, a foreign worker or asylum
   * seeker, another work arrangement, which stops on the step; none preselected; a card's explanation shows once it is chosen), with
   * their follow-ups under them (a foreign worker's sector of work; on the payslip goals the sector, required). Then "Does
   * any of this apply to you?", one closed box with its answer showing ("Not answered yet" until something is ticked, never
   * "None of these" unasked; less-text mockups, MATT GO 2026-10-23 13:28: the rare cases in the same list, help lines only
   * where they say what isn't checked). The Form 106 goal asks one question with the same three cards (an Israeli worker, a
   * foreign worker or asylum seeker, another work arrangement): only those cards change a Form 106 check (ANSWER_EFFECTS), so
   * nothing else is asked there. Several ticks combine (the early-stop table). A partial notice shows right under the answer that caused it;
   * a stop shows under the heading, its alternative the bar's one primary, and questions that can no longer matter go.
   * The bottom bar: Continue, dimmed but pressable while an answer is missing; what is missing is named once it is pressed.
   */
  /** The partial notice's sentences on the last draw of "Who are you?", and the answer it is shown under. */
  let whoNoticeKeys: string[] = [];
  let whoNoticeAt = '';
  /** The statuses on the last draw, and the status a combination's notice goes under (the one ticked last). */
  let whoPrevStatuses: readonly SpecialStatus[] = [];
  let whoComboAt = '';
  const STATUSES_D: readonly SpecialStatus[] = ['youth', 'apprentice', 'adjustedMinimum', 'contractor', 'cleaningContractor', 'oldAgePensioner'];
  /** Rare statuses, last in the list (Matt, 2026-10-22 20:04; one box since the less-text mockups, 2026-10-23). */
  const RARE_STATUSES_D: readonly SpecialStatus[] = ['rehabilitation'];
  /** The only statuses whose help line is shown: they say what isn't checked (a preview of the notice); the others restated their label. */
  const HELP_STATUSES_D: readonly SpecialStatus[] = ['contractor', 'rehabilitation'];
  /**
   * The statuses a group card sets (MATT GO 2026-10-23 13:40, both flows): a foreign worker's status; "Another work
   * arrangement" its own status alone (a stop: no checks run); an Israeli worker keeps the situations ticked under it.
   */
  function groupStatuses(w: Who): SpecialStatus[] | undefined {
    const kept = (state.answers.specialStatuses ?? []).filter((x) => x !== 'foreignWorker' && x !== 'otherArrangement');
    if (w === 'other') return ['otherArrangement'];
    const next = w === 'foreign' ? [...kept, 'foreignWorker' as const] : kept;
    return next.length ? next : undefined;
  }
  function renderWhoD(): HTMLElement[] {
    const tr = (key: string, params?: Record<string, string | number>) => t(state.lang, key, params);
    const who = state.flow.who;
    const statuses = state.answers.specialStatuses ?? [];
    const others = statuses.filter((x) => x !== 'foreignWorker' && x !== 'otherArrangement');
    const early = earlyNotice('who');
    const p = state.flow.goal ? possibleChecks(state.flow.goal, atStep('who', situation()), earlyOptions()) : undefined;
    const sectorAnswered = state.answers.sector !== undefined || notSureChosen.has('sector');
    const pick = (w: Who) => {
      state.flow.who = w;
      setAnswer('specialStatuses', groupStatuses(w));
      document.getElementById(`who-${w}`)?.focus();
    };
    if (state.flow.goal === 'form106') return [whoForm106D(tr, who, early)];
    // A partial notice goes right under the answer that last added a sentence to it (in view right after the tap), and stays
    // there while that answer stands; a stop is under the heading.
    const keys = early.node && !early.stop && p ? [...new Set(p.lost.map((l) => `${l.kind}.${l.value}`))] : [];
    const added = keys.filter((k) => !whoNoticeKeys.includes(k));
    const raw = added.length ? added[added.length - 1] : keys.includes(whoNoticeAt) ? whoNoticeAt : keys[0];
    // Two statuses together (a contractor's worker who also cleans): under the one of them just ticked.
    if (raw?.startsWith('combination.') && added.includes(raw)) {
      const members = COMBINATIONS[raw.slice(12) as Combination] ?? [];
      whoComboAt = members.find((x) => !whoPrevStatuses.includes(x)) ?? members.at(-1) ?? '';
    }
    const noticeAt = raw?.startsWith('combination.') ? `status.${whoComboAt}` : raw;
    whoNoticeKeys = keys;
    whoNoticeAt = raw ?? '';
    whoPrevStatuses = statuses;
    const noticeUnder = (key: string): HTMLElement | '' => (noticeAt === key || noticeAt?.startsWith(`${key}.`) ? early.node : '');
    // Follow-ups that can still matter: after a stop, an unanswered question changes nothing, so it goes.
    const keyOf = (w: Who) => (w === 'other' ? 'whoD.otherArrangement' : `whoD.${w}`);
    const groupFollowUps = (w: Who): (HTMLElement | '')[] => [
      // The group's own notice (a foreign worker on the Form 106 goal) under the group's explanation.
      ...(w === 'foreign' ? [noticeUnder('status.foreignWorker')] : []),
      // Answered ("Not sure" too) stays visible and changeable while a stop shows; only an unanswered one goes.
      ...(w === 'foreign' && !(early.stop && !state.answers.workerType && !notSureChosen.has('workerType'))
        ? workerTypeQuestions(3).flatMap((q, i) => [q, noticeUnder(i === 0 ? 'workerType' : 'caregiverEmployer')]) : []),
      // Not under "Another work arrangement": every check stops there, whatever the sector.
      ...(sectorOnWho() && w !== 'other' && !(early.stop && !sectorAnswered) ? [el('div', { id: 'fd-who-sector' }, sectorQuestion())] : []),
    ];
    const groups = choiceQuestion(el, {
      name: 'who', type: 'radio', question: tr('whoD.heading'), level: 3, hiddenLegend: true, cards: true, selected: who,
      options: WHO.map((w) => ({
        value: w, label: tr(keyOf(w)),
        // The explanation of the chosen card only (the titles say which is which).
        ...(who === w ? { help: tr(`${keyOf(w)}.help`), followUp: el('div', { class: 'who-follow-up' }, ...groupFollowUps(w)) } : {}),
      })),
      onChange: (v) => pick(v as Who),
    });
    groups.querySelectorAll<HTMLLabelElement>('label.option-card').forEach((label) => label.append(fd().flowIcon(label.htmlFor === 'who-foreign' ? 'globe' : label.htmlFor === 'who-other' ? 'briefcase' : 'user')));
    // The status whose sentence a partial notice says: the notice goes right under it.
    // A status this screen lists (the foreign-worker status is the group card: its notice goes under the groups).
    const partialAt = noticeAt?.startsWith('status.') && [...STATUSES_D, ...RARE_STATUSES_D].includes(noticeAt.slice(7) as SpecialStatus) ? noticeAt.slice(7) : undefined;
    /** The notice has a place above (under a status, the group, its sector of work or employer); otherwise under the groups' answers. */
    const placed = Boolean(partialAt) || ['status.foreignWorker', 'workerType', 'caregiverEmployer'].some((k) => noticeAt === k || noticeAt?.startsWith(`${k}.`));
    const followUp = (x: SpecialStatus): HTMLElement | undefined => {
      if (!statuses.includes(x)) return undefined;
      const parts: (HTMLElement | '')[] = [];
      if (x === 'youth') parts.push(dateQuestion('birthDate', tr('questions.birthDateRequired'), state.answers.birthDate, (v) => setAnswer('birthDate', v), true));
      if (x === 'adjustedMinimum') {
        parts.push(radioGroup('adjustedMinimumPercent', tr('questions.adjustedMinimumPercent'),
          [...['30', '40', '50', '60', '70', '80', '100'].map((n) => [n, `${n}%`] as [string, string]), ['', tr('questions.dontKnow')]],
          state.answers.adjustedMinimumPercent === undefined ? '' : String(state.answers.adjustedMinimumPercent),
          (v) => setAnswer('adjustedMinimumPercent', v === '' ? undefined : Number(v)), 4));
      }
      // A guarding or catering contractor: which of the two (the checks ask for it; a guard's pension, severance and
      // recuperation name the guarding order, catering's the catering entry); the notice under the answer it depends on.
      if (x === 'contractor') parts.push(answerQuestion('contractorSector', 'contractorSector', 4) ?? '');
      if (x === partialAt) {
        if (x === 'contractor') parts.push(early.node);
        else parts.unshift(early.node);
      }
      return parts.length ? el('div', {}, ...parts) : undefined;
    };
    // The checklist's help says what a status leaves unchecked on payslips (payslip goals only: the Form 106 goal has its own
    // screen, whoForm106D, where none of these statuses is asked; persona P20, 2026-10-23; docs/claims-register.md).
    const statusOption = (x: SpecialStatus) => {
      const more = followUp(x);
      // The contractor's help gives way to its notice once ticked (the notice says the same, and more).
      const help = HELP_STATUSES_D.includes(x) && x !== partialAt ? tr(`whoD.status.${x}.help`) : undefined;
      return { value: x, label: tr(hasMessage('en', `whoD.status.${x}`) ? `whoD.status.${x}` : `questions.specialStatus.${x}`), ...(help ? { help } : {}), ...(more ? { followUp: more } : {}) };
    };
    const setStatuses = (ticked: SpecialStatus[] | undefined) => {
      if (ticked === undefined) state.flow.statusesUnanswered = true;
      else delete state.flow.statusesUnanswered;
      state.flow.statusesAnswered = true;
      const next = [...(ticked ?? []), ...(state.flow.who === 'foreign' ? ['foreignWorker' as const] : [])];
      setAnswer('specialStatuses', next.length ? next : undefined);
    };
    const ALL_STATUSES_D = [...STATUSES_D, ...RARE_STATUSES_D];
    const common = others.filter((x) => ALL_STATUSES_D.includes(x));
    // Nothing ticked yet: not answered (no box ticked, "None of these" included), never "None of these" unasked.
    const unasked = !state.flow.statusesAnswered && !common.length;
    const checklist = choiceQuestion(el, {
      name: 'specialStatus', idPrefix: 'status-', type: 'checkbox', question: tr('whoD.statuses'), help: tr('whoD.statuses.help'), level: 3, hiddenLegend: true, className: 'status-question fd-checklist',
      options: ALL_STATUSES_D.map(statusOption),
      none: tr('questions.specialStatus.none'), selected: state.flow.statusesUnanswered || unasked ? undefined : common,
      onChange: (v) => setStatuses(v as SpecialStatus[] | undefined),
    });
    // A closed box with its answer showing; open while something in it needs looking at.
    const fold = (id: string, title: string, answer: string, body: HTMLElement, open: boolean) => {
      const d = el('details', { class: 'fd-card fd-fold fd-ask', id }, el('summary', {}, el('span', {}, el('span', { class: 'fd-ask-q' }, title), answer ? el('span', { class: 'fd-small fd-ask-a' }, answer) : '')), body);
      d.open = open || state.moreOpen.has(id);
      d.addEventListener('toggle', () => (d.open ? state.moreOpen.add(id) : state.moreOpen.delete(id)));
      return d;
    };
    // The label alone (statusOption builds the follow-ups, which hold the notice: never build them twice).
    const labelOf = (x: SpecialStatus) => tr(hasMessage('en', `whoD.status.${x}`) ? `whoD.status.${x}` : `questions.specialStatus.${x}`);
    // Under "Another work arrangement" neither the situations nor the sector is asked (no check runs; a translation needs neither).
    const needed = whoNeeded({ group: who, statusesUnanswered: statusesMissingD(), sectorMissing: sectorOnWho() && Boolean(who) && who !== 'other' && !sectorAnswered && !early.stop });
    const statusesAnswer = state.flow.statusesUnanswered || unasked ? tr('whoD.notAnswered') : common.length ? common.map(labelOf).join(', ') : tr('questions.specialStatus.none');
    const statusesBox = fold('fd-who-statuses', tr('whoD.statuses'), statusesAnswer, checklist,
      Boolean(state.flow.statusesUnanswered) || statusesMissingD() || Boolean(partialAt) || common.some((x) => x === 'youth' || x === 'adjustedMinimum' || x === 'contractor' || RARE_STATUSES_D.includes(x)));
    const why = el('details', { class: 'fd-why', id: 'fd-who-why' }, el('summary', {}, tr('whoD.why')), el('p', { class: 'fd-small' }, tr('whoD.lead')));
    why.open = state.moreOpen.has('fd-who-why');
    why.addEventListener('toggle', () => (why.open ? state.moreOpen.add('fd-who-why') : state.moreOpen.delete('fd-who-why')));
    if (state.flow.neededShown && needed.length) queueMicrotask(() => fd().showNeeded(el, tr, needed, false));
    if (!needed.length) delete state.flow.neededShown;
    // A stop: the alternative stays the notice's one primary, on the first screen at every width; "Back" is a link.
    if (early.stop && early.node) {
      const back = early.node.querySelector<HTMLButtonElement>('#early-back');
      if (back && early.node.querySelector('#early-alternative')) back.className = 'link-button';
    }
    const section = el('section', { class: 'rv fd fd-screen flow-step', 'aria-labelledby': 'who-heading' },
      el('div', {}, el('h2', { id: 'who-heading', tabindex: '-1', class: 'fd-title' }, tr('whoD.heading')), why),
      early.stop ? early.node : '',
      el('div', { id: 'fd-who-groups' }, groups),
      // "Another work arrangement" stops: the situations under the Israeli workers' group can no longer matter.
      who === 'other' ? '' : statusesBox,
      // A partial notice with no answer of its own on this screen: under the groups' answers.
      !early.stop && !placed ? early.node : '',
      fd().bottomBar({
        el, tr, sticky: true, privacyHref: '#/privacy', needed, showMissing: Boolean(state.flow.neededShown),
        ...(early.stop ? {} : { action: { label: tr('flow.continue'), id: 'who-continue', onClick: () => {
          const to = stepAfter('who', state.flow.goal ?? 'month', situation(), (state.flow.goal === 'form106' ? hasForm106() : payslipsRead()) && heldFiles.length === 0, earlyOptions());
          if (to !== 'stop') goTo(to);
        } } }),
        onNeeded: () => {
          state.flow.neededShown = true;
          render();
          fd().showNeeded(el, tr, needed, true);
        },
      }));
    return [section];
  }

  /**
   * "Who are you?" on the Form 106 goal (less-text mockups, MATT GO 2026-10-23 13:28): one question, three cards (the
   * same as on the payslip goals, MATT GO 13:40). Only a foreign worker or asylum seeker (NO_CREDIT) and another work
   * arrangement (a stop) change a Form 106 check (ANSWER_EFFECTS); every other status, a foreign worker's sector of work and
   * the private or public sector change payslip checks only, so they are not asked here (docs/specs/early-stop-table.md).
   * The cards set their status as on the payslip goals (groupStatuses); answers given on a payslip goal are kept. A partial
   * notice goes right under the card that caused it; a stop under the heading.
   */
  function whoForm106D(tr: (key: string, params?: Record<string, string | number>) => string, who: Who | undefined, early: { stop: boolean; node: HTMLElement | '' }): HTMLElement {
    const chosen: Who | undefined = who;
    const pick = (c: Who) => {
      state.flow.who = c;
      setAnswer('specialStatuses', groupStatuses(c));
      document.getElementById(`who-${c}`)?.focus();
    };
    const notice = !early.stop && early.node ? early.node : '';
    const groups = choiceQuestion(el, {
      name: 'who', type: 'radio', question: tr('whoD.heading'), level: 3, hiddenLegend: true, cards: true, selected: chosen,
      options: WHO.map((c) => {
        const key = c === 'other' ? 'whoD.otherArrangement' : `whoD.${c}`;
        return { value: c, label: tr(key), ...(chosen === c ? { help: tr(`${key}.help`), ...(notice ? { followUp: el('div', { class: 'who-follow-up' }, notice) } : {}) } : {}) };
      }),
      onChange: (v) => pick(v as Who),
    });
    groups.querySelectorAll<HTMLLabelElement>('label.option-card').forEach((label) => label.append(fd().flowIcon(label.htmlFor === 'who-foreign' ? 'globe' : label.htmlFor === 'who-other' ? 'briefcase' : 'user')));
    const needed = whoNeeded({ group: who });
    const why = el('details', { class: 'fd-why', id: 'fd-who-why' }, el('summary', {}, tr('whoD.why')), el('p', { class: 'fd-small' }, tr('whoD.lead')));
    why.open = state.moreOpen.has('fd-who-why');
    why.addEventListener('toggle', () => (why.open ? state.moreOpen.add('fd-who-why') : state.moreOpen.delete('fd-who-why')));
    if (state.flow.neededShown && needed.length) queueMicrotask(() => fd().showNeeded(el, tr, needed, false));
    if (!needed.length) delete state.flow.neededShown;
    return el('section', { class: 'rv fd fd-screen flow-step', 'aria-labelledby': 'who-heading' },
      el('div', {}, el('h2', { id: 'who-heading', tabindex: '-1', class: 'fd-title' }, tr('whoD.heading')), why),
      early.stop ? early.node : '',
      el('div', { id: 'fd-who-groups' }, groups),
      fd().bottomBar({
        el, tr, sticky: true, privacyHref: '#/privacy', needed, showMissing: Boolean(state.flow.neededShown),
        ...(early.stop ? {} : { action: { label: tr('flow.continue'), id: 'who-continue', onClick: () => {
          const to = stepAfter('who', 'form106', situation(), (state.flow.goal === 'form106' ? hasForm106() : payslipsRead()) && heldFiles.length === 0, earlyOptions());
          if (to !== 'stop') goTo(to);
        } } }),
        onNeeded: () => {
          state.flow.neededShown = true;
          render();
          fd().showNeeded(el, tr, needed, true);
        },
      }));
  }

  /** What the user answered, in a few words (the questions step's one-line summary). */
  function answersSummary(): string {
    const tr = (key: string, params?: Record<string, string | number>) => t(state.lang, key, params);
    const a = state.answers;
    const parts = [
      a.sector ? tr(`questions.sector.${a.sector}`) : '',
      a.payBasis ? tr(`questions.payBasis.${a.payBasis}`) : '',
      a.employmentEndDate ? tr('flow.summary.left', { date: formatDate(a.employmentEndDate) }) : '',
      a.jobPercent !== undefined ? `${a.jobPercent}%` : '',
      a.workWeekDays ? tr(`questions.workWeekDays.${a.workWeekDays}`) : '',
      a.recuperationPayment ? tr(`questions.recuperationPayment.${a.recuperationPayment}`) : '',
      // The Form 106 questions (other income, tax statuses, sex) answered: said so, not "none yet" (screen review 2026-10-18).
      // Sex is a payslip question too (the pension age): with no Form 106 loaded it is itself, not "your Form 106 answers".
      ...(answersSummaryParts(a, hasForm106()).map((k) => tr(k))),
    ].filter(Boolean);
    return parts.length ? parts.join(' · ') : tr('flow.summary.noAnswers');
  }

  /** What was loaded, in a few words. */
  function filesSummary(): string {
    const tr = (key: string, params?: Record<string, string | number>) => t(state.lang, key, params);
    const r = state.result;
    if (r && 'payslips' in r && r.input === 'manual') return tr(r.enteredByHand ? 'flow.summary.byHand' : 'flow.summary.pasted');
    const read = r && 'payslips' in r ? r.payslips.length : 0;
    const forms = (state.uploads ?? []).filter((u, i) => u.form106 && !formNotCounted(i)).length;
    // A Form 106 typed in and no file: "entered by hand", never "files: 0 · Form 106 read: 0" (screen review #34).
    if (state.form106Manual && !state.files.length && !forms && !read) return tr('flow.summary.byHand');
    if (state.flow.goal === 'form106' || forms) return tr(read ? 'flow.summary.files.form106AndPayslips' : 'flow.summary.files.form106', { files: state.files.length, forms, payslips: read });
    return tr('flow.summary.files', { files: state.files.length, payslips: read });
  }

  /** What was loaded, by type (docs/review/doc-type-audit.md): the shared lines name what is there. */
  function loaded(): Loaded {
    const r = state.result;
    return { payslips: r && 'payslips' in r ? r.payslips.length : 0, forms106: form106Docs().length };
  }

  /** Who the user said they are, in a few words. */
  function whoSummary(): string {
    const tr = (key: string) => t(state.lang, key);
    const statuses = state.answers.specialStatuses ?? [];
    const type = state.answers.workerType ? ` · ${tr(`questions.workerType.${state.answers.workerType}`)}` : '';
    if (statuses.includes('foreignWorker') && statuses.length === 1) return `${tr('flow.who.foreign')}${type}`;
    if (statuses.length) return statuses.map((s) => tr(`questions.specialStatus.${s}`)).join(', ') + type;
    return tr(`flow.who.${state.flow.who ?? 'israeli'}`);
  }

  /** Finished steps before the open one: one line each, with Edit. */
  function stepSummaries(open: FlowStep): HTMLElement[] {
    const tr = (key: string, params?: Record<string, string | number>) => t(state.lang, key, params);
    // "Check your answers" is itself the summary of every step.
    if (open === 'confirm' || translating()) return [];
    // Design D: the step bar shows where the person is and leads back; no summary lines on "Who are you?" and the upload.
    if (__FLOW_D__ && (open === 'who' || open === 'upload' || open === 'questions')) return [];
    const order: FlowStep[] = ['goal', 'who', 'upload', 'questions', 'confirm', 'results'];
    const before = (s: FlowStep) => order.indexOf(s) < order.indexOf(open);
    const line = (s: FlowStep, label: string, value: string) => {
      const edit = fd().pencilButton(el, { id: `edit-${s}`, label: tr('flow.editFor', { step: label }), onClick: () => goTo(s) });
      return el('li', { class: 'step-summary' }, el('span', { class: 'step-summary-text' }, el('span', { 'aria-hidden': 'true', class: 'step-done' }, '✓'), ' ', el('span', { class: 'step-summary-label' }, `${label}: `), el('bdi', {}, value)), edit);
    };
    // The upload step: the two choices are one line with one Edit (ux-expert, upload.md S1).
    if (open === 'upload' && state.flow.goal) {
      const values = [tr(firstKey(state.lang, goalSummaryKey(state.flow.goal, loaded(), state.translateAsked === true))), ...(state.flow.who || (state.answers.specialStatuses ?? []).length ? [whoSummary()] : [])];
      const edit = fd().pencilButton(el, { id: 'edit-goal', label: tr('flow.editFor', { step: tr('flow.summary.goal') }), onClick: () => goTo('goal') });
      return [el('ul', { class: 'step-summaries', 'aria-label': tr('flow.summary.label') },
        el('li', { class: 'step-summary choice-line' }, el('span', { class: 'step-summary-text' }, el('span', { 'aria-hidden': 'true', class: 'step-done' }, '✓'), ' ', el('bdi', {}, values.join(' · '))), edit))];
    }
    const lines: HTMLElement[] = [];
    if (before('goal') && state.flow.goal) lines.push(line('goal', tr('flow.summary.goal'), tr(firstKey(state.lang, goalSummaryKey(state.flow.goal, loaded(), state.translateAsked === true)))));
    if (before('who') && (state.flow.who || (state.answers.specialStatuses ?? []).length)) lines.push(line('who', tr('flow.summary.who'), whoSummary()));
    if (before('upload') && hasInput()) lines.push(line('upload', tr(uploadLabelKey(state.flow.goal ?? 'month', loaded())), filesSummary()));
    if (before('questions')) lines.push(line('questions', tr('flow.summary.questions'), answersSummary()));
    // Back to "Check your answers" from the results: every value read can be changed there (cya.confirmHelp says so).
    if (before('confirm') && state.source !== 'json') lines.push(line('confirm', tr('cya.heading'), confirmSummary()));
    if (!lines.length) return [];
    const list = el('ul', { class: 'step-summaries', 'aria-label': tr('flow.summary.label') }, ...lines);
    // A Form 106's results: the choices fold into one row, so the year's result is on the first phone screen (the
    // ux-expert's proposal B2, docs/review/ux-expert/form106-results.md); Edit is one tap away.
    if (open !== 'results' || (!form106Docs().length && !state.rv)) return [list];
    const fold = el('details', { class: 'step-summaries-fold' }, el('summary', {}, tr('flow.summary.fold')), list);
    fold.open = state.moreOpen.has('stepSummaries');
    fold.addEventListener('toggle', () => (fold.open ? state.moreOpen.add('stepSummaries') : state.moreOpen.delete('stepSummaries')));
    return [fold];
  }

  /**
   * The "Check your answers" step in the steps summary (Matt, 2026-10-18 night: exactly what was confirmed and what
   * wasn't): how many numbers were confirmed, then each Form 106 number not confirmed yet, not read or not on the form
   * ("4 numbers confirmed · months not confirmed yet").
   */
  function confirmSummary(): string {
    const tr = (k: string, p?: Record<string, string | number>) => t(state.lang, k, p);
    let confirmed = 0;
    const open: string[] = [];
    const r = state.result;
    if (r && 'payslips' in r) for (const p of r.payslips) confirmed += CONFIRM_ROWS.filter((k) => shownValue(k, p.fields) !== undefined).length;
    for (const d of form106Docs()) {
      const review = 'typed' in d ? [] : state.f106?.form106Review(d.fields) ?? [];
      const has = (k: string) => (k === 'monthsWorked' ? d.fields.months !== undefined || d.fields.monthsWorked !== undefined : (d.fields as Record<string, unknown>)[k] !== undefined);
      confirmed += (state.f106?.FORM106_INPUTS ?? []).filter((k) => has(k)).length;
      for (const it of review) open.push(tr(it.why === 'notRead' ? 'cya.summary.notRead' : 'cya.summary.notConfirmed', { field: tr(`f106.short.${it.key}`) }));
      for (const k of d.fields.notOnForm ?? []) open.push(tr('cya.summary.notOnForm', { field: tr(`f106.short.${k}`) }));
    }
    return [tr('cya.summary.numbers', { count: confirmed }), ...open].join(' · ');
  }

  /** True when the manual form, not a PDF or pasted JSON, is what the checks run on. */
  const manualInUse = () => !state.uploads && state.source === 'form';

  /** Scrolls to the manual form and puts the cursor in its first field. */
  /** "Enter the details by hand": the Form 106 numbers in the Form 106 goal, else a payslip's. */
  function enterByHand(): void {
    // The Form 106 form also when every file not read is a 106 the read gate turned away.
    const unknown = unknownFormatIndices();
    if (state.flow.goal === 'form106' || (unknown.length > 0 && unknown.every(unknownAs106))) openForm106Entry();
    else openManual();
  }

  function openForm106Entry(): void {
    // The form's texts come with the Form 106 chunk.
    void loadForm106().then(() => {
      state.form106Open = true;
      render();
      const first = document.getElementById('form106-taxYear');
      first?.scrollIntoView?.({ block: 'center' });
      first?.focus();
    });
  }

  function openManual(): void {
    state.source = 'form';
    state.manualOpen = true;
    render();
    const first = document.getElementById('manual-payMonth');
    first?.scrollIntoView({ block: 'center' });
    first?.focus();
  }

  /**
   * Manual entry (docs/phase2-plan.md 4.5): the numbers the checks need, each with a hint saying where it
   * usually appears on a payslip. Values live in state.manual; typing doesn't re-render (keeps focus).
   */
  function renderManual(): HTMLElement {
    const tr = (key: string) => t(state.lang, key);
    const m = state.manual;
    const problems = state.manualProblems?.problems ?? [];
    const problemFor = (f: ManualField) => problems.find((p) => p.field === f);
    // Dates are picked with selects whose labels follow the page language (the browser's own date and
    // month controls show the browser's language). Chosen parts are kept here and combined into the
    // strings manualToFields validates; a half-filled date is an error, never a guess.
    const dateGroup = (f: 'payMonth' | 'startDate') => {
      const parts = (state.manualDates[f] ??= eng().splitDate(m[f]));
      const problem = problemFor(f);
      const year = new Date().getFullYear();
      const years = f === 'payMonth' ? Array.from({ length: 9 }, (_, i) => String(year - i)) : Array.from({ length: year - 1949 }, (_, i) => String(year - i));
      const select = (part: 'day' | 'month' | 'year', options: [string, string][], first: boolean) => {
        const id = first ? `manual-${f}` : `manual-${f}-${part}`;
        const s = el('select', {
          id,
          'aria-describedby': `hint-${f}${problem ? ` error-${f}` : ''}`,
          ...(problem ? { 'aria-invalid': 'true' } : {}),
        }, el('option', { value: '' }, '—'), ...options.map(([v, l]) => el('option', { value: v }, l)));
        s.value = parts[part];
        s.addEventListener('change', () => {
          parts[part] = s.value;
          m[f] = f === 'payMonth' ? eng().composeMonth(parts.month, parts.year) : eng().composeDate(parts.day, parts.month, parts.year);
          state.source = 'form';
        });
        return el('span', { class: `date-part date-${part}` }, el('label', { for: id }, tr(`manual.part.${part}`)), s);
      };
      const months = Array.from({ length: 12 }, (_, i) => [String(i + 1), tr(`month.${i + 1}`)] as [string, string]);
      const yearOptions = years.map((y) => [y, y] as [string, string]);
      return el('fieldset', { class: 'manual-field date-group' },
        el('legend', {}, tr(`manual.field.${f}`)),
        el('p', { id: `hint-${f}`, class: 'hint' }, tr(`manual.hint.${f}`)),
        el('div', { class: 'date-parts' },
          ...(f === 'payMonth'
            ? [select('month', months, true), select('year', yearOptions, false)]
            : [select('day', Array.from({ length: 31 }, (_, i) => [String(i + 1), String(i + 1)] as [string, string]), true), select('month', months, false), select('year', yearOptions, false)])),
        problem ? el('p', { id: `error-${f}`, class: 'field-error' }, tr(problem.key)) : '');
    };
    const field = (f: ManualField) => {
      const problem = problemFor(f);
      const input = el('input', {
        id: `manual-${f}`, name: f, type: 'text', dir: 'ltr', autocomplete: 'off', inputmode: 'decimal',
        'aria-describedby': `hint-${f}${problem ? ` error-${f}` : ''}`,
        ...(problem ? { 'aria-invalid': 'true' } : {}),
      });
      input.value = m[f] ?? '';
      input.addEventListener('input', () => {
        m[f] = input.value;
        state.source = 'form';
      });
      return el('div', { class: 'manual-field' },
        el('label', { for: `manual-${f}` }, tr(`manual.field.${f}`)),
        el('p', { id: `hint-${f}`, class: 'hint' }, tr(`manual.hint.${f}`)),
        input,
        problem ? el('p', { id: `error-${f}`, class: 'field-error' }, tr(problem.key)) : '');
    };
    const basis = m.payBasis ?? '';
    const basisGroup = radioGroup('manual-payBasis', tr('manual.field.payBasis'), [
      ...payBasisOptions(tr),
      ['', tr('questions.dontKnow')],
    ], basis, (v) => {
      m.payBasis = v;
      state.source = 'form';
      render();
      document.querySelector<HTMLInputElement>(`input[name="manual-payBasis"][value="${v}"]`)?.focus();
    });
    basisGroup.setAttribute('aria-describedby', 'hint-payBasis');
    // Help under the legend, before the options (Layout patterns; screen review #18).
    basisGroup.querySelector('legend')?.after(el('p', { id: 'hint-payBasis', class: 'hint' }, tr('manual.hint.payBasis')));
    const form = el('form', { class: 'manual-form', novalidate: '' },
      dateGroup('payMonth'),
      dateGroup('startDate'),
      basisGroup,
      ...(basis === 'hourly' || basis === 'daily' || basis === 'shifts' ? [field('hourlyRate'), field('hours')] : [field('basePay')]),
      field('jobPercent'),
      field('gross'),
      field('pensionEmployee'),
      field('pensionEmployer'),
      field('severance'),
      field('recuperationAmount'),
      field('recuperationDays'),
      el('div', { class: 'row' }, el('button', { type: 'submit', class: 'primary' }, tr('manual.submit'))),
    );
    form.addEventListener('submit', (e) => {
      e.preventDefault();
      // Typed numbers replace the files' readings: asks first when the files gave something.
      void guardLoss('typedReplacesFiles', state.lang, LANGUAGES[state.lang].dir, readFilesLoss(), () => {
        checkManual();
        // Numbers that pass go on to the questions step.
        if (state.result && 'payslips' in state.result) goTo('questions');
      }, 'replace');
    });
    const summary = state.manualProblems
      ? el('div', { id: 'manual-problems', class: 'errors', role: 'alert', tabindex: '-1' },
          el('p', {}, tr(state.manualProblems.kind === 'check' ? 'manual.checkHeading' : 'manual.invalidHeading')),
          el('ul', {}, ...problems.map((p) => {
            // About the form as a whole: a summary item without a field (never under one it isn't about).
            const field = p.field;
            if (!field) return el('li', {}, tr(p.key));
            const go = el('a', { href: `#manual-${field}` }, `${tr(`manual.field.${field}`)}: ${tr(p.key)}`);
            go.addEventListener('click', (e) => {
              e.preventDefault();
              document.getElementById(`manual-${field}`)?.focus();
            });
            // A typo check can be wrong for this payslip: "This number is right" (the scans' confirm) lets it through.
            const vouch = state.manualProblems?.kind === 'check' ? button(tr('ocr.vouch'), () => {
              state.manualVouched = [...state.manualVouched, { field, key: p.key, value: (state.manual[field] ?? '').trim() }];
              // As the form's submit: typed numbers replace the files' readings only after asking.
              void guardLoss('typedReplacesFiles', state.lang, LANGUAGES[state.lang].dir, readFilesLoss(), () => {
                checkManual();
                if (state.result && 'payslips' in state.result) goTo('questions');
              }, 'replace');
            }, 'secondary') : '';
            if (vouch) vouch.id = `manual-vouch-${field}-${p.key.split('.').pop() ?? ''}`;
            return el('li', {}, go, vouch ? ' ' : '', vouch);
          })))
      : '';
    return el('section', { class: 'card', 'aria-labelledby': 'manual-heading' },
      el('h2', { id: 'manual-heading' }, tr('manual.heading')),
      el('p', { class: 'help' }, tr('manual.intro')),
      summary,
      form);
  }

  /**
   * "More info" under a finding: a real button (aria-expanded / aria-controls) toggling a region with the
   * rule, the calculation, our assumptions, the confidence, the sources and a next step. Works offline
   * (everything is in the app) except the source links, which open in a new tab and are never fetched.
   */
  /**
   * "More info" under a result: the rule, the steps, assumptions, confidence, sources, the next step. `label` names the
   * toggle otherwise (a Form 106 headline's "How we calculated"), `before` comes first in the panel (the estimate's
   * details) and `also` adds a second finding's sections (the "why" merged into that headline).
   */
  function renderMoreInfo(m: MoreInfo | undefined, id: string, level: 'h4' | 'h5' | 'h6', o: { label?: string; before?: (Node | string)[]; also?: MoreInfo } = {}): HTMLElement {
    const tr = (key: string, params?: Record<string, string | number>) => t(state.lang, key, params);
    const panelId = `more-${id}`;
    const open = state.moreOpen.has(id);
    const toggle = el('button', { type: 'button', class: 'more-toggle', 'aria-expanded': String(open), 'aria-controls': panelId }, o.label ?? tr('more.button'));
    const panel = el('div', { id: panelId, class: 'more-panel', ...(open ? {} : { hidden: '' }) });
    toggle.addEventListener('click', () => {
      const now = !state.moreOpen.has(id);
      if (now) state.moreOpen.add(id);
      else state.moreOpen.delete(id);
      toggle.setAttribute('aria-expanded', String(now));
      panel.hidden = !now;
    });
    const section = (heading: string, ...children: (Node | string)[]) => el('section', {}, el(level, {}, heading), ...children);
    panel.append(...(o.before ?? []), ...[...(m ? [m] : []), ...(o.also ? [o.also] : [])].flatMap((m) => [
      section(tr('more.rule'), ...m.rule.map((r) => el('p', {}, r))),
      section(tr('more.steps'), el('ul', { class: 'steps' }, ...m.steps.map((s) => el('li', {},
        s.label, s.value ? ': ' : '', s.value ? el('bdi', { class: 'step-value' }, s.value) : '',
        s.source ? el('span', { class: 'step-source' }, ` (${s.source})`) : '')))),
      m.assumptions.length ? section(tr('more.assumptions'), el('ul', {}, ...m.assumptions.map((a) => el('li', {}, a)))) : '',
      section(tr('more.confidence'), ...m.confidence.map((c) => el('p', {}, c))),
      section(tr('more.sources'), el('ul', { class: 'sources' }, ...m.sources.map((s) => el('li', {},
        el('a', { href: s.url, target: '_blank', rel: 'noopener noreferrer' }, s.title, el('span', { class: 'visually-hidden' }, ` (${tr('more.newTab')})`)),
        ` · ${s.publisher}`, s.hebrewOnly ? ` ${tr('more.hebrewOnly')}` : '', s.unverified ? ` ${tr('more.unverified')}` : '',
        s.law ? el('span', { class: 'source-law' }, `${tr('more.law')}: ${s.law}`) : '',
        el('span', { class: 'source-checked' }, tr('more.checked', { date: s.checked })))))),
      section(tr('more.next'), el('p', {}, m.next)),
    ]));
    return el('div', { class: 'more-info' }, toggle, panel);
  }

  /**
   * Digital PDFs that don't print a start date (pension and recuperation need it): asked per file. A
   * printed seniority date (Michpal "וותק") is offered, pre-filled, never used until the user confirms
   * it or picks another date (seniority can be credited from before the start).
   */
  /**
   * The question for an answer a check needs, by key: on the questions step and, with another `name`, right under a
   * "can't check" result (never a dead end, src/ui/missingInfo.ts). Each saves itself and reruns the checks. The Form 106
   * question sets (income sources, situations, National Insurance's certificate) are one tap away on the questions step.
   */
  function answerQuestion(key: string, name = key, level: ChoiceSpec['level'] = 3): HTMLElement | undefined {
    const tr = (k: string) => t(state.lang, k);
    const radio = (k: string, options: [string, string][], value: string, save: (v: string) => void) => radioGroup(name, tr(`questions.${k}`), options, value, save, level);
    const dontKnow: [string, string] = ['', tr('questions.dontKnow')];
    /** A way to the question on the questions step (the Form 106 sets live there). */
    const toQuestion = () => {
      const go = button(tr(`missing.goToQuestion.${key}`), () => goTo('questions'), 'secondary');
      go.id = `${name}-go`;
      return el('div', { class: 'row' }, go);
    };
    // B2 (on unless MAGIA_F106_GROUPS=0): a foreign worker's visa, asked per tax year on the questions step.
    if (__F106_GROUPS__ && key === 'visaYears') return toQuestion();
    // Olim (preview only): the month of aliyah, asked under "new immigrant" on the questions step.
    if (__OLIM__ && key === 'aliyahMonth') return toQuestion();
    // Credit points for a degree: asked under "degree" on the questions step.
    if (key === 'degreeKind' || key === 'degreeYear' || key === 'degreeStudyYears' || key === 'dischargeMonth') return toQuestion();
    switch (key) {
      case 'sector':
        return radio('sector', [['private', tr('questions.sector.private')], ['public', tr('questions.sector.public')], dontKnow],
          state.answers.sector ?? '', (v) => setAnswer('sector', v === '' ? undefined : (v as Sector)));
      case 'hadPensionAtHire':
        return radio('hadPensionAtHire', [['yes', tr('questions.yes')], ['no', tr('questions.no')], dontKnow],
          state.answers.hadPensionAtHire === undefined ? '' : state.answers.hadPensionAtHire ? 'yes' : 'no', (v) => setAnswer('hadPensionAtHire', v === '' ? undefined : v === 'yes'));
      case 'payBasis':
        return radioGroup(name, tr('questions.payBasis'), [...payBasisOptions(tr), dontKnow],
          state.answers.payBasis ?? '', (v) => setAnswer('payBasis', v === '' ? undefined : (v as PayBasisAnswer)), level);
      case 'housingRegion':
        return radio('housingRegion', [...HOUSING_REGIONS.map((r) => [r, tr(`questions.housingRegion.${r}`)] as [string, string]), dontKnow],
          state.answers.housingRegion ?? '', (v) => setAnswer('housingRegion', v === '' ? undefined : (v as HousingRegion)));
      case 'workerType':
        return radio('workerType', [...WORKER_TYPES.map((w) => [w, tr(`questions.workerType.${w}`)] as [string, string]), dontKnow], state.answers.workerType ?? '', (v) => setAnswer('workerType', v === '' ? undefined : (v as WorkerType)));
      case 'caregiverEmployer':
        return radio('caregiverEmployer', [...CAREGIVER_EMPLOYERS.map((c) => [c, tr(`questions.caregiverEmployer.${c}`)] as [string, string]), dontKnow], state.answers.caregiverEmployer ?? '', (v) => setAnswer('caregiverEmployer', v === '' ? undefined : (v as CaregiverEmployer)));
      case 'contractorSector':
        return radio('contractorSector', [...CONTRACTOR_SECTORS.map((c) => [c, tr(`questions.contractorSector.${c}`)] as [string, string]), dontKnow], state.answers.contractorSector ?? '', (v) => setAnswer('contractorSector', v === '' ? undefined : (v as ContractorSector)));
      case 'leavingReason':
        return radioGroup(name, tr('questions.leavingReason'), [...LEAVING_REASONS.map((r) => [r, tr(`questions.leavingReason.${r}`)] as [string, string]), dontKnow],
          state.answers.leavingReason ?? '', (v) => setAnswer('leavingReason', v === '' ? undefined : (v as LeavingReason)), level, tr('questions.leavingReason.help'));
      case 'adjustedMinimumPercent':
        return radio('adjustedMinimumPercent', [...['30', '40', '50', '60', '70', '80', '100'].map((p) => [p, `${p}%`] as [string, string]), dontKnow],
          state.answers.adjustedMinimumPercent === undefined ? '' : String(state.answers.adjustedMinimumPercent), (v) => setAnswer('adjustedMinimumPercent', v === '' ? undefined : Number(v)));
      case 'jobPercent':
        return numberQuestion(name, tr('questions.jobPercent'), state.answers.jobPercent, (v) => setAnswer('jobPercent', v), tr('questions.jobPercent.help'));
      case 'birthDate':
        return dateQuestion(name, tr('questions.birthDateRequired'), state.answers.birthDate, (v) => setAnswer('birthDate', v));
      case 'noticeDate':
        return dateQuestion(name, tr('questions.noticeDate'), state.answers.noticeDate, (v) => setAnswer('noticeDate', v));
      case 'niCertificates': {
        // The certificate's own boxes right here (the same draft as on the questions step), for each year with
        // National Insurance pay ticked; it saves itself and the checks run again (persona audit M3).
        const f106 = state.f106;
        const certs = state.answers.niCertificates ?? {};
        const keys = Object.entries(state.answers.incomeSources ?? {}).filter(([, v]) => v.includes('niBenefits')).map(([k]) => k);
        if (f106 && keys.length) {
          return el('div', { class: 'ni-entries' }, ...keys.map((key) => f106.niCertificateEntry({
            key, year: key === 'unknown' ? undefined : Number(key), value: certs[key], el, tr: (k, p) => t(state.lang, k, p), save: (v) => {
              const rest = Object.fromEntries(Object.entries(state.answers.niCertificates ?? {}).filter(([k]) => k !== key));
              setAnswer('niCertificates', v ? { ...rest, [key]: v } : Object.keys(rest).length ? rest : undefined);
            },
          })));
        }
        return toQuestion();
      }
      case 'incomeSources':
      case 'taxStatuses': {
        return toQuestion();
      }
      default:
        return undefined;
    }
  }

  function startDateQuestions(): HTMLElement[] {
    if (!state.uploads) return [];
    const tr = (key: string, params?: Record<string, string | number>) => t(state.lang, key, params);
    const files = new Map<string, IsoDate | undefined>();
    for (const e of eng().entriesByMonth(state.uploads)) {
      if (state.ocr.has(e.fileIndex) || e.fields.startDate) continue;
      if (!files.has(e.fileName) || e.fields.seniorityDate) files.set(e.fileName, e.fields.seniorityDate ?? files.get(e.fileName));
    }
    return [...files].map(([file, seniority], n) => {
      const id = `start-${n}`;
      const chosen = state.startDates.get(file);
      if (seniority) state.dateParts[id] ??= eng().splitDate(seniority);
      const set = (v: IsoDate | undefined) => {
        if (v) state.startDates.set(file, v);
        else state.startDates.delete(file);
        analyseUploads(state.uploads ?? []);
        renderKeepingFocus();
      };
      const legend = files.size > 1 ? tr('questions.startDate.file', { file }) : tr('questions.startDate');
      return el('div', { class: 'start-date-question' },
        seniority ? el('p', {}, tr('questions.startDate.seniority', { date: formatDate(seniority) })) : '',
        seniority && chosen === undefined ? el('div', { class: 'row' }, button(tr('questions.startDate.useSeniority'), () => {
          state.dateParts[id] = eng().splitDate(seniority);
          set(seniority);
        }, 'secondary')) : '',
        dateQuestion(id, seniority ? tr('questions.startDate.orChoose') : legend, chosen, set),
        chosen ? el('p', { class: 'help', role: 'status' }, tr('questions.startDate.used', { date: formatDate(chosen) })) : '');
    });
  }

  /**
   * "Please check these numbers": every value held back (src/upload/hold.ts; a beta vendor's pension values, any
   * payslip's numbers that don't fit the rest of it), each with "This number is right" and Change: the value typed in
   * its place, checked as under a "can't check" result (usePayslipFix), and the checks run again (definition of done:
   * a held value can be corrected right there). Results stay capped at medium.
   */
  function pensionCheckQuestion(): HTMLElement | '' {
    // Design D asks these on "Check what we read", in its amber box (src/ui/flowD/check.ts), not here.
    if (__FLOW_D__ || !state.uploads) return '';
    const tr = (key: string, params?: Record<string, string | number>) => t(state.lang, key, params);
    const pensionKeys: readonly string[] = ['pensionEmployee', 'pensionEmployer', 'severanceEmployer'];
    let other = false;
    // Digital pages and confirmed scans alike (a scan's own review screen judged its other numbers), as analyseUploads holds them.
    const items = heldEntries(state.uploads).flatMap((e) => e.held.map((field) => {
      const { key } = e;
      const vouched = state.vouched.get(key)?.includes(field) ?? false;
      const amount = e.read[field] ?? 0;
      const pension = pensionKeys.includes(field);
      if (!pension) other = true;
      // Not a pension value (hourly rate, hours, tax…): "looks unusual", the value as the payslip shows it.
      const shown = field === 'startDate' ? formatDate(String(amount)) : COUNT_KEYS.includes(field) ? String(amount) : formatMoney(Number(amount), state.lang);
      // The value's name as "Check your answers" shows it (standard hours has none there yet: docs/review/plausibility-coverage.md "→ text rewrite").
      const label = pension ? tr(`ocr.field.${field}`) : tr(field === 'standardHours' ? 'field.standardHours' : eng().heldLabelKey(field));
      const id = `held-${key.replace(/[^\w-]/g, '_')}-${field}`;
      const done = () => {
        state.heldEdit = undefined;
        renderKeepingFocus();
      };
      const editing = state.heldEdit === id;
      const change = fd().pencilButton(el, { id: `${id}-change`, label: tr('cya.changeFor', { item: label }), onClick: () => {
        state.heldEdit = id;
        render();
        queueMicrotask(() => document.getElementById(`${id}-${field}`)?.focus());
      } });
      const kinds: Record<string, Engine.FieldInput> = { [field]: HELD_KINDS[field] ?? MISSING_INPUTS[field] ?? 'count' };
      return el('li', { class: 'pension-check', 'data-field': field, 'data-amount': String(amount) },
        el('p', {}, pension ? tr('questions.pensionCheck.item', { field: label, file: e.fileName, amount })
          : tr('questions.unusualCheck.item', { field: label, file: e.fileName, value: shown })),
        vouched ? el('p', { class: 'help', role: 'status' }, tr('ocr.implausibleVouched')) : '',
        editing
          ? eng().missingInfo({
            el, tr, id, level: 4, missing: [field], current: e.read as Record<string, unknown>, answer: () => undefined, monthName, kinds,
            // Typed: used (never held again), the checks run again and this item leaves the list.
            onUse: (values) => {
              const problems = usePayslipFix(key, e.read, values, kinds);
              if (!problems.length) done();
              return problems;
            },
            change: { prefill: prefillOf(field, amount, kinds[field]), heading: tr('cya.changeHeading', { item: label }), help: tr('cya.changeHelp'), use: tr('cya.use'), cancel: tr('cya.cancel'), onCancel: done, label },
          })
          : el('div', { class: 'row' }, vouched ? '' : button(tr('ocr.vouch'), () => {
            state.vouched.set(key, [...(state.vouched.get(key) ?? []), field]);
            analyseUploads(state.uploads ?? []);
            renderKeepingFocus();
          }, 'secondary'), change));
    }));
    return items.length ? el('fieldset', { class: 'pension-checks' }, el('legend', {}, tr(other ? 'questions.unusualCheck.heading' : 'questions.pensionCheck.heading')), el('ul', {}, ...items)) : '';
  }

  // ---- Installing the app ----
  // Android Chrome and Samsung Internet fire beforeinstallprompt when the app can be installed: kept, so
  // "Install the app" can open the browser's own prompt. iPhones have no such event: a short card shows
  // how (Share → Add to Home Screen), dismissible and remembered. Nothing shows inside the installed app.
  let installPrompt: (Event & { prompt(): Promise<unknown>; userChoice: Promise<{ outcome: string }> }) | undefined;
  let installed = false;
  window.addEventListener('beforeinstallprompt', (e) => {
    e.preventDefault();
    installPrompt = e as typeof installPrompt;
    renderKeepingFocus();
  });
  window.addEventListener('appinstalled', () => {
    installPrompt = undefined;
    installed = true;
    renderKeepingFocus();
  });
  function runningInstalled(): boolean {
    try {
      return matchMedia('(display-mode: standalone)').matches || (navigator as Navigator & { standalone?: boolean }).standalone === true;
    } catch {
      return false;
    }
  }
  const installOffer = () => installPrompt !== undefined && !installed && !runningInstalled();
  function installButton(id: string): HTMLButtonElement {
    const b = button(t(state.lang, 'install.button'), () => {
      const e = installPrompt;
      if (!e) return;
      // A prompt can be shown once: whatever the answer, this event is used up.
      installPrompt = undefined;
      void e.prompt().then(() => e.userChoice).then((choice) => {
        if (choice.outcome === 'accepted') installed = true;
        renderKeepingFocus();
      }, () => renderKeepingFocus());
    }, 'secondary');
    b.id = id;
    return b;
  }
  const IOS_INSTALL_KEY = 'magia-li.iosInstall';
  function iosInstallDismissed(): boolean {
    try {
      return localStorage.getItem(IOS_INSTALL_KEY) === 'dismissed';
    } catch {
      return false;
    }
  }
  function renderIosInstall(): HTMLElement | '' {
    if (!/iPhone|iPad|iPod/i.test(navigator.userAgent) || runningInstalled() || installed || iosInstallDismissed()) return '';
    const tr = (key: string) => t(state.lang, key);
    const close = button('✕', () => {
      try {
        localStorage.setItem(IOS_INSTALL_KEY, 'dismissed');
      } catch {
        // Storage blocked: closed for this visit only.
      }
      render();
      document.getElementById('pdf-choose')?.focus();
    }, 'secondary');
    close.className = 'close-button';
    close.setAttribute('aria-label', tr('install.ios.close'));
    return el('section', { class: 'card ios-install', 'aria-labelledby': 'ios-install-heading' },
      el('div', { class: 'ios-install-head' }, el('h2', { id: 'ios-install-heading' }, tr('install.ios.heading')), close),
      el('p', { class: 'help' }, tr('install.ios.why')),
      el('ol', {},
        el('li', {}, icon(SHARE_ICON), ' ', tr('install.ios.step1')),
        el('li', {}, icon(ADD_ICON), ' ', tr('install.ios.step2'))));
  }

  /**
   * The scanner and photo tips for this device (src/ui/photoTips.ts), short, with icons: next to "Take a
   * photo", and again when a photo came out blurred or small. `photosOnly`: a photo was taken, so even a
   * desktop gets the photo rules.
   */
  function photoTipsList(photosOnly = false): HTMLElement | '' {
    const tr = (key: string) => t(state.lang, key);
    const device = scannerDevice(navigator.userAgent, isPhone());
    const tips = photoTipKeys(device === 'desktop' && photosOnly ? 'phone' : device, state.flow.goal === 'form106');
    // A Form 106 on a desktop: the upload step's own tip already says it (flow.upload.form106Tip), never twice.
    if (tips.length === 1 && state.flow.goal === 'form106' && !photosOnly) return '';
    if (tips.length === 1) return el('p', { class: 'help photo-tips desktop-tip' }, tr(tips[0]?.key ?? ''));
    // The scanner line, then "Or take a photo:" and the four numbered steps.
    return el('div', { class: 'photo-tips', 'data-device': device },
      el('p', { class: 'tips-scanner' }, tr(tips[0]?.key ?? '')),
      el('p', { class: 'tips-heading' }, tr('tips.photoIntro')),
      el('ol', {}, ...tips.slice(1).map((tip) => el('li', {}, tr(tip.key)))));
  }

  /** The kind of phone for the too-big photo's camera steps (src/ui/photoTooLarge.ts): the platform word only, never the model. */
  function devicePlatform() {
    const nav = navigator as Navigator & { userAgentData?: UaData };
    return eng().platformOf(nav.userAgent, nav.maxTouchPoints ?? 0, nav.userAgentData);
  }

  /** A phone or tablet (touch first): offer the camera and the scanner tip. */
  function isPhone(): boolean {
    try {
      return matchMedia('(pointer: coarse)').matches;
    } catch {
      return false;
    }
  }

  /**
   * Upload → Check → Results: where the user is. Check = something is loaded or entered but the results
   * aren't there yet (reading, a scan to confirm, questions); Results = findings are showing.
   */
  function renderSteps(): HTMLElement {
    const tr = (key: string) => t(state.lang, key);
    const current = ({ goal: 0, who: 0, upload: 1, questions: 2, confirm: 2, results: 3 } as const)[stage()];
    return el('ol', { class: 'progress-steps', 'aria-label': tr('steps.label') },
      ...(['start', 'upload', 'check', 'results'] as const).map((s, i) => el('li', {
        class: i < current ? 'done' : '',
        ...(i === current ? { 'aria-current': 'step' } : {}),
      }, el('span', { class: 'step-mark', 'aria-hidden': 'true' }, i < current ? '✓' : String(i + 1)), tr(`steps.${s}`), i < current ? el('span', { class: 'visually-hidden' }, ` ${tr('steps.done')}`) : '')));
  }

  /** ?debug=1: the panel a tester screenshots (src/ui/debugPanel.ts). English only, like ?dev. */
  function renderDebugPanel(debugText: typeof import('./debugText').debugText): HTMLElement {
    const files = (state.uploads ?? []).map((u, i) => ({
      label: `file ${i + 1} (${u.fileType ?? 'unknown type'}${u.encrypted ? ', password-protected' : ''})`,
      status: u.problem ?? (u.ocr ? `needs OCR (${state.ocr.get(i)?.status ?? 'offer'}${state.ocr.get(i)?.failure ? `, failed: ${state.ocr.get(i)?.failure}` : ''})` : `read, ${u.payslips.length} payslip(s), ${u.vendor ?? 'no vendor'}`),
    }));
    const text = debugText({ pdfBuild: state.pdfBuild, pdfReader: state.pdfReader, readerConfig: currentReaderConfig(), files, events: debugEvents });
    const copy = button('Copy', () => void navigator.clipboard?.writeText(text).catch(() => undefined), 'secondary');
    return el('section', { class: 'card debug-panel', lang: 'en', dir: 'ltr', 'aria-label': 'Debug' }, el('pre', { id: 'debug-text' }, text), el('div', { class: 'row' }, copy));
  }

  /** The sex question matters only when the birth date puts the user below the men's pension age in the pay month (or today). */
  function underPensionAgeForMen(payslips: PayslipFields[]): boolean {
    const birth = state.answers.birthDate;
    if (!birth) return false;
    const days = (payslips.length ? payslips.map((f) => (f.payMonth ? `${f.payMonth}-01` : today())) : [today()]) as IsoDate[];
    return days.some((day) => {
      const minAge = eng().defaultParams.entry('pensionMinimumAge', day)?.value;
      return minAge !== undefined && eng().completedYears(birth, eng().previousDay(day)) < minAge.men;
    });
  }

  /**
   * "Help improve Magia Li (beta)": was each result right, one comment, and a report the user reads in
   * full and then copies or downloads themselves. Built on the device; nothing is sent (CSP unchanged).
   */
  /** A feedback answer's key: payslip index and finding. */
  const feedbackKey = (i: number, f: Finding) => `${i}:${f.messageKey}`;

  /** Builds the diagnostic report on this device from the current results (nothing is sent). */
  function buildDiagnosticReport(input: 'manual' | 'digitalPdf', payslips: Analysed[]): void {
    const d = state.diag;
    const uploads = input === 'digitalPdf' ? (state.uploads ?? []) : [];
    const vendors = [...new Set(withOcr(uploads).map((u) => u.vendor).filter((v) => v !== null))];
    // A file read by its labels (src/fallback) is a digital PDF, not a scan: its confirmed numbers aren't OCR's.
    const ocrJobs = input === 'digitalPdf' ? [...state.ocr.values()].filter((j) => j.kind !== 'labels') : [];
    const ocrPages = ocrJobs.flatMap((j) => j.pages.filter((p) => p.confirmed));
    d.report = eng().buildReport({
      appVersion: BUILD_ID,
      inputType: ocrPages.length ? (ocrJobs.some((j) => j.kind === 'photo' && j.pages.some((p) => p.confirmed)) ? 'photo' : 'scan') : input,
      vendor: vendors.length === 1 ? (vendors[0] ?? null) : null,
      // OCR: a rounded confidence and the names of fields the user changed; never a value.
      ...(ocrPages.length ? { ocr: { confidence: ocrPages.reduce((t, p) => t + p.result.confidence, 0) / ocrPages.length, editedFields: [...new Set(ocrPages.flatMap((p) => eng().editedKeys(p.original, p.draft)))] } } : {}),
      payslips: payslips.map((p, i) => ({
        fields: p.fields,
        findings: p.findings,
        ...(p.reconciliation ? { reconciliation: p.reconciliation } : {}),
        feedback: p.findings.map((f) => d.feedback[feedbackKey(i, f)]),
      })),
      comment: d.comment,
      ...(state.pdfReader ? { pdfReader: state.pdfReader } : {}),
      // Unrecognised files too: their layout is what we'd need to support them.
      ...(uploads.length ? {
        // OCR text counts as the page text: it scrubs the comment and feeds the (masked) layout skeleton.
        pages: [...uploads.flatMap((u) => u.pages), ...ocrJobs.flatMap((j) => j.pages.map((p) => ({ items: p.result.items.map(({ str, x, y, right, font }) => ({ str, x, y, right, font })) })))],
        // Per file: recognised or why not, the type its bytes say, password-protected or not. Never a name or password.
        files: withOcr(uploads).map((u) => ({ ...(fileRecognised(u) ? {} : { problem: u.problem ?? 'unknownLayout' }), ...(u.fileType ? { fileType: u.fileType } : {}), ...(u.encrypted ? { encrypted: true } : {}), ...(u.readMethod ? { readMethod: u.readMethod } : {}) })),
      } : {}),
      includeSkeleton: d.includeLayout,
      errors: d.errors,
    });
    d.copied = false;
  }

  /** Files whose payslip format we don't know: a PDF whose layout isn't recognised, or a scan read only by the generic search. */
  function unknownFormatIndices(): number[] {
    const uploads = state.uploads ?? [];
    // A doubtful page the read gate dropped once its reading was settled (mergeOcr) counts too, never silently.
    const merged = withOcr(uploads);
    return uploads.flatMap((u, i) => ((!u.ocr && u.problem === 'unknownLayout') || (state.ocr.get(i)?.pages.some((p) => p.result.draft.vendor === null) ?? false)
      || (merged[i]?.unrecognised !== undefined && state.ocr.get(i)?.status !== 'offer' && state.ocr.get(i)?.status !== 'running') ? [i] : []));
  }

  /**
   * A file not recognised is spoken of as a Form 106 (its texts) on the Form 106 goal, or when it was read as a 106 layout
   * that the read gate (src/form106/readGate.ts) turned away.
   */
  function unknownReasonKey(index: number | undefined): string {
    if (unknownAs106(index)) return 'upload.reason.unknownLayout.form106';
    return index !== undefined && state.uploads?.[index]?.unrecognised?.doc === 'payslip' ? 'upload.reason.unknownLayout.readGate' : 'upload.reason.unknownLayout';
  }
  function unknownAs106(index: number | undefined): boolean {
    return state.flow.goal === 'form106' || (index !== undefined && state.uploads?.[index]?.unrecognised?.doc === 'form106');
  }

  /**
   * "We don't know this payslip format yet": prominent, and one button straight into the diagnostic
   * report with the anonymous layout (no names, no numbers: src/diagnostics/skeleton.ts) included,
   * ready to read and send. Nothing is sent by the app.
   */
  function renderUnknownFormat(): HTMLElement[] {
    const tr = (key: string, params?: Record<string, string | number>) => t(state.lang, key, params);
    const indices = unknownFormatIndices();
    if (indices.length === 0 || !diagnosticInput()) return [];
    const as106 = indices.every(unknownAs106);
    return [layoutOffer({ id: 'unknown-format', heading: tr(as106 ? 'unknownFormat.heading.form106' : 'unknownFormat.heading'), text: [tr(as106 ? 'unknownFormat.text.form106' : 'unknownFormat.text')], indices })];
  }

  /**
   * The offer to send the files' anonymous layout (the diagnostic report, nothing sent by the app) and, in a build with
   * it, the cover-your-details tool: one component for "a format we don't know yet" (with its heading) and "this page
   * can't be shown with translated labels yet" (G24; inside the translated view's box, whose summary is its heading).
   * `text`: its paragraphs, the first one describing the send button.
   */
  function layoutOffer(o: { id: string; heading?: string; text: readonly string[]; indices: readonly number[]; sendLabel?: string }): HTMLElement {
    const tr = (key: string, params?: Record<string, string | number>) => t(state.lang, key, params);
    const files = o.indices.map((i) => state.uploads?.[i]?.name ?? '');
    // A narrow card (the translated view's box) takes the fold's shorter label, as the folded unknown-format offer does.
    const send = button(o.sendLabel ?? tr('unknownFormat.send'), () => openReport(true), 'secondary');
    send.setAttribute('aria-describedby', `${o.id}-text`);
    return el(o.heading ? 'section' : 'div', { class: o.heading ? 'unknown-format' : 'layout-offer', ...(o.heading ? { 'aria-labelledby': `${o.id}-heading` } : {}) },
      ...(o.heading ? [el('h3', { id: `${o.id}-heading` }, o.heading)] : []),
      ...o.text.map((p, i) => el('p', i === o.text.length - 1 ? { id: `${o.id}-text` } : {}, p)),
      // One file: already named by the message above and its row (upload.md, cannot-read change 4).
      ...(files.length > 1 ? [el('p', { class: 'help' }, tr('unknownFormat.files'), ' ', ...files.flatMap((n, i) => [i ? ', ' : '', el('bdi', {}, n)])) ] : []),
      el('p', { class: 'help' }, tr('unknownFormat.private')),
      ...(__COVER__ ? [el('p', { class: 'help' }, tr('coverEntry.private'))] : []),
      el('div', { class: 'row upload-actions' }, send, __COVER__ ? coverButton(tr('coverEntry.send'), o.indices) : ''),
    );
  }

  /**
   * What the diagnostic report is built from: the results' payslips (none for a Form 106 alone), or, with a Form 106
   * typed in and no payslip results, nothing but the comment and the answers. Undefined: nothing to report on yet.
   */
  function diagnosticInput(): { input: 'manual' | 'digitalPdf'; payslips: Analysed[] } | undefined {
    const r = state.result;
    if (r && 'payslips' in r) return { input: r.input, payslips: r.payslips };
    return hasForm106() ? { input: 'manual', payslips: [] } : undefined;
  }

  /**
   * Opens the diagnostic report, built and ready to read and send: the unknown-format offer (with the anonymous layout)
   * and "Results look wrong? Create a diagnostic report" under every result.
   */
  function openReport(withLayout: boolean): void {
    const from = diagnosticInput();
    if (!from) return;
    state.diag.open = true;
    // The layout only when the button says so (the unknown-format offer); otherwise the person's own tick decides.
    if (withLayout) state.diag.includeLayout = true;
    buildDiagnosticReport(from.input, from.payslips);
    render();
    const report = document.getElementById('diag-report');
    report?.scrollIntoView?.({ block: 'center' });
    report?.focus();
  }

  function renderDiagnostics(input: 'manual' | 'digitalPdf', payslips: Analysed[]): HTMLElement {
    const tr = (key: string) => t(state.lang, key);
    const d = state.diag;
    const details = el('details', { class: 'card diagnostics' });
    details.open = d.open;
    details.addEventListener('toggle', () => (d.open = details.open));
    const key = feedbackKey;
    const several = payslips.length > 1;
    const questions = payslips.flatMap((p, i) =>
      p.findings
        .filter(eng().isShown)
        .map((f) =>
          radioGroup(`diag-${key(i, f)}`,
            `${several && p.fields.payMonth ? `${Number(p.fields.payMonth.slice(5))}/${p.fields.payMonth.slice(0, 4)} · ` : ''}${tr(`rule.${f.ruleId}`)} · ${tr(`results.status.${f.status}`)}: ${tr('diag.wasRight')}`,
            (['yes', 'no', 'notSure'] as const).map((v) => [v, tr(`diag.${v}`)] as [string, string]),
            d.feedback[key(i, f)] ?? '', (v) => {
              d.feedback[key(i, f)] = v as Feedback;
              d.report = undefined;
              render();
            }, 4),
        ),
    );
    const uploads = input === 'digitalPdf' ? (state.uploads ?? []) : [];
    const layout = el('input', { type: 'checkbox', id: 'diag-layout' });
    layout.checked = d.includeLayout;
    layout.addEventListener('change', () => {
      d.includeLayout = layout.checked;
      d.report = undefined;
      render();
    });
    const comment = el('textarea', { id: 'diag-comment', rows: '3', maxlength: '500', dir: 'auto', 'aria-describedby': 'diag-comment-help' });
    comment.value = d.comment;
    comment.addEventListener('input', () => {
      d.comment = comment.value;
      if (d.report !== undefined) {
        d.report = undefined;
        renderKeepingFocus();
      }
    });
    const build = () => {
      buildDiagnosticReport(input, payslips);
      render();
      document.getElementById('diag-report')?.focus();
    };
    const text = d.report ? eng().renderReport(d.report) : '';
    const download = () => {
      const url = URL.createObjectURL(new Blob([text], { type: 'text/plain;charset=utf-8' }));
      const a = el('a', { href: url, download: eng().REPORT_FILE_NAME });
      a.click();
      setTimeout(() => URL.revokeObjectURL(url), 0);
    };
    // The app sends nothing: the link opens the user's email app, and the user sends it (CSP unchanged).
    const email = d.report && eng().reportEmail(d.report, { fullAttached: t(state.lang, 'diag.emailFullAttached', { file: eng().REPORT_FILE_NAME }), attachLayout: t(state.lang, 'diag.emailAttachLayout', { file: eng().REPORT_FILE_NAME }) });
    const review = !d.report || !email ? [] : [
      el('h3', {}, el('label', { for: 'diag-report' }, tr('diag.reviewHeading'))),
      el('p', { class: 'help' }, tr('diag.reviewHelp')),
      Object.assign(el('textarea', { id: 'diag-report', rows: '14', readonly: '', dir: 'ltr', spellcheck: 'false' }), { value: text }),
      el(
        'div',
        { class: 'row' },
        button(d.copied ? tr('diag.copied') : tr('diag.copy'), () => {
          const done = () => {
            d.copied = true;
            render();
          };
          navigator.clipboard?.writeText(text).then(done, () => (document.getElementById('diag-report') as HTMLTextAreaElement | null)?.select());
        }, 'secondary'),
        button(tr('diag.download'), download, 'secondary'),
        Object.assign(el('a', { href: email.href, class: 'button secondary' }, tr('diag.email')), {
          // The body can't carry the layout (or a long report): download the file to attach first.
          onclick: () => (email.needsFile ? download() : undefined),
        }),
      ),
      ...(email.needsFile ? [el('p', { class: 'help' }, t(state.lang, 'diag.emailAttachHelp', { file: eng().REPORT_FILE_NAME }))] : []),
    ];
    details.append(
      el('summary', {}, tr('diag.heading')),
      el('p', { class: 'help' }, tr('diag.intro')),
      ...questions,
      el('label', { for: 'diag-comment' }, tr('diag.comment')),
      el('p', { id: 'diag-comment-help', class: 'help' }, tr('diag.commentHelp')),
      comment,
      uploads.length ? el('label', { class: 'choice' }, layout, tr('diag.includeLayout')) : '',
      el('div', { class: 'row' }, button(tr('diag.build'), build)),
      // A second, separate offer (opt-in, never automatic): a covered picture of any page (src/cover).
      ...(__COVER__ && uploads.length ? [el('div', { class: 'row' }, coverButton(tr('coverEntry.problem'), uploads.map((_, i) => i)))] : []),
      ...review,
    );
    return details;
  }

  /**
   * "Does any of these apply to you?" (phase 2 plan 1.1): several may apply. "None of these" is
   * exclusive: ticking it clears the others, ticking any other clears it. A status's follow-up sits
   * right under its option: the date of birth (required) under "under 18", the assessment-letter
   * percentage under "adjusted minimum wage".
   */
  /** The months with unpaid leave, as checkboxes; only with more than one month loaded. */
  function unpaidLeaveMonthsQuestion(inPlay: PayslipFields[]): HTMLFieldSetElement | '' {
    const months = [...new Set(inPlay.map((f) => f.payMonth).filter((m): m is YearMonth => m !== undefined))].sort();
    if (months.length < 2) return '';
    return choiceQuestion(el, {
      name: 'unpaidLeaveMonths', idPrefix: 'unpaid-leave-', type: 'checkbox', question: t(state.lang, 'questions.unpaidLeaveMonths'), level: 4, className: 'unpaid-leave-months',
      options: months.map((m) => ({ value: m, label: formatMonth(m) })), selected: state.answers.unpaidLeaveMonths,
      onChange: (v) => setAnswer('unpaidLeaveMonths', ((v as YearMonth[] | undefined) ?? []).sort()),
    });
  }

  /** `leave`: statuses not offered here (under the Israeli workers' card, the foreign-worker status is its own card). */
  function renderStatusQuestion(level: ChoiceSpec['level'] = 3, leave: readonly SpecialStatus[] = []): HTMLFieldSetElement {
    const tr = (key: string) => t(state.lang, key);
    const selected = state.answers.specialStatuses ?? [];
    const all = (['youth', 'apprentice', 'adjustedMinimum', 'rehabilitation', 'cleaningContractor', 'contractor', 'foreignWorker', 'oldAgePensioner'] as SpecialStatus[]).filter((x) => !leave.includes(x));
    // A status's follow-up sits right under its option.
    const followUp = (s: SpecialStatus): HTMLElement | undefined => {
      if (!selected.includes(s)) return undefined;
      if (s === 'youth') return dateQuestion('birthDate', tr('questions.birthDateRequired'), state.answers.birthDate, (v) => setAnswer('birthDate', v), true);
      // Foreign workers (phase 3 part A): the sector selects the rule pack (deposit, deduction caps).
      if (s === 'foreignWorker') return el('div', {}, ...workerTypeQuestions(level + 1 as ChoiceSpec['level']));
      // Guarding or catering (payslip goals only: the Form 106 checks don't read it): pension, severance and recuperation ask for it.
      if (s === 'contractor' && state.flow.goal !== 'form106') return answerQuestion('contractorSector', 'contractorSector', level + 1 as ChoiceSpec['level']);
      if (s === 'adjustedMinimum') {
        return radioGroup('adjustedMinimumPercent', tr('questions.adjustedMinimumPercent'),
          [...['30', '40', '50', '60', '70', '80', '100'].map((p) => [p, `${p}%`] as [string, string]), ['', tr('questions.dontKnow')]],
          state.answers.adjustedMinimumPercent === undefined ? '' : String(state.answers.adjustedMinimumPercent),
          (v) => setAnswer('adjustedMinimumPercent', v === '' ? undefined : Number(v)), level + 1 as ChoiceSpec['level']);
      }
      return undefined;
    };
    // Nothing ticked counts as "None of these" (no special status), as before.
    return choiceQuestion(el, {
      name: 'specialStatus', idPrefix: 'status-', type: 'checkbox', question: tr('questions.specialStatus'), help: tr('questions.specialStatus.help'), level, className: 'status-question',
      options: all.map((s) => {
        const more = followUp(s);
        return { value: s, label: tr(`questions.specialStatus.${s}`), ...(more ? { followUp: more } : {}) };
      }),
      none: tr('questions.specialStatus.none'), selected,
      // A status not offered here (a group card's own: the foreign worker's, another work arrangement) stays as it is.
      onChange: (v) => {
        const ticked = [...((v as SpecialStatus[] | undefined) ?? []), ...selected.filter((x) => !all.includes(x))];
        setAnswer('specialStatuses', ticked.length ? ticked : undefined);
      },
    });
  }

  /**
   * "I left this job" (severance, chunk 9): ticking it opens the last day of work and the reason (dismissal,
   * resignation, or a resignation the law treats as dismissal). Unticking clears both.
   */
  let unpaidMonthsKept: readonly YearMonth[] | undefined;
  const LEFT_JOB_KEYS = ['employmentEndDate', 'leavingReason', 'noticeDate'];
  let leftJobKept: { answers: Record<string, unknown>; dateParts: State['dateParts'] } | undefined;
  const leftJobOpen = (): boolean => state.answers.employmentEndDate !== undefined || state.answers.leavingReason !== undefined || state.moreOpen.has('leftJob');
  function leftJobQuestion(): HTMLElement {
    const tr = (key: string) => t(state.lang, key);
    const open = leftJobOpen();
    const box = el('input', { type: 'checkbox', id: 'left-job' });
    box.checked = open;
    box.addEventListener('change', () => {
      setLeftJob(box.checked);
      document.getElementById('left-job')?.focus();
    });
    const wrap = el('div', { class: 'left-job' }, el('label', { class: 'choice' }, box, tr('questions.leftJob')));
    if (open) wrap.append(el('div', { class: 'follow-up' }, ...leftJobFollowUps()));
    return wrap;
  }
  /** Left (or not, design D's "No"): the leaving answers count again, or stop counting but are kept for a later "Yes" (nothing is lost). */
  function setLeftJob(left: boolean): void {
    if (left) {
      state.moreOpen.delete('leftJobNo');
      state.moreOpen.add('leftJob');
      if (leftJobKept) {
        state.answers = { ...state.answers, ...leftJobKept.answers } as UserAnswers;
        for (const k of ['employmentEndDate', 'noticeDate']) if (leftJobKept.dateParts[k]) state.dateParts[k] = leftJobKept.dateParts[k];
        leftJobKept = undefined;
        if (state.uploads) analyseUploads(state.uploads);
        else if (state.result && 'payslips' in state.result) check();
      }
      render();
      return;
    }
    // Unticked (or "No"): the answers stop counting but are kept, and come back if it's ticked again (nothing is lost).
    state.moreOpen.add('leftJobNo');
    state.moreOpen.delete('leftJob');
    leftJobKept = { answers: Object.fromEntries(Object.entries(state.answers).filter(([k]) => LEFT_JOB_KEYS.includes(k))), dateParts: { ...state.dateParts } };
    delete state.dateParts.employmentEndDate;
    delete state.dateParts.noticeDate;
    state.answers = Object.fromEntries(Object.entries(state.answers).filter(([k]) => !LEFT_JOB_KEYS.includes(k))) as UserAnswers;
    setAnswer('leavingReason', undefined);
  }
  /** Asked once left: the last day of work, the reason, and on dismissal the notice date. */
  function leftJobFollowUps(): HTMLElement[] {
    const tr = (key: string) => t(state.lang, key);
    return [
      dateQuestion('employmentEndDate', tr('questions.employmentEndDate'), state.answers.employmentEndDate, (v) => setAnswer('employmentEndDate', v)),
      radioGroup('leavingReason', tr('questions.leavingReason'),
        [...LEAVING_REASONS.map((r) => [r, tr(`questions.leavingReason.${r}`)] as [string, string]), ['', tr('questions.dontKnow')]],
        state.answers.leavingReason ?? '',
        (v) => setAnswer('leavingReason', v === '' ? undefined : (v as LeavingReason)), 4, tr('questions.leavingReason.help')),
      ...(state.answers.leavingReason === 'dismissed'
        ? [dateQuestion('noticeDate', tr('questions.noticeDate'), state.answers.noticeDate, (v) => setAnswer('noticeDate', v)), el('p', { class: 'help' }, tr('questions.noticeDate.help'))]
        : [])];
  }

  /**
   * A date asked with day / month / year selects labelled in the page's language (the browser's own
   * date control shows the browser's language and format). Chosen parts are kept in state.dateParts,
   * so a half-filled date survives re-renders; only a complete, valid date is passed on, never a guess.
   */
  function dateQuestion(id: string, legend: string, value: IsoDate | undefined, onChange: (v: IsoDate | undefined) => void, required = false): HTMLElement {
    const tr = (key: string) => t(state.lang, key);
    const parts = (state.dateParts[id] ??= eng().splitDate(value));
    const year = new Date().getFullYear();
    const select = (part: 'day' | 'month' | 'year', options: [string, string][], first: boolean) => {
      const sid = first ? id : `${id}-${part}`;
      const sel = el('select', { id: sid, ...(required ? { 'aria-required': 'true' } : {}) }, el('option', { value: '' }, '—'), ...options.map(([v, l]) => el('option', { value: v }, l)));
      sel.value = parts[part];
      sel.addEventListener('change', () => {
        parts[part] = sel.value;
        const iso = eng().composeDate(parts.day, parts.month, parts.year);
        const complete = isIsoDate(iso) ? iso : undefined;
        if (complete !== value) onChange(complete);
      });
      return el('span', { class: `date-part date-${part}` }, el('label', { for: sid }, tr(`manual.part.${part}`)), sel);
    };
    return el('fieldset', { class: 'date-group' },
      el('legend', {}, legend),
      el('div', { class: 'date-parts' },
        select('day', Array.from({ length: 31 }, (_, i) => [String(i + 1), String(i + 1)] as [string, string]), true),
        select('month', Array.from({ length: 12 }, (_, i) => [String(i + 1), tr(`month.${i + 1}`)] as [string, string]), false),
        select('year', Array.from({ length: year - 1939 }, (_, i) => [String(year - i), String(year - i)] as [string, string]), false)));
  }

  function setAnswer<K extends keyof UserAnswers>(key: K, value: UserAnswers[K] | undefined): void {
    const rest = Object.fromEntries(Object.entries(state.answers).filter(([k]) => k !== key)) as UserAnswers;
    state.answers = value === undefined ? rest : { ...rest, [key]: value };
    // "A foreign worker" is the status: unticking it (on the questions step) ends the group too (one source for the early-stop table).
    // Design D has no "special situation" group: unticked there, the person is in the Israeli workers' group.
    if (key === 'specialStatuses' && state.flow.who === 'foreign' && !(state.answers.specialStatuses ?? []).includes('foreignWorker')) state.flow.who = 'israeli';
    if (state.uploads) {
      analyseUploads(state.uploads);
      render();
    } else if (state.result && 'payslips' in state.result) check();
    else render();
  }

  /** An answer kept as it is typed: the checks are re-run, the screen is not redrawn (the typing field stays as it is). */
  function saveAnswer<K extends keyof UserAnswers>(key: K, value: UserAnswers[K] | undefined): void {
    const rest = Object.fromEntries(Object.entries(state.answers).filter(([k]) => k !== key)) as UserAnswers;
    // A saved answer can make the unpaid-leave question appear or go (the job percentage changes the minimum): that one case redraws at once,
    // while the person is still typing (not on the blur, which would swallow the first tap on Continue).
    const asked = (): boolean => fieldsInPlay().some((f) => eng().asksUnpaidLeave(f, eng().defaultParams, state.answers));
    const before = state.uploads ? asked() : false;
    state.answers = value === undefined ? rest : { ...rest, [key]: value };
    if (state.uploads) analyseUploads(state.uploads);
    if (state.uploads && asked() !== before) renderKeepingFocus();
  }

  /** Re-renders and puts focus back on the same control (by id), with the caret where it was; not while Tab leaves a field (it goes on, K9). */
  function renderKeepingFocus(): void {
    const active = document.activeElement;
    if (tabLeaving(active) && active instanceof HTMLElement && takesTyping(active)) return render();
    const id = active instanceof HTMLElement && active.id ? active.id : undefined;
    const typed = active instanceof HTMLTextAreaElement || (active instanceof HTMLInputElement && active.type === 'text');
    const caret = typed ? (active as HTMLInputElement | HTMLTextAreaElement).selectionStart : null;
    render();
    const again = id ? document.getElementById(id) : null;
    if (again instanceof HTMLElement) {
      again.focus();
      if (caret !== null && (again instanceof HTMLTextAreaElement || again instanceof HTMLInputElement)) again.setSelectionRange(caret, caret);
    }
  }

  /** What a new check (or a reload) would lose: everything loaded, typed or answered. */
  function everythingLoss(): Loss {
    const typed = Object.values(state.manual).some((v) => v !== undefined && v.trim() !== '')
      || Object.values(state.manualDates).some((d) => d !== undefined && (d.day !== '' || d.month !== '' || d.year !== ''))
      || Object.values(state.form106Draft).some((v) => v !== undefined && String(v).trim() !== '')
      || (state.json.trim() !== '' && !state.exampleLoaded) || state.fieldFixes.size > 0 || state.form106Fixes.size > 0 || state.diag.comment.trim() !== '';
    const files = filesLoss();
    const loss: Loss = { ...files, forms106: (files.forms106 ?? 0) + (state.form106Manual ? 1 : 0), typed, answers: Object.keys(state.answers).length > 0 || Object.keys(state.diag.feedback).length > 0 };
    // One test of "is there work to lose" with the automatic update (hasUnsavedData): anything else it counts is typed.
    return losesSomething(loss) || !hasUnsavedData(state) ? loss : { ...loss, typed: true };
  }
  const workToLose = () => losesSomething(everythingLoss());
  /** The files chosen: the payslips and Forms 106 read from them, and the others (not read, scans not read yet). */
  function filesLoss(): Loss {
    const { payslips, forms106, gave } = readFromFiles();
    return { payslips, forms106, files: Math.max(0, state.files.length + state.pending.length - gave) };
  }
  /** Only what the files gave (typed numbers replacing them: a file that gave nothing isn't missed). */
  function readFilesLoss(): Loss {
    const { payslips, forms106 } = readFromFiles();
    return { payslips, forms106 };
  }
  /** Payslips per file, digital or read from a scan (confirmed or still in review: its numbers may have been corrected). */
  function payslipsOfFile(index: number): number {
    const u = state.uploads?.[index];
    if (!u) return 0;
    const job = state.ocr.get(index);
    return filePayslips(u.payslips.length, job && { status: job.status, pages: job.pages.length });
  }
  function readFromFiles(): { payslips: number; forms106: number; gave: number } {
    const uploads = state.uploads ?? [];
    const payslips = uploads.map((_, i) => payslipsOfFile(i));
    return { payslips: payslips.reduce((n, k) => n + k, 0), forms106: uploads.filter((u) => u.form106).length, gave: uploads.filter((u, i) => (payslips[i] ?? 0) > 0 || u.form106).length };
  }

  /** The browser's own warning is the only one possible when leaving the page (Back out of the app, close, reload). */
  let leaving = false;
  window.addEventListener('beforeunload', (e) => {
    if (leaving || !workToLose()) return;
    e.preventDefault();
    e.returnValue = '';
  });
  function reloadGuarded(): void {
    void guardLoss('reload', state.lang, LANGUAGES[state.lang].dir, everythingLoss(), () => {
      leaving = true;
      location.reload();
    });
  }

  /** A new check: everything entered goes; the language, an update waiting and the loaded chunks stay. */
  function startOver(): void {
    heldFiles = [];
    heldLetGo = false;
    stopOcr();
    filesGeneration++;
    forgetNotSure();
    multiYearDraft = undefined;
    leftJobKept = undefined;
    unpaidMonthsKept = undefined;
    const keep = { lang: state.lang, updateAvailable: state.updateAvailable, checksTotal: state.checksTotal, f106: state.f106, tv: state.tv, pdfReader: state.pdfReader, pdfBuild: state.pdfBuild };
    Object.assign(state, fresh(), keep);
    // The logo from the opening page: the start screen (the page isn't marked seen: only its "Start" does that).
    showOpening = false;
    if (location.hash) history.pushState({ step: 'goal' }, '', `${location.pathname}${location.search}#/`);
    goTo('goal');
  }

  /** "Back to the start": the start screen, asking first when something would be lost (like the logo). */
  function backToStart(): HTMLElement {
    const a = el('a', { href: '#/', class: 'back-to-start' }, t(state.lang, 'flow.backToStart'));
    a.addEventListener('click', (e) => {
      e.preventDefault();
      void guardLoss('logo', state.lang, LANGUAGES[state.lang].dir, everythingLoss(), startOver);
    });
    return el('p', { class: 'back-link' }, a);
  }

  /** The logo and the app's name, one link: the start screen. */
  function homeLink(): HTMLElement {
    const tr = (key: string) => t(state.lang, key);
    const a = el('a', { href: '#/', class: 'home', 'aria-label': tr('app.home') },
      __BRAND__ === 'c' ? brandLogo() : el('img', { class: 'logo', src: logoUrl, width: '40', height: '40', alt: '' }), el('span', { class: 'home-name' }, tr('app.name')));
    a.addEventListener('click', (e) => {
      e.preventDefault();
      void guardLoss('logo', state.lang, LANGUAGES[state.lang].dir, everythingLoss(), startOver);
    });
    return a;
  }

  // The first entry is the step shown now; Back to it from a later step stays in the app.
  history.replaceState({ step: state.flow.step }, '');
  window.addEventListener('popstate', (e) => {
    const step = (e.state as { step?: FlowStep } | null)?.step;
    if (step && !pageFromHash(location.hash)) goTo(step, true);
  });

  // Pages are views of this app (#/privacy, …): they work offline and in every language.
  window.addEventListener('hashchange', () => {
    render();
    // A page's section (#/privacy/counter) takes the focus itself, else the page's title.
    const section = /^#\/privacy\/counter$/.test(location.hash) ? document.getElementById('privacy-counter') : null;
    const target = section ?? (pageFromHash(location.hash) ? document.getElementById('page-title') : document.querySelector<HTMLElement>('main h2[tabindex="-1"]'));
    target?.focus();
    if (!pageFromHash(location.hash)) window.scrollTo(0, 0);
  });

  // First-visit welcome (src/ui/welcome.ts), outside the root; "How it works" in the footer reopens it. Not in design D:
  // the opening page (src/ui/flowD/opening.ts) replaces it there (MATT GO 2026-10-23 00:56).
  const welcome = __FLOW_D__ ? undefined : createWelcome({ lang: () => state.lang, setLang });

  let updateTimer: ReturnType<typeof setTimeout> | undefined;
  async function runUpdateCheck() {
    clearTimeout(updateTimer);
    state.updateCheck = 'checking';
    renderKeepingFocus();
    const result = await checkForUpdates();
    state.updateCheck = result;
    renderKeepingFocus();
    // "You have the latest version" and a failed check go after a few seconds and the link is back; a found update stays.
    updateTimer = clearLater(result, () => state.updateCheck, () => {
      delete state.updateCheck;
      renderKeepingFocus();
    });
  }

  render();
  if (COUNTER_URL) void readTotal().then((n) => {
    if (n === undefined) return;
    state.checksTotal = n;
    if (stage() === 'goal') renderKeepingFocus();
  });
  // The preview test checklist (a dev aid, beta builds only) opens straight, without the welcome.
  if (!__FLOW_D__ && !pageFromHash(location.hash) && !(__BETA__ && isChecklistHash(location.hash)) && shouldShowWelcome()) welcome?.open();
  return {
    showUpdateNotice() {
      state.updateAvailable = true;
      renderKeepingFocus();
    },
    hasUnsavedData: () => workToLose(),
  };
}

/**
 * A globe (decorative: the switcher's name says "Language"). A fixed string, never user text; parsed
 * as HTML, so the <svg> gets its namespace without a URL in the source.
 */
const GLOBE = '<svg class="globe" viewBox="0 0 24 24" aria-hidden="true" focusable="false" fill="none" stroke="currentColor" stroke-width="1.6"><path d="M12 2a10 10 0 1 0 0 20a10 10 0 1 0 0-20Z"/><path d="M2 12h20"/><path d="M12 2c2.8 2.9 4.2 6.2 4.2 10s-1.4 7.1-4.2 10c-2.8-2.9-4.2-6.2-4.2-10S9.2 4.9 12 2Z"/></svg>';
/** A FileList as an array, by index (not its iterator: some engines lack one on such lists). */
function filesOf(list: FileList | null | undefined): File[] {
  const out: File[] = [];
  for (let i = 0; i < (list?.length ?? 0); i++) {
    const f = list?.item(i);
    if (f) out.push(f);
  }
  return out;
}

/** Each finding status's icon (with its label beside it: never colour, never the icon alone). */
const STATUS_ICON: Record<'verify' | 'ok' | 'cannot_check', string> = { verify: '!', ok: '✓', cannot_check: '–' };

/** iOS's Share and "Add to Home Screen" symbols, drawn here (fixed strings, never user text). */
const SHARE_ICON = '<svg viewBox="0 0 24 24" aria-hidden="true" focusable="false" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"><path d="M12 3v12"/><path d="M8 7l4-4 4 4"/><path d="M6 11H5a1 1 0 0 0-1 1v8a1 1 0 0 0 1 1h14a1 1 0 0 0 1-1v-8a1 1 0 0 0-1-1h-1"/></svg>';
const ADD_ICON = '<svg viewBox="0 0 24 24" aria-hidden="true" focusable="false" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round"><rect x="4" y="4" width="16" height="16" rx="4"/><path d="M12 8v8M8 12h8"/></svg>';
function icon(svg: string): HTMLElement {
  const span = el('span', { class: 'inline-icon', 'aria-hidden': 'true' });
  span.innerHTML = svg;
  return span;
}

function globeIcon(): HTMLElement {
  const span = el('span', { class: 'globe-wrap', 'aria-hidden': 'true' });
  span.innerHTML = GLOBE;
  return span;
}

function button(label: string, onClick: () => void, variant: 'primary' | 'secondary' = 'primary', lang?: string): HTMLButtonElement {
  const b = el('button', { type: 'button', class: variant, ...(lang ? { lang } : {}) }, label);
  b.addEventListener('click', onClick);
  return b;
}

function numberQuestion(id: string, legend: string, value: number | undefined, onChange: (v: number | undefined) => void, help?: string): HTMLElement {
  const input = el('input', { type: 'number', id, min: '1', max: '100', step: 'any', inputmode: 'decimal', dir: 'ltr', ...(help ? { 'aria-describedby': `${id}-help` } : {}) });
  if (value !== undefined) input.value = String(value);
  input.addEventListener('change', () => {
    const n = Number(input.value);
    onChange(input.value.trim() === '' || !(n > 0 && n <= 100) ? undefined : n);
  });
  return el('div', { class: 'number-question' }, el('label', { for: id }, legend), help ? el('p', { id: `${id}-help`, class: 'help' }, help) : '', input);
}

/** The box under a "can't check" result asking for what's missing (renderView). */
type Fix = (missing: string[], id: string, level: 4 | 5 | 6) => HTMLElement | '';

/** Held-back payslip values that are counts (hours, days), not money: shown as printed. */
const COUNT_KEYS: readonly string[] = ['hoursWorked', 'hoursPaid', 'daysWorked', 'standardHours', 'jobPercent'];
/**
 * How a held value is typed when corrected ("Please check these numbers"): as under a "can't check" result, except the
 * values no check names as missing (standard hours, hours worked) and the job percentage, which here is this payslip's
 * printed figure (a number), not the question's answer for every payslip.
 */
const HELD_KINDS: Partial<Record<HeldKey, Engine.FieldInput>> = { hoursWorked: 'count', standardHours: 'count', jobPercent: 'count' };

/** "Check your answers": a payslip's values the checks use, in the order a payslip prints them (i18n `cya.field.<key>`). */
/** Design D's "What we'll check" on the empty upload step (uploadD.check.*): the payslip checks people know by name. */
const CHECK_TOPICS_D = ['minimumWage', 'overtime', 'pension', 'travel', 'recuperation', 'vacation', 'sick', 'completeness'] as const;
/** A row of the earlier "Check your answers" (and design D's check screen, src/ui/flowD/checkWire.ts). */
export type ConfirmRow = (typeof CONFIRM_ROWS)[number];
const CONFIRM_ROWS = ['payMonth', 'startDate', 'payBasis', 'jobPercent', 'hourlyRate', 'hoursPaid', 'daysWorked', 'baseSalary', 'grossPay', 'incomeTax',
  'nationalInsurance', 'healthTax', 'pensionEmployee', 'pensionEmployer', 'severanceEmployer', 'foreignDeposit', 'recuperationDays', 'netPay', 'vacationBalance', 'sickBalance'] as const;

/** A value read, as the fill-in box starts with it when changed: shekels, a count, a month or date, months, a balance's boxes. */
function prefillOf(key: string, value: unknown, kindOf?: string): Record<string, Engine.TypedValue> {
  if (value === undefined || value === null) return {};
  const kind = kindOf ?? MISSING_INPUTS[key];
  if (kind === 'money' && typeof value === 'number') return { [key]: String(value / 100) };
  if (kind === 'months12' && Array.isArray(value)) return { [key]: value as number[] };
  if (kind === 'leaveBalance' && typeof value === 'object') {
    const b = value as { opening?: number; used?: number; closing?: number };
    return { [key]: { opening: b.opening === undefined ? '' : String(b.opening), used: b.used === undefined ? '' : String(b.used), closing: b.closing === undefined ? '' : String(b.closing) } };
  }
  return typeof value === 'string' || typeof value === 'number' ? { [key]: String(value) } : {};
}

/** A month's name in the page's language (the months picker). */
/** A pay month by name ("May 2026"), in the page's language: the file list and the payslip headings alike. */
function namedMonth(ym: string): string {
  return new Intl.DateTimeFormat(document.documentElement.lang || 'he', { month: 'long', year: 'numeric' }).format(new Date(Number(ym.slice(0, 4)), Number(ym.slice(5, 7)) - 1, 15));
}

function monthName(m: number): string {
  return new Intl.DateTimeFormat(document.documentElement.lang || 'he', { month: 'long' }).format(new Date(2026, m - 1, 15));
}

/** A single-select question (src/ui/question.ts); `level`: the question's heading level, one below the block it's in. */
/** The pay-basis options (questions, OCR, manual entry): by the day or by shift are compared by the hour (resolvePayBasis). */
function payBasisOptions(tr: (key: string) => string): [string, string, string][] {
  return (['monthly', 'hourly', 'daily', 'shifts'] as const).map((b) => [b, tr(`questions.payBasis.${b}`), tr(`questions.payBasis.${b}.help`)]);
}

/**
 * Questions answered "Not sure" (or "Prefer not to say") on purpose, by name: the answer's value '' is also "not
 * answered", so only these show it ticked; an unanswered question shows nothing ticked (never a preselected radio;
 * design-conventions audit, docs/review/design-conventions.md). Cleared by a new check.
 */
const notSureChosen = new Set<string>();
const forgetNotSure = (): void => notSureChosen.clear();

function radioGroup(
  name: string,
  legend: string,
  options: ([value: string, label: string] | [value: string, label: string, help: string])[],
  selected: string,
  onChange: (value: string) => void,
  level: ChoiceSpec['level'] = 3,
  help?: string,
): HTMLFieldSetElement {
  const shown = selected === '' && !notSureChosen.has(name) ? undefined : selected;
  return choiceQuestion(el, {
    name, type: 'radio', question: legend, ...(help ? { help } : {}), level, options: options.map(([value, label, optionHelp]) => ({ value, label, ...(optionHelp ? { help: optionHelp } : {}) })), selected: shown,
    onChange: (v) => {
      if (v === '') notSureChosen.add(name);
      else notSureChosen.delete(name);
      onChange(v as string);
    },
  });
}
